import { r as registerInstance, c as createEvent, h, g as getElement, H as Host } from './index-96374631.js';

/**
 * Returns a function, that, as long as it continues to be invoked, will not
 * be triggered. The function will be called after it stops being called for
 * N milliseconds. If `immediate` is passed, trigger the function on the
 * leading edge, instead of the trailing. The function also has a property 'clear' 
 * that is a function which will clear the timer to prevent previously scheduled executions. 
 *
 * @source underscore.js
 * @see http://unscriptable.com/2009/03/20/debouncing-javascript-methods/
 * @param {Function} function to wrap
 * @param {Number} timeout in ms (`100`)
 * @param {Boolean} whether to execute at the beginning (`false`)
 * @api public
 */
function debounce(func, wait, immediate){
  var timeout, args, context, timestamp, result;
  if (null == wait) wait = 100;

  function later() {
    var last = Date.now() - timestamp;

    if (last < wait && last >= 0) {
      timeout = setTimeout(later, wait - last);
    } else {
      timeout = null;
      if (!immediate) {
        result = func.apply(context, args);
        context = args = null;
      }
    }
  }
  var debounced = function(){
    context = this;
    args = arguments;
    timestamp = Date.now();
    var callNow = immediate && !timeout;
    if (!timeout) timeout = setTimeout(later, wait);
    if (callNow) {
      result = func.apply(context, args);
      context = args = null;
    }

    return result;
  };

  debounced.clear = function() {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };
  
  debounced.flush = function() {
    if (timeout) {
      result = func.apply(context, args);
      context = args = null;
      
      clearTimeout(timeout);
      timeout = null;
    }
  };

  return debounced;
}
// Adds compatibility for ES modules
debounce.debounce = debounce;

var debounce_1 = debounce;

const autocompleteCss = ":host{position:relative;display:block;text-align:left;font-family:inherit;--autocomplete-suggestion-focus-background:gray}.autocomplete .suggestions{position:absolute;left:0;right:0;z-index:20;overflow-y:auto;top:100%;background-color:var(--fi-process-background, #ffffff);transition:max-height 0.1s ease-out;max-height:0;width:calc(100% + 20px);margin-left:-11px}.autocomplete .suggestions::-webkit-scrollbar{width:8px}.autocomplete .suggestions::-webkit-scrollbar-thumb{background:#d7d7d7;border-radius:6px}.autocomplete .suggestion{display:block;width:100%;border:none;text-align:left;cursor:pointer;padding:var(--fi-button-padding, 10px);font-size:18px;font-weight:normal;font-stretch:normal;font-style:normal;line-height:normal;letter-spacing:normal;color:var(--fi-secondary, #2b2e42);background-color:var(--fi-process-background, transparent);outline:none}.autocomplete .last{border-top:1px solid var(--fi-secondary-700, #e3e8e9)}.autocomplete .suggestion:hover,.autocomplete .suggestions.active{font-weight:bold}.autocomplete input:focus+.underline{transform:scale(1)}.autocomplete .input{position:relative}.autocomplete button{border-radius:0;text-transform:none}.autocomplete .underline{background-color:#888d96;display:inline-block;height:1px;left:0;position:absolute;bottom:0;-webkit-transform:scale(0, 1);transform:scale(0, 1);-webkit-transition:all 0.15s linear;transition:all 0.15s linear;width:100%}.autocomplete input:focus{outline:none}.autocomplete input:focus+.input_border{background:var(--fi-primary, #ffc809)}.autocomplete .input-wrapper{padding:10px 10px 0 10px;vertical-align:top;display:inline-block;width:100%;margin-left:-10px}.autocomplete .input-wrapper .autocomplete-input{text-overflow:ellipsis;background-color:var(--fi-process-background, transparent)}.autocomplete .input-wrapper .icon{margin-left:var(--fi-button-padding, 13px);position:absolute}.autocomplete .input-wrapper .icon img{display:block}.autocomplete .input-wrapper .icon-check{transform:rotate(90deg);transition:all 0.15s linear;bottom:0;right:0}.autocomplete .input-wrapper .icon-valid{animation:scale 0.5s;bottom:10px;right:7px}.autocomplete .input-wrapper .icon-disabled:after{border-color:#d2d6de}@keyframes scale{0%{transform:scale(1)}50%{transform:scale(2)}100%{transform:scale(1)}}.autocomplete .fi-active .icon-check{transform:rotate(-90deg) !important}.autocomplete .fi-active .suggestions{max-height:var(--dropdownMaxHeight, 300px);border-bottom-left-radius:4px;border-bottom-right-radius:4px;border-right:1px solid;border-bottom:1px solid;border-left:1px solid;border-color:var(--fi-select-border, #888d96);border-top:none;transition:max-height 0.15s ease-in}.autocomplete .fi-active .shadow{box-shadow:inset 0 -10px 7px -10px #888d96}.autocomplete .fi-active .input-wrapper{border-top-left-radius:4px;border-top-right-radius:4px;border:1px solid var(--fi-select-border, #888d96);border-bottom:none;box-sizing:content-box;margin-left:-11px !important;margin-top:-1px;width:100%}.autocomplete .label-as-placeholder{visibility:hidden}.autocomplete label{visibility:visible;font-size:12px;font-weight:normal;font-stretch:normal;font-style:normal;line-height:normal;letter-spacing:normal;color:var(--fi-secondary-400, #8b99ae);display:block;height:14px;text-align:left;padding-left:0;padding-bottom:0}.autocomplete input{font-size:18px;font-weight:normal;box-shadow:none;box-sizing:border-box;line-height:normal;margin:0;max-width:100%;vertical-align:middle;width:100%;border-radius:0;outline:none;border:none;border-bottom:1px solid var(--fi-secondary-600, #d1d6df);background-color:#ffffff;font-stretch:normal;font-style:normal;letter-spacing:normal;color:var(--fi-secondary, #2b2e42);padding:5px 24px 8px 0;caret-color:#888d96;height:36px;font-family:inherit}.autocomplete .valid input{border-bottom:1px solid #00c316}.autocomplete input::placeholder{font-size:18px;font-weight:normal;font-stretch:normal;font-style:normal;line-height:normal;letter-spacing:normal;color:var(--fi-secondary-400, #8b99ae);opacity:1}.autocomplete input:disabled{border-bottom:1px solid #d2d6de;color:#d2d6de;-webkit-text-fill-color:#d2d6de;opacity:1;}.autocomplete input:disabled::placeholder{color:#d2d6de;-webkit-text-fill-color:#d2d6de;opacity:1;}.autocomplete .error input+.underline{transform:scale(1);background-color:var(--fi-error, #ff0226);backface-visibility:hidden;perspective:1000px;animation:shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both}.autocomplete .error_message,.autocomplete .description{font-size:12px;font-weight:normal;font-stretch:normal;font-style:normal;line-height:normal;letter-spacing:normal;position:relative}.autocomplete .error_message{color:var(--fi-error, #ff0226)}.autocomplete .description{padding-top:6px;color:var(--fi-secondary-400, #8b99ae)}.autocomplete .loader{position:absolute;bottom:7px;right:16px}";

let Autocomplete = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.selected = createEvent(this, "selected", 7);
    this.activeIndex = -1;
    this.data = [];
    this.active = false;
    /**
     * The text is displayed by the form field for users
     */
    this.text = "";
    /**
     * Blur event
     */
    this.blurEvent = () => { };
    /**
     * Focus event
     */
    this.focusEvent = () => { };
    /**
     * The actual value of the form field
     */
    this.value = "";
    /**
     * The placeholder for the input field
     */
    this.placeholder = "";
    /**
     * Enable/Disable the input field
     */
    this.disabled = false;
    /**
     * The minimum input size for generating suggestions
     */
    this.minInput = 0;
    /**
     * Form validation: is the form input required
     */
    this.required = false;
    /**
     * A hint to the browser for which keyboard to display.
     * Possible values: `"none"`, `"text"`, `"tel"`, `"url"`,
     * `"email"`, `"numeric"`, `"decimal"`, and `"search"`.
     */
    this.inputType = 'text';
    /**
     * id of the input field
     */
    this.inputId = "";
    /**
     * The class names, which should be set on the rendered html elements
     */
    this.cssClasses = {
      wrapper: "",
      input: "",
      suggestions: "suggestions",
      suggestion: "suggestion",
      active: "active"
    };
    /**
     * Class name that is injected based on field validation
     */
    this.validationClass = "";
    /**
     * debounce time in ms, default is 350
     */
    this.debounceTime = 350;
    /**
     * Suggestions preparation with debounce.
     */
    this.debouncedPreparation = debounce_1(() => {
      this.prepareSuggestions();
    }, this.debounceTime);
    /**
     * Maximum height for dropdown
     */
    this.maxHeight = 150;
    /**
     * When content is loading display loader
     */
    this.loadingContent = false;
    this.prepareSuggestions = async () => {
      if (this.suggestionGenerator && this.text.length >= this.minInput) {
        let suggestions = await this.suggestionGenerator(this.text);
        if (this.otherValue) {
          suggestions = [...suggestions, this.otherValue];
        }
        this.data = suggestions || [];
      }
      else {
        this.data = [];
      }
    };
  }
  /**
   * Values have changed so render new values
   */
  async valuesChanged() {
    await this.prepareSuggestions();
  }
  handleKeyDown(keyCode) {
    if (keyCode == 40 || keyCode == 38) { // up/down arrows
      event.preventDefault();
      this.handleActivation(keyCode == 40);
    }
    else if (keyCode == 13 || keyCode == 9) { // enter/tab
      event.preventDefault();
      this.handleSelection(this.activeIndex);
    }
    else if (keyCode == 27) { // esc
      this.handleClose();
    }
  }
  handleKeyUp(keyCode, text) {
    this.active = true;
    this.text = text;
    if ([40, 38, 13, 9, 27].indexOf(keyCode) < 0) {
      this.debouncedPreparation();
    }
  }
  handleFocus(e) {
    e.preventDefault();
    this.focusEvent();
    this.debouncedPreparation();
    this.active = true;
  }
  handleBlur(e) {
    e.preventDefault();
    setTimeout(() => {
      if (this.active) {
        if (this.value !== '') {
          this.clearData();
          const option = this.data.find((option) => option.value.toLowerCase() === this.value.toLowerCase());
          if (!!option) {
            this.handleSelection(this.data.indexOf(option));
          }
          this.active = false;
        }
        else {
          this.handleClose();
        }
      }
      this.blurEvent();
    }, 250);
  }
  handleClose() {
    this.clearData();
  }
  handleActivation(next = true) {
    if (this.data.length > 0) {
      if (next && (this.activeIndex + 1) < this.data.length) {
        this.activeIndex += 1;
      }
      else if (next) {
        this.activeIndex = 0;
      }
      else if (!next && (this.activeIndex) > 0) {
        this.activeIndex -= 1;
      }
      else if (!next) {
        this.activeIndex = this.data.length - 1;
      }
    }
  }
  handleSelection(index) {
    if (index >= 0 && index < this.data.length && this.data[index].value !== '') {
      this.text = this.data[index].text;
      this.value = this.data[index].value;
      this.activeIndex = index;
      this.selected.emit(this.data[index]);
      this.clearData();
    }
  }
  clearData() {
    this.activeIndex = -1;
    this.active = false;
  }
  getLabelClass() {
    if (this.value.length > 0)
      return 'label';
    else
      return 'label-as-placeholder';
  }
  focusInput() {
    if (this.active) {
      this.el.querySelector('input').blur();
    }
    else {
      this.el.querySelector('input').focus();
    }
  }
  render() {
    return (h("div", { class: "autocomplete" }, h("div", { class: `${this.validationClass} input ${this.active ? 'fi-active' : ''}` }, h("div", { class: "input-wrapper" }, h("label", { htmlFor: this.inputId, class: this.getLabelClass() }, this.placeholder), h("input", { name: this.inputId, class: "autocomplete-input", type: this.inputType, value: this.text, disabled: this.disabled, placeholder: this.placeholder, spellcheck: false, autoCapitalize: "off", autoCorrect: "off", autoComplete: "off", onKeyDown: (e) => this.handleKeyDown(e.keyCode), onKeyUp: (e) => this.handleKeyUp(e.keyCode, e.target['value']), onBlur: (e) => {
        this.handleBlur(e);
      }, onFocus: (e) => {
        this.handleFocus(e);
      } }), h("span", { class: "underline" }), this.loadingContent && h("fi-loader", { class: "loader", size: 10 }), h("div", { class: `${this.disabled ? 'icon-disabled ' : ''} icon ${this.validationClass === 'valid' ? 'icon-valid' : 'icon-check'}`, onClick: () => this.focusInput() }, this.validationClass === 'valid' ?
      h("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 16 16" }, h("g", { fill: "none", "fill-rule": "evenodd" }, h("g", { fill: "#00C316" }, h("g", null, h("path", { d: "M3.175 10.407c-.233-.225-.233-.589 0-.814l8.547-8.349c.338-.325.887-.325 1.225 0 .337.326.337.855 0 1.18L5.2 10l3.746 3.575c.337.326.337.855 0 1.18-.338.326-.887.326-1.225 0l-4.547-4.348z", transform: "translate(-314 -310) translate(20 283) matrix(0 -1 -1 0 310.1 43.1)" })))))
      :
        h("svg", { xmlns: "http://www.w3.org/2000/svg", width: "30", height: "30", viewBox: "0 0 30 30" }, h("g", { fill: "none", "fill-rule": "evenodd" }, h("g", { fill: "#535567" }, h("g", null, h("path", { d: "M17.563 20.756l-5.393-5.349c-.227-.225-.227-.589 0-.814l5.393-5.349c.328-.325.862-.325 1.19 0 .329.326.329.855 0 1.18L14.14 15l4.614 4.575c.328.326.328.855 0 1.18-.329.326-.863.326-1.19 0", transform: "translate(-290 -274) translate(30 239) matrix(-1 0 0 1 291 35)" }))))))), this.active
      ? h("div", { class: `${this.cssClasses.suggestions} ${this.data.length > 5 ? 'shadow' : ''}`, style: { '--dropdownMaxHeight': `${this.data.length > 5 ? 198 : 300}px` } }, this.loadingContent && h("div", { style: { textAlign: 'center' } }, h("fi-loader", { size: 16 })), this.data.map((suggestion, index) => {
        return h("button", { onClick: () => this.handleSelection(index), type: "button", class: this.cssClasses.suggestion + (this.value == suggestion.value ? (" " + this.cssClasses.active) : "") + (this.otherValue && this.data && index === this.data.length - 1 ? ' last' : ''), "data-value": suggestion.value }, this.loadingContent && suggestion.value === "" ? "" : suggestion.text);
      }))
      : "")));
  }
  get el() { return getElement(this); }
};
Autocomplete.style = autocompleteCss;

const buttonCss = ".button-disabled.sc-fi-button{pointer-events:none}.button-fake-disable.sc-fi-button{background-color:var(--fi-disabled-background, #fff0bb);color:var(--fi-disabled-text, #989791);opacity:1;pointer-events:initial}button.sc-fi-button{border:none;border-radius:var(--fi-button-border-radius, 6px);font-size:16px;text-transform:uppercase;cursor:pointer;outline:none;width:100%;font-family:inherit;font-weight:bold;padding:0;font-stretch:normal;font-style:normal;line-height:1.25;letter-spacing:0.5px;text-align:center;color:var(--primary)}button.sc-fi-button .button-content.sc-fi-button{display:flex;justify-content:space-around;align-items:center;vertical-align:middle;height:100%;padding:0 15px}button.sc-fi-button .button-content.sc-fi-button .button-text.sc-fi-button{display:flex;white-space:nowrap;font-weight:bold}button.sc-fi-button .button-content.sc-fi-button .button-icon.sc-fi-button{margin-right:10px}button.sc-fi-button .b-continue.sc-fi-button{justify-content:space-between}button.sc-fi-button .b-continue.sc-fi-button:not(.b-image) .button-image.sc-fi-button{padding-left:15px}button.sc-fi-button .b-tiny.sc-fi-button{padding:6px 10px}button.sc-fi-button .b-tiny.sc-fi-button>div.sc-fi-button{display:flex;height:100%;font-size:14px;text-transform:none}button.sc-fi-button .b-tiny.sc-fi-button>div.sc-fi-button img.sc-fi-button{padding-right:8px}button.sc-fi-button .b-small.sc-fi-button{padding:0 10px}button.sc-fi-button .b-small.sc-fi-button>div.sc-fi-button{display:flex;align-items:center;height:100%}button.sc-fi-button .b-small.sc-fi-button>div.sc-fi-button img.sc-fi-button{padding-right:8px}.button-tiny.sc-fi-button{height:auto;width:auto}.button-small.sc-fi-button{height:40px}.button-large.sc-fi-button{height:50px}.flat.sc-fi-button{background-color:transparent;color:var(--fi-text-color, #2b2e42)}.flat.default.sc-fi-button{background-color:var(--fi-secondary-700, #e3e8e9)}.flat.sc-fi-button:hover{background-color:var(--fi-background-color, #eef5f7)}.flat.primary.sc-fi-button{background-color:var(--fi-primary, #ffc809);box-shadow:var(--fi-button-shadow, none)}.flat.primary.sc-fi-button .button-content.sc-fi-button>div.sc-fi-button{color:var(--fi-button-color, #2b2e42)}.flat.primary.sc-fi-button:hover{background-color:var(--fi-primary-300, #ffde6c)}@media (hover: none), (hover: on-demand){.flat.primary.sc-fi-button:hover{background-color:var(--fi-primary, #ffc809)}}.flat.primary.button-disabled.sc-fi-button{background-color:var(--fi-disabled-background, #fff0bb);box-shadow:var(--fi-button-shadow-disabled, none)}.flat.primary.button-disabled.sc-fi-button .button-content.sc-fi-button>div.sc-fi-button{opacity:1;color:var(--fi-disabled-text, #989791)}.flat.primary.sc-fi-button .button-disabled.sc-fi-button{opacity:1;pointer-events:initial}.flat.secondary.sc-fi-button{background-color:var(--fi-secondary, #2b2e42);color:var(--fi-secondary-900, #ffffff)}.flat.secondary.sc-fi-button .button-content.sc-fi-button>div.sc-fi-button{color:var(--fi-secondary-900, #ffffff)}.flat.secondary.sc-fi-button:hover{background-color:var(--fi-secondary-100, #535567)}@media (hover: none), (hover: on-demand){.flat.secondary.sc-fi-button:hover{background-color:var(--fi-secondary, #2b2e42)}}.flat.secondary.button-disabled.sc-fi-button{background-color:var(--fi-secondary-100, #535567)}.flat.secondary.button-disabled.sc-fi-button{color:var(--fi-secondary-900, #ffffff)}.flat.white.sc-fi-button{background-color:var(--fi-secondary-900, #ffffff);color:var(--fi-secondary, #2b2e42)}.flat.white.sc-fi-button .button-content.sc-fi-button>div.sc-fi-button{color:var(--fi-secondary, #2b2e42)}.flat.danger.sc-fi-button{background-color:var(--fi-errorr, #ff0226);color:var(--fi-secondary-100, #ffffff)}.flat.danger.sc-fi-button:hover{opacity:0.8}@media (hover: none), (hover: on-demand){.flat.danger.sc-fi-button:hover{opacity:1}}.outline.sc-fi-button{background-color:transparent;border:1px solid var(--fi-secondary-100, #535567)}.outline.sc-fi-button:hover{color:var(--fi-secondary-100, #535567)}.outline.default.sc-fi-button{color:var(--fi-secondary-100, #535567)}.outline.primary.sc-fi-button{border-color:var(--fi-primary, #ffc809);color:var(--fi-primary, #ffc809)}.outline.primary.sc-fi-button:hover,.outline.primary.button-disabled.sc-fi-button{border-color:var(--fi-primary-300, #ffde6c);color:var(--fi-primary-300, #ffde6c)}@media (hover: none), (hover: on-demand){.outline.primary.sc-fi-button:hover{border-color:var(--fi-primary, #ffc809);color:var(--fi-primary, #ffc809)}}.outline.secondary.sc-fi-button{border-color:var(--fi-secondary, #2b2e42);color:var(--fi-secondary, #2b2e42)}.outline.secondary.sc-fi-button:hover,.outline.secondary.sc-fi-button:hover .button-content.sc-fi-button>div.sc-fi-button,.outline.secondary.button-disabled.sc-fi-button{background-color:var(--fi-secondary, #2b2e42);color:var(--fi-secondary-900, #ffffff) !important}@media (hover: none), (hover: on-demand){.outline.secondary.sc-fi-button:hover,.outline.secondary.sc-fi-button:hover .button-content.sc-fi-button>div.sc-fi-button,.outline.secondary.button-disabled.sc-fi-button{border-color:var(--fi-secondary, #2b2e42);color:var(--fi-secondary, #2b2e42)}}.outline.danger.sc-fi-button{border-color:var(--fi-error, #ff0226);color:var(--fi-error, #ff0226)}.outline.danger.sc-fi-button:hover,.outline.danger.button-disabled.sc-fi-button{border-color:var(--fi-error, #ff0226);color:var(--fi-error, #ff0226)}@media (hover: none), (hover: on-demand){.outline.danger.sc-fi-button:hover,.outline.danger.button-disabled.sc-fi-button{border-color:var(--fi-error, #ff0226);color:var(--fi-error, #ff0226)}}";

let Button = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    /**
     * The type of button: flat or outline
     */
    this.type = 'flat';
    /**
     * The color of the button e.g. primary
     */
    this.color = 'primary';
    /**
     * Button continue style
     */
    this.continue = false;
    /**
     * Button disabled status
     */
    this.disabled = false;
    /**
     * To show button as disabled but still be clickable
     */
    this.disabledClass = false;
    /**
     * Loading indicator
     */
    this.loading = false;
    /**
     * Button text
     */
    this.text = '';
    /**
     * Button size
     */
    this.size = 'large';
    /**
     * Button image url
     */
    this.image = '';
    /**
     * Button icon image url
     */
    this.icon = '';
    /**
     *
     * @private
     */
    this.buttonType = 'button';
  }
  renderIcon() {
    return h("img", { src: this.icon, alt: "", class: "button-icon" });
  }
  render() {
    const { type, disabled, color, size, text, loading, disabledClass, buttonType } = this;
    return (h(Host, { "aria-disabled": disabled, class: `${disabled && 'button-disabled'} ${(disabled && disabledClass) && 'button-fake-disable'}` }, h("button", { class: `${type} ${color} button-${size} ${(disabled || disabledClass) && 'button-disabled'}`, disabled: disabled, type: buttonType }, h("fi-ripple", null, h("div", { class: `button-content b-${size} ${this.continue ? 'b-continue' : ''} ${this.image ? 'b-image' : ''}` }, h("div", { class: "button-image" }, this.image && !loading ? h("img", { src: this.image, width: 71 }) : null), loading ? h("fi-loader", null, h("div", { style: { display: 'none' } }, h("slot", null))) : h("div", { class: "button-text" }, this.icon && this.renderIcon(), h("div", null, text, h("slot", null))), h("div", { class: "button-continue" }, this.continue && !loading ?
      h("svg", { xmlns: "http://www.w3.org/2000/svg", width: "15", height: "24", viewBox: "0 0 16 25", fill: "none" }, h("g", { "clip-path": "url(#clip0_122_29)" }, h("path", { d: "M15.4973 12.4823L3.48706 0.494629L0.500688 3.47541L12.5109 15.4631L15.4973 12.4823Z", fill: "currentColor" }), h("path", { d: "M3.49072 24.4998L15.5009 12.5121L12.5145 9.53137L0.504346 21.5191L3.49072 24.4998Z", fill: "currentColor" })), h("defs", null, h("clipPath", { id: "clip0_122_29" }, h("rect", { width: "15", height: "24", fill: "white", transform: "translate(0.5 0.5)" }))))
      : null))))));
  }
};
Button.style = buttonCss;

const buttonCollectionCss = ".sc-fi-button-collection-h{display:block}.buttons-container.grid.sc-fi-button-collection{display:grid;grid-template-columns:repeat(2, minmax(0, 1fr))}.buttons-container.grid.sc-fi-button-collection .button.sc-fi-button-collection{flex-direction:column;padding:20px}.buttons-container.grid.sc-fi-button-collection .button-continue.sc-fi-button-collection{display:none}.buttons-container.grid.sc-fi-button-collection .button-text.sc-fi-button-collection{flex:initial}.buttons-container.sc-fi-button-collection .disabled.sc-fi-button-collection{opacity:0.5;pointer-events:none}.buttons-container.sc-fi-button-collection .button.sc-fi-button-collection{padding:10px 10px 10px 15px;border-radius:var(--fi-button-border-radius, 12px);border:solid 1px #c4ccd9;background-color:#ffffff;display:flex;justify-content:center;align-items:center;cursor:pointer;min-height:78px;text-align:left}@media only screen and (min-width: 576px){.buttons-container.sc-fi-button-collection .button.sc-fi-button-collection{padding:20px 10px 20px 20px}}.buttons-container.sc-fi-button-collection .button-container.sc-fi-button-collection{box-shadow:0 20px 15px -15px rgba(196, 204, 217, 0.7);margin:0 14px 20px;cursor:pointer}.buttons-container.sc-fi-button-collection .button-continue.sc-fi-button-collection{display:flex}.buttons-container.sc-fi-button-collection .button-text.sc-fi-button-collection{flex:1;padding:0 10px 0 15px}.buttons-container.sc-fi-button-collection .button-heading.sc-fi-button-collection{font-size:18px;font-weight:bold;font-stretch:normal;font-style:normal;line-height:1.11;letter-spacing:normal;color:#2b2e42}@media only screen and (min-width: 767px){.buttons-container.sc-fi-button-collection .button-heading.sc-fi-button-collection{font-size:22px}}.buttons-container.sc-fi-button-collection .button-description.sc-fi-button-collection{font-size:12px;font-weight:normal;font-stretch:normal;font-style:normal;line-height:normal;letter-spacing:normal;color:#535567;margin-top:10px}@media only screen and (min-width: 767px){.buttons-container.sc-fi-button-collection .button-description.sc-fi-button-collection{font-size:15px}}";

let ButtonCollection = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.fiInput = createEvent(this, "fiInput", 7);
    /**
     * Select options
     */
    this.options = [];
    /**
     * Buttons layout: list or grid
     */
    this.layout = 'list';
    /**
     * Buttons disabled status
     */
    this.disabled = false;
    this.selectOption = (option) => {
      if (this.disabled) {
        return;
      }
      this.fiInput.emit(option.value);
    };
  }
  buttons(option) {
    var _a, _b, _c, _d;
    return (h("div", { class: `button-container ${this.disabled ? "disabled" : ""}` }, h("fi-ripple", { onClick: () => this.selectOption(option) }, h("div", { class: "button" }, ((_a = option.properties) === null || _a === void 0 ? void 0 : _a.image) ?
      h("div", { class: "button-image" }, h("img", { src: (_b = option.properties) === null || _b === void 0 ? void 0 : _b.image.urlSvg, width: 71 }))
      : '', h("div", { class: "button-text" }, h("div", { class: "button-heading" }, option.label), ((_c = option.properties) === null || _c === void 0 ? void 0 : _c.description) ?
      h("div", { class: "button-description" }, (_d = option.properties) === null || _d === void 0 ? void 0 : _d.description)
      : null), h("div", { class: "button-continue" }, h("svg", { xmlns: "http://www.w3.org/2000/svg", width: "30", height: "30", viewBox: "0 0 30 30" }, h("g", { fill: "none", "fill-rule": "evenodd" }, h("g", { fill: "#535567" }, h("g", null, h("path", { d: "M17.563 20.756l-5.393-5.349c-.227-.225-.227-.589 0-.814l5.393-5.349c.328-.325.862-.325 1.19 0 .329.326.329.855 0 1.18L14.14 15l4.614 4.575c.328.326.328.855 0 1.18-.329.326-.863.326-1.19 0", transform: "translate(-290 -274) translate(30 239) matrix(-1 0 0 1 291 35)" }))))))))));
  }
  generateButtons() {
    return this.options.map(option => this.buttons(option));
  }
  render() {
    return (h(Host, null, h("div", { class: `buttons-container ${this.layout}` }, this.generateButtons())));
  }
};
ButtonCollection.style = buttonCollectionCss;

const buttonSelectCss = ".sc-fi-button-select-h{display:block}.options.sc-fi-button-select{display:grid;grid-template-columns:1fr;gap:20px}@media only screen and (max-width: 800px){.options.sc-fi-button-select{grid-template-columns:1fr}}.options.sc-fi-button-select>button.sc-fi-button-select{position:relative;border-radius:var(--fi-button-border-radius, 4px);font-size:18px;line-height:1.43;cursor:pointer;outline:none;text-align:left;box-sizing:content-box;-webkit-box-sizing:content-box;padding:0;white-space:nowrap;text-transform:none;background-color:var(--fi-button-background, #ffffff);transition:background-color 0.1s ease-in-out;border:1px solid var(--fi-button-border-color, #d1d6df);font-family:inherit}.options.sc-fi-button-select>button.sc-fi-button-select:disabled{opacity:0.5;pointer-events:none}.options.sc-fi-button-select>button.sc-fi-button-select .icon.sc-fi-button-select{position:absolute;display:none;width:12px;height:12px;right:var(--fi-button-padding, 13px);top:var(--fi-button-padding, 13px)}.options.sc-fi-button-select>button.sc-fi-button-select .icon.sc-fi-button-select:after{content:\"\";position:absolute;left:6px;top:2px;width:5px;height:10px;border:solid var(--fi-secondary-color, #2b2e42);border-width:0 1px 1px 0;-webkit-transform:rotate(45deg);-ms-transform:rotate(45deg);transform:rotate(45deg)}.options.sc-fi-button-select>button.active.sc-fi-button-select{background-color:var(--fi-primary-color, #ecfff0);border:1px solid var(--fi-primary-300, #00c316)}.options.sc-fi-button-select>button.active.sc-fi-button-select .icon.sc-fi-button-select{display:block}.options.sc-fi-button-select>button.sc-fi-button-select .button-content.sc-fi-button-select{padding:12px 16px;white-space:nowrap;text-overflow:ellipsis;overflow:hidden;font-family:inherit;position:relative}.options.sc-fi-button-select .fi-button-select-loader.sc-fi-button-select{position:absolute;right:var(--fi-button-padding, 13px);top:var(--fi-button-padding, 13px)}";

let ButtonSelect = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.fiInput = createEvent(this, "fiInput", 7);
    /**
     * Initial value
     */
    this.value = "";
    /**
     * Initial select options
     */
    this.options = [];
    /**
     * Buttons disabled status
     */
    this.disabled = false;
    /**
     * Buttons loading status
     */
    this.loading = false;
    /**
     * Buttons type
     */
    this.type = "select";
    this.selectOption = (option) => {
      if (this.disabled) {
        return;
      }
      this.value = option.value;
      this.fiInput.emit(option.value);
    };
    this.createButtonContent = (option) => {
      return (h("div", { class: "button-content" }, option.properties && option.properties.label ? option.properties.label : option.label));
    };
  }
  createOption(option) {
    return (h("button", { key: option.value, type: "button", class: `button-option ${this.value === option.value.toString() && "active"}`, onClick: () => this.selectOption(option), disabled: this.disabled }, h("fi-ripple", null, this.createButtonContent(option))));
  }
  createOptions() {
    return this.options.map(option => this.createOption(option));
  }
  render() {
    return (h(Host, { class: "fi-button-select" }, h("div", { class: "options" }, this.createOptions())));
  }
};
ButtonSelect.style = buttonSelectCss;

const checkboxCss = ".sc-fi-checkbox-h{display:block;position:relative}.checkbox-container.sc-fi-checkbox{display:block;position:relative;padding-left:30px;cursor:pointer;font-size:16px;font-weight:normal;font-stretch:normal;font-style:normal;line-height:1.43;letter-spacing:normal;color:var(--fi-secondary, #2b2e42);-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}.small-container.sc-fi-checkbox{width:calc(100% - 60px)}.checkbox-container.sc-fi-checkbox input.sc-fi-checkbox{position:absolute;opacity:0;cursor:pointer;height:0;width:0}.checkmark.sc-fi-checkbox{position:absolute;top:0;left:0;height:18px;width:18px;background-color:transparent;border-radius:22%;border:solid 1px var(--fi-secondary-600, #d1d6df)}.checkbox-container.sc-fi-checkbox:hover input.sc-fi-checkbox~.checkmark.sc-fi-checkbox{background-color:transparent}.checkbox-container.sc-fi-checkbox input.sc-fi-checkbox:checked~.checkmark.sc-fi-checkbox{background-color:var(--fi-light-green, #ecfff0);border:solid 1px var(--fi-green, #00c316)}.checkmark.sc-fi-checkbox:after{content:\"\";position:absolute;display:none}.checkbox-container.sc-fi-checkbox input.sc-fi-checkbox:checked~.checkmark.sc-fi-checkbox:after{display:block}.checkbox-container.sc-fi-checkbox .checkmark.sc-fi-checkbox:after{left:6px;top:2px;width:5px;height:10px;border:solid var(--fi-green, #00c316);border-width:0 1px 1px 0;-webkit-transform:rotate(45deg);-ms-transform:rotate(45deg);transform:rotate(45deg)}.error.sc-fi-checkbox{background-color:var(--fi-error, #ff0226) !important;border:none;height:20px;width:20px}.expand.sc-fi-checkbox{position:absolute;top:0;right:0;width:20px;height:20px;cursor:pointer;transition:all 0.35s}.detail-collapse.sc-fi-checkbox{transform:rotate(180deg)}.info.sc-fi-checkbox{position:absolute;top:0;right:0;width:20px;height:20px;cursor:pointer}";

let Checkbox = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.fiInput = createEvent(this, "fiInput", 7);
    this.fiOpenModal = createEvent(this, "fiOpenModal", 7);
    this.fiLinkClick = createEvent(this, "fiLinkClick", 7);
    /**
     * If value is checked
     */
    this.validationClass = "";
    /**
     * If value is checked
     */
    this.checked = false;
    /**
     * Checkbox is disabled
     */
    this.disabled = false;
    /**
     * Name of the input
     */
    this.name = "";
    /**
     * Long label
     */
    this.longLabel = "";
    /**
     * To show info icon
     */
    this.showInfoIcon = false;
    /**
     * Label
     */
    this.label = "";
    this.value = false;
    this.expanded = false;
    this.onChange = () => {
      this.value = !this.value;
      this.fiInput.emit(this.value);
    };
    this.onClick = (event) => {
      if (event.target.className === 'open-modal') {
        event.preventDefault();
        this.fiOpenModal.emit();
      }
    };
    this.onExpand = () => {
      this.expanded = !this.expanded;
    };
    this.labelClick = (e) => {
      if (e.target instanceof HTMLAnchorElement) {
        this.fiLinkClick.emit(e);
      }
    };
    this.renderCheckboxLabel = () => {
      if (this.longLabel && this.expanded) {
        return h("label", { class: "checkbox-label", htmlFor: this.name, innerHTML: this.longLabel, onClick: this.labelClick });
      }
      else if (!this.expanded || !this.longLabel) {
        return h("label", { class: "checkbox-label", htmlFor: this.name, innerHTML: this.label, onClick: this.labelClick });
      }
    };
    this.renderExtendIcon = () => {
      if (!this.longLabel)
        return;
      return (h("div", { onClick: this.onExpand, class: `expand ${this.expanded ? 'detail-collapse' : 'detail-expand'}` }, h("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", viewBox: "0 0 20 20" }, h("g", { fill: "none", "fill-rule": "evenodd" }, h("g", { fill: "#535567" }, h("g", null, h("path", { d: "M11.828 15.756l-5.65-5.349c-.238-.225-.238-.589 0-.814l5.65-5.349c.344-.325.903-.325 1.247 0 .344.326.344.855 0 1.18L8.242 10l4.833 4.575c.344.326.344.855 0 1.18-.344.326-.903.326-1.247 0", transform: "translate(-335 -243) translate(335 243) rotate(-90 9.667 10)" })))))));
    };
    this.renderInfoIcon = () => {
      if (!this.showInfoIcon)
        return;
      return (h("div", { onClick: () => this.fiOpenModal.emit(), class: "info" }, h("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 490 490" }, h("path", { fill: "#535567", d: "M350,210a17.48,17.48,0,0,0-17.5,17.5V393.75a17.5,17.5,0,1,0,35,0V227.5A17.48,17.48,0,0,0,350,210Z", transform: "translate(-105 -35)" }), h("path", { fill: "#535567", d: "M368.17,149.62a22,22,0,1,0-20.34,34.17,22,22,0,0,0,20.34-34.17Z", transform: "translate(-105 -35)" }), h("path", { fill: "#535567", d: "M350,35a245.28,245.28,0,1,0,173.16,71.84A245,245,0,0,0,350,35Zm0,455a210.25,210.25,0,1,1,148.42-61.58A210,210,0,0,1,350,490Z", transform: "translate(-105 -35)" }))));
    };
  }
  /**
   * Update the native input element when the checked changes
   */
  valueChanged() {
    this.value = this.checked;
    this.fiInput.emit(this.value);
  }
  componentWillLoad() {
    this.value = this.checked;
  }
  render() {
    return (h("div", null, h("label", { class: `checkbox-container ${(this.longLabel || this.showInfoIcon) ? 'small-container' : ''}`, onChange: this.onChange, onClick: this.onClick }, h("input", { type: "checkbox", id: this.name, name: this.name, checked: this.value, value: this.value.toString(), disabled: this.disabled }), this.renderCheckboxLabel(), h("slot", null), h("span", { class: `${this.validationClass} checkmark` })), this.renderInfoIcon(), this.renderExtendIcon()));
  }
  static get watchers() { return {
    "checked": ["valueChanged"]
  }; }
};
Checkbox.style = checkboxCss;

const datePickerCss = ".sc-fi-date-h{display:block;text-align:left;position:relative}.sc-fi-date-h>*.sc-fi-date:not(:last-child){margin-right:18px}.sc-fi-date-h .label.sc-fi-date{font-size:12px;color:#8b99ae;position:absolute;z-index:1;left:0;top:0}.sc-fi-date-h .select-container-day.sc-fi-date{max-width:15%;display:inline-block}.sc-fi-date-h .select-container-year.sc-fi-date{max-width:19%;display:inline-block}.sc-fi-date-h .select-container-month.sc-fi-date{max-width:15%;display:inline-block}.sc-fi-date-h .status-icon.sc-fi-date{position:absolute;top:50%;right:4px}.sc-fi-date-h .status-icon.sc-fi-date img.sc-fi-date{animation:scale 0.5s}.sc-fi-date-h .hint.sc-fi-date{font-size:12px;padding-top:6px;text-align:left;color:var(--fi-secondary-400, #8b99ae)}.sc-fi-date-h .hint.warning.sc-fi-date{color:var(--fi-warning, #ff5d00)}.sc-fi-date-h .hint.error.sc-fi-date{color:var(--fi-error, #ff0226)}@keyframes scale{0%{transform:scale(1)}50%{transform:scale(2)}100%{transform:scale(1)}}";

const selectors = {
  DD: '.day-select input',
  MM: '.month-select input',
  YYYY: '.year-select input',
};
let DatePicker = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.fiInputDate = createEvent(this, "fiInputDate", 7);
    this.fiBlur = createEvent(this, "fiBlur", 7);
    /**
     * names for months
     */
    this.months = [];
    /**
     * Max value
     */
    this.maxValue = null;
    /**
     * Min value
     */
    this.minValue = null;
    /**
     * Class name that is injected based on field validation
     */
    this.validationClass = '';
    /**
     * Use native <select>
     */
    this.type = 'auto';
    /**
     * Object of empty value labels year, month, day
     */
    this.emptyValues = { year: 'Year', month: 'Month', day: 'Day' };
    /**
     * Format how dropdowns are displayed eg DD/MM/YYYY
     * Default: 'DD/MM/YYYY'
     */
    this.displayFormat = 'DD/MM/YYYY';
    /**
     * Field label
     */
    this.label = '';
    /**
     * all field inputs are touched or not
     */
    this.touched = false;
    this.selectedDay = '';
    this.selectedMonth = '';
    this.selectedYear = '';
    this.dayValid = '';
    this.dayFieldTouched = false;
    this.monthValid = '';
    this.monthFieldTouched = false;
    this.yearValid = '';
    this.yearFieldTouched = false;
    this.dayRecalculate = () => {
      if (this.getDays.length < parseInt(this.selectedDay, 10)) {
        this.selectedDay = this.getDays.length.toString();
        this.dayValid = 'valid';
      }
    };
    this.focusNextInput = (inputName, stateName, valueLength, valueInt) => {
      var _a;
      if (parseInt(this[stateName]) > valueInt || this[stateName].length === valueLength) {
        const dayIndex = this.dateFormatArray.findIndex(value => value === inputName);
        const nextField = this.dateFormatArray[dayIndex + 1];
        if (nextField !== undefined) {
          (_a = this.el.querySelector(selectors[nextField])) === null || _a === void 0 ? void 0 : _a.select();
        }
      }
    };
    this.onDaySelected = (ev) => {
      this.selectedDay = ev.detail.unmasked;
      this.dayValid = this.selectedDay.length !== 2 ? 'error' : 'valid';
      this.dayRecalculate();
      this.focusNextInput('DD', 'selectedDay', 2, 3);
      this.onDateSelected();
    };
    this.onMonthSelected = (ev) => {
      this.selectedMonth = ev.detail.unmasked;
      if (this.getMonths.length < parseInt(this.selectedMonth, 10)) {
        this.selectedMonth = this.getMonths.length.toString();
      }
      this.monthValid = this.selectedMonth.length !== 2 ? 'error' : 'valid';
      this.dayRecalculate();
      this.focusNextInput('MM', 'selectedMonth', 2, 1);
      this.onDateSelected();
    };
    this.onYearSelected = (ev) => {
      this.selectedYear = ev.detail.unmasked;
      if (this.selectedYear.length === 4 && this.getYears.includes(this.selectedYear)) {
        this.yearValid = 'valid';
      }
      else {
        this.yearValid = 'error';
      }
      this.dayRecalculate();
      this.focusNextInput('YYYY', 'selectedYear', 4, 1900);
      this.onDateSelected();
    };
    this.dayBlur = (e) => {
      this.dayFieldTouched = true;
      if (parseInt(this.selectedDay, 10) < 10 && !this.selectedDay.includes('0')) {
        this.selectedDay = '0' + this.selectedDay;
      }
      this.dayValid = this.selectedDay.length !== 2 ? 'error' : 'valid';
      this.fieldBlur(e);
    };
    this.monthBlur = (e) => {
      this.monthFieldTouched = true;
      if (parseInt(this.selectedMonth, 10) < 10 && !this.selectedMonth.includes('0')) {
        this.selectedMonth = '0' + this.selectedMonth;
      }
      this.monthValid = this.selectedMonth.length !== 2 ? 'error' : 'valid';
      this.fieldBlur(e);
    };
    this.yearBlur = (e) => {
      this.yearFieldTouched = true;
      this.yearValid = !this.getYears.includes(this.selectedYear) ? 'error' : 'valid';
      this.fieldBlur(e);
    };
    this.fieldBlur = (e) => {
      e.stopPropagation();
      if (e.detail.relatedTarget == null) {
        this.fiBlur.emit();
      }
      if (e.detail.relatedTarget instanceof HTMLElement) {
        const targetName = e.detail.relatedTarget.getAttribute('name');
        if (targetName == null || targetName.indexOf(this.name) < 0) {
          this.fiBlur.emit();
        }
      }
    };
    this.getValidationClassForField = (stateName) => {
      if (this.validationClass !== '' && this.dayFieldTouched && this.monthFieldTouched && this.yearFieldTouched) {
        // field is invalid so no need to do any other checks here
        if (this[stateName] !== 'valid') {
          return this[stateName];
        }
        const date = new Date(this.value);
        // check min max value in case all fields are valid
        if (this.dayValid === 'valid' &&
          this.monthValid === 'valid' &&
          this.yearValid === 'valid' &&
          ((this.maxValue !== null && date > this.maxValue) ||
            (this.minValue !== null && date < this.minValue))) {
          return 'error';
        }
        // fields valid and min/max value is correct, so use provided validation class
        return this.validationClass;
      }
      return '';
    };
    this.getValidationClass = () => {
      if (this.validationClass === '') {
        if (this.dayValid === 'valid' &&
          this.monthValid === 'valid' &&
          this.yearValid === 'valid' &&
          this.dayFieldTouched &&
          this.monthFieldTouched &&
          this.yearFieldTouched) {
          return 'valid';
        }
        else if (this.dayValid === 'error' || this.monthValid === 'error' || this.yearValid === 'error') {
          return 'error';
        }
        else {
          return '';
        }
      }
      const date = new Date(this.value);
      if (this.dayFieldTouched &&
        this.monthFieldTouched &&
        this.yearFieldTouched &&
        date < this.maxValue &&
        date > this.minValue) {
        return this.validationClass;
      }
      else {
        return '';
      }
    };
    this.renderCheckmark = () => {
      return (h("div", { class: `status-icon checkmark` }, h("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 16 16" }, h("g", { fill: "none", "fill-rule": "evenodd" }, h("g", { fill: "#00C316" }, h("g", null, h("path", { d: "M3.175 10.407c-.233-.225-.233-.589 0-.814l8.547-8.349c.338-.325.887-.325 1.225 0 .337.326.337.855 0 1.18L5.2 10l3.746 3.575c.337.326.337.855 0 1.18-.338.326-.887.326-1.225 0l-4.547-4.348z", transform: "translate(-314 -310) translate(20 283) matrix(0 -1 -1 0 310.1 43.1)" })))))));
    };
    this.renderWarning = () => {
      return (h("div", { class: `status-icon warning` }, h("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 16 16" }, h("g", { fill: "none", "fill-rule": "evenodd" }, h("g", { fill: "#ff5d00" }, h("g", null, h("path", { d: "m14.933 13.375 -6.5 -11.25c-0.097 -0.167 -0.264 -0.25 -0.433 -0.25s-0.338 0.083 -0.433 0.25l-6.5 11.25C0.875 13.709 1.116 14.125 1.5 14.125h13c0.384 0 0.625 -0.416 0.433 -0.75M7.5 6.5c0 -0.069 0.056 -0.125 0.125 -0.125h0.75c0.069 0 0.125 0.056 0.125 0.125v2.875c0 0.069 -0.056 0.125 -0.125 0.125h-0.75c-0.069 0 -0.125 -0.056 -0.125 -0.125zm0.5 5.5a0.75 0.75 0 0 1 0 -1.5 0.75 0.75 0 0 1 0 1.5" })))))));
    };
  }
  touchedChanged(newValue) {
    if (newValue) {
      this.dayFieldTouched = true;
      this.dayValid = this.selectedDay.length !== 2 ? 'error' : 'valid';
      this.monthFieldTouched = true;
      this.monthValid = this.selectedMonth.length !== 2 ? 'error' : 'valid';
      this.yearFieldTouched = true;
      this.yearValid = !this.getYears.includes(this.selectedYear) ? 'error' : 'valid';
    }
  }
  datepickerDisplayFormatChanged(newValue, oldValue) {
    if (newValue !== oldValue) {
      this.dateFormatArray = newValue.split('/');
    }
  }
  valueChanged(newValue, oldValue) {
    if (newValue !== oldValue && newValue instanceof Date) {
      this.selectedDay = newValue.getDay().toString();
      this.selectedMonth = (newValue.getMonth() + 1).toString();
      this.selectedYear = newValue.getFullYear().toString();
    }
  }
  get getDays() {
    let numDays = new Date(parseInt(this.selectedYear, 10), parseInt(this.selectedMonth, 10) || 0, 0).getDate();
    if (isNaN(numDays))
      numDays = 31;
    let start = 1;
    const days = [];
    /*if (this.maxValue && this.selectedYear === this.maxValue.getFullYear() && this.selectedMonth === this.maxValue.getMonth() + 1) {
      numDays = this.maxValue.getDate()
    }
    if (this.minValue && this.selectedYear === this.minValue.getFullYear() && this.selectedMonth === this.minValue.getMonth() + 1) {
      start = this.minValue.getDate()
    }*/
    for (let index = start; index <= numDays; index++) {
      days.push({
        key: index.toString(),
        value: index.toString(),
        label: index.toString(),
      });
    }
    return days;
  }
  get getMonths() {
    if (this.months.length !== 12) {
      const months = [];
      for (let index = 1; index <= 12; index++) {
        months.push({
          key: index.toString(),
          value: index.toString(),
          label: index.toString(),
        });
      }
      return months;
    }
    return this.months.map((month, i) => ({
      label: month,
      value: (i + 1).toString(),
      key: (i + 1).toString(),
    }));
  }
  get getYears() {
    let max = new Date().getFullYear();
    if (this.maxValue !== null) {
      max = new Date(this.maxValue).getFullYear();
    }
    let min = new Date().getFullYear() - 100;
    if (this.minValue !== null) {
      min = new Date(this.minValue).getFullYear();
    }
    const years = [];
    for (let index = max; index >= min; index--) {
      years.push(index.toString());
    }
    return years;
  }
  onDateSelected() {
    const date = `${this.selectedYear}-${this.selectedMonth}-${this.selectedDay}`;
    this.fiInputDate.emit(date);
  }
  componentWillLoad() {
    if (this.value !== undefined && this.value !== null && this.value !== '') {
      if (typeof this.value === 'string') {
        const dateValues = this.value.split('-');
        // month is 0 indexed
        this.value = new Date(parseInt(dateValues[0], 10), parseInt(dateValues[1], 10) - 1, parseInt(dateValues[2], 10));
      }
      else {
        this.value = new Date(this.value);
      }
      const day = this.value.getDate();
      this.selectedDay = `${day < 10 ? '0' : ''}${day}`;
      const month = this.value.getMonth() + 1;
      this.selectedMonth = `${month < 10 ? '0' : ''}${month}`;
      this.selectedYear = this.value.getFullYear().toString();
      this.dayValid = 'valid';
      this.monthValid = 'valid';
      this.yearValid = 'valid';
      this.dayFieldTouched = true;
      this.monthFieldTouched = true;
      this.yearFieldTouched = true;
    }
    this.dateFormatArray = this.displayFormat.split('/');
    if (!this.dateFormatArray.includes('DD')) {
      this.selectedDay = '01';
      this.dayValid = 'valid';
    }
    if (!this.dateFormatArray.includes('MM')) {
      this.selectedMonth = '01';
      this.monthValid = 'valid';
    }
    if (!this.dateFormatArray.includes('YYYY')) {
      this.selectedYear = new Date().getFullYear().toString();
      this.yearValid = 'valid';
    }
  }
  renderSelect(type) {
    switch (type) {
      case 'DD':
        return (h("div", { class: "select-container-day" }, h("fi-input", { name: this.name + '_day', class: "date-select day-select", placeholder: this.emptyValues.day, value: this.selectedDay, onFiInput: this.onDaySelected, "validation-class": this.getValidationClassForField('dayValid'), validationCheckmarkEnabled: false, pattern: '00', type: "tel", onFiBlur: this.dayBlur })));
      case 'MM':
        return (h("div", { class: "select-container-month" }, h("fi-input", { name: this.name + '_month', class: "date-select month-select", placeholder: this.emptyValues.month, value: this.selectedMonth.toString(), onFiInput: this.onMonthSelected, "validation-class": this.getValidationClassForField('monthValid'), validationCheckmarkEnabled: false, pattern: '00', type: "tel", onFiBlur: this.monthBlur })));
      case 'YYYY':
        return (h("div", { class: "select-container-year" }, h("fi-input", { name: this.name + '_year', class: "date-select year-select", placeholder: this.emptyValues.year, value: this.selectedYear.toString(), onFiInput: this.onYearSelected, "validation-class": this.getValidationClassForField('yearValid'), validationCheckmarkEnabled: false, type: "tel", pattern: '0000', onFiBlur: this.yearBlur })));
    }
  }
  render() {
    return (h(Host, null, h("div", { class: "label" }, this.label), this.dateFormatArray.map(type => this.renderSelect(type)), this.getValidationClass().includes('valid') && this.renderCheckmark(), this.getValidationClass().includes('warning') && this.renderWarning(), this.hint && h("div", { class: `hint ${this.validationClass}` }, this.hint)));
  }
  get el() { return getElement(this); }
  static get watchers() { return {
    "touched": ["touchedChanged"],
    "displayFormat": ["datepickerDisplayFormatChanged"],
    "value": ["valueChanged"]
  }; }
};
DatePicker.style = datePickerCss;

function _typeof(obj) {
  "@babel/helpers - typeof";

  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function (obj) {
      return typeof obj;
    };
  } else {
    _typeof = function (obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _possibleConstructorReturn(self, call) {
  if (call && (typeof call === "object" || typeof call === "function")) {
    return call;
  }

  return _assertThisInitialized(self);
}

function _superPropBase(object, property) {
  while (!Object.prototype.hasOwnProperty.call(object, property)) {
    object = _getPrototypeOf(object);
    if (object === null) break;
  }

  return object;
}

function _get(target, property, receiver) {
  if (typeof Reflect !== "undefined" && Reflect.get) {
    _get = Reflect.get;
  } else {
    _get = function _get(target, property, receiver) {
      var base = _superPropBase(target, property);

      if (!base) return;
      var desc = Object.getOwnPropertyDescriptor(base, property);

      if (desc.get) {
        return desc.get.call(receiver);
      }

      return desc.value;
    };
  }

  return _get(target, property, receiver || target);
}

function set(target, property, value, receiver) {
  if (typeof Reflect !== "undefined" && Reflect.set) {
    set = Reflect.set;
  } else {
    set = function set(target, property, value, receiver) {
      var base = _superPropBase(target, property);

      var desc;

      if (base) {
        desc = Object.getOwnPropertyDescriptor(base, property);

        if (desc.set) {
          desc.set.call(receiver, value);
          return true;
        } else if (!desc.writable) {
          return false;
        }
      }

      desc = Object.getOwnPropertyDescriptor(receiver, property);

      if (desc) {
        if (!desc.writable) {
          return false;
        }

        desc.value = value;
        Object.defineProperty(receiver, property, desc);
      } else {
        _defineProperty(receiver, property, value);
      }

      return true;
    };
  }

  return set(target, property, value, receiver);
}

function _set(target, property, value, receiver, isStrict) {
  var s = set(target, property, value, receiver || target);

  if (!s && isStrict) {
    throw new Error('failed to set property');
  }

  return value;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

/** Checks if value is string */
function isString(str) {
  return typeof str === 'string' || str instanceof String;
}
/**
  Direction
  @prop {string} NONE
  @prop {string} LEFT
  @prop {string} FORCE_LEFT
  @prop {string} RIGHT
  @prop {string} FORCE_RIGHT
*/

var DIRECTION = {
  NONE: 'NONE',
  LEFT: 'LEFT',
  FORCE_LEFT: 'FORCE_LEFT',
  RIGHT: 'RIGHT',
  FORCE_RIGHT: 'FORCE_RIGHT'
};
/** */

function forceDirection(direction) {
  switch (direction) {
    case DIRECTION.LEFT:
      return DIRECTION.FORCE_LEFT;

    case DIRECTION.RIGHT:
      return DIRECTION.FORCE_RIGHT;

    default:
      return direction;
  }
}
/** Escapes regular expression control chars */

function escapeRegExp(str) {
  return str.replace(/([.*+?^=!:${}()|[\]/\\])/g, '\\$1');
} // cloned from https://github.com/epoberezkin/fast-deep-equal with small changes

function objectIncludes(b, a) {
  if (a === b) return true;
  var arrA = Array.isArray(a),
      arrB = Array.isArray(b),
      i;

  if (arrA && arrB) {
    if (a.length != b.length) return false;

    for (i = 0; i < a.length; i++) {
      if (!objectIncludes(a[i], b[i])) return false;
    }

    return true;
  }

  if (arrA != arrB) return false;

  if (a && b && _typeof(a) === 'object' && _typeof(b) === 'object') {
    var dateA = a instanceof Date,
        dateB = b instanceof Date;
    if (dateA && dateB) return a.getTime() == b.getTime();
    if (dateA != dateB) return false;
    var regexpA = a instanceof RegExp,
        regexpB = b instanceof RegExp;
    if (regexpA && regexpB) return a.toString() == b.toString();
    if (regexpA != regexpB) return false;
    var keys = Object.keys(a); // if (keys.length !== Object.keys(b).length) return false;

    for (i = 0; i < keys.length; i++) {
      if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false;
    }

    for (i = 0; i < keys.length; i++) {
      if (!objectIncludes(b[keys[i]], a[keys[i]])) return false;
    }

    return true;
  } else if (a && b && typeof a === 'function' && typeof b === 'function') {
    return a.toString() === b.toString();
  }

  return false;
}

/** Provides details of changing input */

var ActionDetails =
/*#__PURE__*/
function () {
  /** Current input value */

  /** Current cursor position */

  /** Old input value */

  /** Old selection */
  function ActionDetails(value, cursorPos, oldValue, oldSelection) {
    _classCallCheck(this, ActionDetails);

    this.value = value;
    this.cursorPos = cursorPos;
    this.oldValue = oldValue;
    this.oldSelection = oldSelection; // double check if left part was changed (autofilling, other non-standard input triggers)

    while (this.value.slice(0, this.startChangePos) !== this.oldValue.slice(0, this.startChangePos)) {
      --this.oldSelection.start;
    }
  }
  /**
    Start changing position
    @readonly
  */


  _createClass(ActionDetails, [{
    key: "startChangePos",
    get: function get() {
      return Math.min(this.cursorPos, this.oldSelection.start);
    }
    /**
      Inserted symbols count
      @readonly
    */

  }, {
    key: "insertedCount",
    get: function get() {
      return this.cursorPos - this.startChangePos;
    }
    /**
      Inserted symbols
      @readonly
    */

  }, {
    key: "inserted",
    get: function get() {
      return this.value.substr(this.startChangePos, this.insertedCount);
    }
    /**
      Removed symbols count
      @readonly
    */

  }, {
    key: "removedCount",
    get: function get() {
      // Math.max for opposite operation
      return Math.max(this.oldSelection.end - this.startChangePos || // for Delete
      this.oldValue.length - this.value.length, 0);
    }
    /**
      Removed symbols
      @readonly
    */

  }, {
    key: "removed",
    get: function get() {
      return this.oldValue.substr(this.startChangePos, this.removedCount);
    }
    /**
      Unchanged head symbols
      @readonly
    */

  }, {
    key: "head",
    get: function get() {
      return this.value.substring(0, this.startChangePos);
    }
    /**
      Unchanged tail symbols
      @readonly
    */

  }, {
    key: "tail",
    get: function get() {
      return this.value.substring(this.startChangePos + this.insertedCount);
    }
    /**
      Remove direction
      @readonly
    */

  }, {
    key: "removeDirection",
    get: function get() {
      if (!this.removedCount || this.insertedCount) return DIRECTION.NONE; // align right if delete at right or if range removed (event with backspace)

      return this.oldSelection.end === this.cursorPos || this.oldSelection.start === this.cursorPos ? DIRECTION.RIGHT : DIRECTION.LEFT;
    }
  }]);

  return ActionDetails;
}();

/**
  Provides details of changing model value
  @param {Object} [details]
  @param {string} [details.inserted] - Inserted symbols
  @param {boolean} [details.skip] - Can skip chars
  @param {number} [details.removeCount] - Removed symbols count
  @param {number} [details.tailShift] - Additional offset if any changes occurred before tail
*/
var ChangeDetails =
/*#__PURE__*/
function () {
  /** Inserted symbols */

  /** Can skip chars */

  /** Additional offset if any changes occurred before tail */

  /** Raw inserted is used by dynamic mask */
  function ChangeDetails(details) {
    _classCallCheck(this, ChangeDetails);

    Object.assign(this, {
      inserted: '',
      rawInserted: '',
      skip: false,
      tailShift: 0
    }, details);
  }
  /**
    Aggregate changes
    @returns {ChangeDetails} `this`
  */


  _createClass(ChangeDetails, [{
    key: "aggregate",
    value: function aggregate(details) {
      this.rawInserted += details.rawInserted;
      this.skip = this.skip || details.skip;
      this.inserted += details.inserted;
      this.tailShift += details.tailShift;
      return this;
    }
    /** Total offset considering all changes */

  }, {
    key: "offset",
    get: function get() {
      return this.tailShift + this.inserted.length;
    }
  }]);

  return ChangeDetails;
}();

/** Provides details of continuous extracted tail */
var ContinuousTailDetails =
/*#__PURE__*/
function () {
  /** Tail value as string */

  /** Tail start position */

  /** Start position */
  function ContinuousTailDetails() {
    var value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
    var from = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    var stop = arguments.length > 2 ? arguments[2] : undefined;

    _classCallCheck(this, ContinuousTailDetails);

    this.value = value;
    this.from = from;
    this.stop = stop;
  }

  _createClass(ContinuousTailDetails, [{
    key: "toString",
    value: function toString() {
      return this.value;
    }
  }, {
    key: "extend",
    value: function extend(tail) {
      this.value += String(tail);
    }
  }, {
    key: "appendTo",
    value: function appendTo(masked) {
      return masked.append(this.toString(), {
        tail: true
      }).aggregate(masked._appendPlaceholder());
    }
  }, {
    key: "shiftBefore",
    value: function shiftBefore(pos) {
      if (this.from >= pos || !this.value.length) return '';
      var shiftChar = this.value[0];
      this.value = this.value.slice(1);
      return shiftChar;
    }
  }, {
    key: "state",
    get: function get() {
      return {
        value: this.value,
        from: this.from,
        stop: this.stop
      };
    },
    set: function set(state) {
      Object.assign(this, state);
    }
  }]);

  return ContinuousTailDetails;
}();

/**
 * Applies mask on element.
 * @constructor
 * @param {HTMLInputElement|HTMLTextAreaElement|MaskElement} el - Element to apply mask
 * @param {Object} opts - Custom mask options
 * @return {InputMask}
 */
function IMask(el) {
  var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  // currently available only for input-like elements
  return new IMask.InputMask(el, opts);
}

/** Supported mask type */

/** Provides common masking stuff */
var Masked =
/*#__PURE__*/
function () {
  // $Shape<MaskedOptions>; TODO after fix https://github.com/facebook/flow/issues/4773

  /** @type {Mask} */

  /** */
  // $FlowFixMe no ideas

  /** Transforms value before mask processing */

  /** Validates if value is acceptable */

  /** Does additional processing in the end of editing */

  /** Format typed value to string */

  /** Parse strgin to get typed value */

  /** Enable characters overwriting */

  /** */
  function Masked(opts) {
    _classCallCheck(this, Masked);

    this._value = '';

    this._update(Object.assign({}, Masked.DEFAULTS, {}, opts));

    this.isInitialized = true;
  }
  /** Sets and applies new options */


  _createClass(Masked, [{
    key: "updateOptions",
    value: function updateOptions(opts) {
      if (!Object.keys(opts).length) return;
      this.withValueRefresh(this._update.bind(this, opts));
    }
    /**
      Sets new options
      @protected
    */

  }, {
    key: "_update",
    value: function _update(opts) {
      Object.assign(this, opts);
    }
    /** Mask state */

  }, {
    key: "reset",

    /** Resets value */
    value: function reset() {
      this._value = '';
    }
    /** */

  }, {
    key: "resolve",

    /** Resolve new value */
    value: function resolve(value) {
      this.reset();
      this.append(value, {
        input: true
      }, '');
      this.doCommit();
      return this.value;
    }
    /** */

  }, {
    key: "nearestInputPos",

    /** Finds nearest input position in direction */
    value: function nearestInputPos(cursorPos, direction) {
      return cursorPos;
    }
    /** Extracts value in range considering flags */

  }, {
    key: "extractInput",
    value: function extractInput() {
      var fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      var toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
      return this.value.slice(fromPos, toPos);
    }
    /** Extracts tail in range */

  }, {
    key: "extractTail",
    value: function extractTail() {
      var fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      var toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
      return new ContinuousTailDetails(this.extractInput(fromPos, toPos), fromPos);
    }
    /** Appends tail */
    // $FlowFixMe no ideas

  }, {
    key: "appendTail",
    value: function appendTail(tail) {
      if (isString(tail)) tail = new ContinuousTailDetails(String(tail));
      return tail.appendTo(this);
    }
    /** Appends char */

  }, {
    key: "_appendCharRaw",
    value: function _appendCharRaw(ch) {
      var flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      ch = this.doPrepare(ch, flags);
      if (!ch) return new ChangeDetails();
      this._value += ch;
      return new ChangeDetails({
        inserted: ch,
        rawInserted: ch
      });
    }
    /** Appends char */

  }, {
    key: "_appendChar",
    value: function _appendChar(ch) {
      var flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var checkTail = arguments.length > 2 ? arguments[2] : undefined;
      var consistentState = this.state;

      var details = this._appendCharRaw(ch, flags);

      if (details.inserted) {
        var consistentTail;
        var appended = this.doValidate(flags) !== false;

        if (appended && checkTail != null) {
          // validation ok, check tail
          var beforeTailState = this.state;

          if (this.overwrite) {
            consistentTail = checkTail.state;
            checkTail.shiftBefore(this.value.length);
          }

          var tailDetails = this.appendTail(checkTail);
          appended = tailDetails.rawInserted === checkTail.toString(); // if ok, rollback state after tail

          if (appended && tailDetails.inserted) this.state = beforeTailState;
        } // revert all if something went wrong


        if (!appended) {
          details = new ChangeDetails();
          this.state = consistentState;
          if (checkTail && consistentTail) checkTail.state = consistentTail;
        }
      }

      return details;
    }
    /** Appends optional placeholder at end */

  }, {
    key: "_appendPlaceholder",
    value: function _appendPlaceholder() {
      return new ChangeDetails();
    }
    /** Appends symbols considering flags */
    // $FlowFixMe no ideas

  }, {
    key: "append",
    value: function append(str, flags, tail) {
      if (!isString(str)) throw new Error('value should be string');
      var details = new ChangeDetails();
      var checkTail = isString(tail) ? new ContinuousTailDetails(String(tail)) : tail;
      if (flags.tail) flags._beforeTailState = this.state;

      for (var ci = 0; ci < str.length; ++ci) {
        details.aggregate(this._appendChar(str[ci], flags, checkTail));
      } // append tail but aggregate only tailShift


      if (checkTail != null) {
        details.tailShift += this.appendTail(checkTail).tailShift; // TODO it's a good idea to clear state after appending ends
        // but it causes bugs when one append calls another (when dynamic dispatch set rawInputValue)
        // this._resetBeforeTailState();
      }

      return details;
    }
    /** */

  }, {
    key: "remove",
    value: function remove() {
      var fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      var toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
      this._value = this.value.slice(0, fromPos) + this.value.slice(toPos);
      return new ChangeDetails();
    }
    /** Calls function and reapplies current value */

  }, {
    key: "withValueRefresh",
    value: function withValueRefresh(fn) {
      if (this._refreshing || !this.isInitialized) return fn();
      this._refreshing = true;
      var rawInput = this.rawInputValue;
      var value = this.value;
      var ret = fn();
      this.rawInputValue = rawInput; // append lost trailing chars at end

      if (this.value !== value && value.indexOf(this.value) === 0) {
        this.append(value.slice(this.value.length), {}, '');
      }

      delete this._refreshing;
      return ret;
    }
    /** */

  }, {
    key: "runIsolated",
    value: function runIsolated(fn) {
      if (this._isolated || !this.isInitialized) return fn(this);
      this._isolated = true;
      var state = this.state;
      var ret = fn(this);
      this.state = state;
      delete this._isolated;
      return ret;
    }
    /**
      Prepares string before mask processing
      @protected
    */

  }, {
    key: "doPrepare",
    value: function doPrepare(str) {
      var flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      return this.prepare ? this.prepare(str, this, flags) : str;
    }
    /**
      Validates if value is acceptable
      @protected
    */

  }, {
    key: "doValidate",
    value: function doValidate(flags) {
      return (!this.validate || this.validate(this.value, this, flags)) && (!this.parent || this.parent.doValidate(flags));
    }
    /**
      Does additional processing in the end of editing
      @protected
    */

  }, {
    key: "doCommit",
    value: function doCommit() {
      if (this.commit) this.commit(this.value, this);
    }
    /** */

  }, {
    key: "doFormat",
    value: function doFormat(value) {
      return this.format ? this.format(value, this) : value;
    }
    /** */

  }, {
    key: "doParse",
    value: function doParse(str) {
      return this.parse ? this.parse(str, this) : str;
    }
    /** */

  }, {
    key: "splice",
    value: function splice(start, deleteCount, inserted, removeDirection) {
      var tailPos = start + deleteCount;
      var tail = this.extractTail(tailPos);
      var startChangePos = this.nearestInputPos(start, removeDirection);
      var changeDetails = new ChangeDetails({
        tailShift: startChangePos - start // adjust tailShift if start was aligned

      }).aggregate(this.remove(startChangePos)).aggregate(this.append(inserted, {
        input: true
      }, tail));
      return changeDetails;
    }
  }, {
    key: "state",
    get: function get() {
      return {
        _value: this.value
      };
    },
    set: function set(state) {
      this._value = state._value;
    }
  }, {
    key: "value",
    get: function get() {
      return this._value;
    },
    set: function set(value) {
      this.resolve(value);
    }
  }, {
    key: "unmaskedValue",
    get: function get() {
      return this.value;
    },
    set: function set(value) {
      this.reset();
      this.append(value, {}, '');
      this.doCommit();
    }
    /** */

  }, {
    key: "typedValue",
    get: function get() {
      return this.doParse(this.value);
    },
    set: function set(value) {
      this.value = this.doFormat(value);
    }
    /** Value that includes raw user input */

  }, {
    key: "rawInputValue",
    get: function get() {
      return this.extractInput(0, this.value.length, {
        raw: true
      });
    },
    set: function set(value) {
      this.reset();
      this.append(value, {
        raw: true
      }, '');
      this.doCommit();
    }
    /** */

  }, {
    key: "isComplete",
    get: function get() {
      return true;
    }
  }]);

  return Masked;
}();
Masked.DEFAULTS = {
  format: function format(v) {
    return v;
  },
  parse: function parse(v) {
    return v;
  }
};
IMask.Masked = Masked;

/** Get Masked class by mask type */

function maskedClass(mask) {
  if (mask == null) {
    throw new Error('mask property should be defined');
  } // $FlowFixMe


  if (mask instanceof RegExp) return IMask.MaskedRegExp; // $FlowFixMe

  if (isString(mask)) return IMask.MaskedPattern; // $FlowFixMe

  if (mask instanceof Date || mask === Date) return IMask.MaskedDate; // $FlowFixMe

  if (mask instanceof Number || typeof mask === 'number' || mask === Number) return IMask.MaskedNumber; // $FlowFixMe

  if (Array.isArray(mask) || mask === Array) return IMask.MaskedDynamic; // $FlowFixMe

  if (IMask.Masked && mask.prototype instanceof IMask.Masked) return mask; // $FlowFixMe

  if (mask instanceof Function) return IMask.MaskedFunction; // $FlowFixMe

  if (mask instanceof IMask.Masked) return mask.constructor;
  console.warn('Mask not found for mask', mask); // eslint-disable-line no-console
  // $FlowFixMe

  return IMask.Masked;
}
/** Creates new {@link Masked} depending on mask type */

function createMask(opts) {
  // $FlowFixMe
  if (IMask.Masked && opts instanceof IMask.Masked) return opts;
  opts = Object.assign({}, opts);
  var mask = opts.mask; // $FlowFixMe

  if (IMask.Masked && mask instanceof IMask.Masked) return mask;
  var MaskedClass = maskedClass(mask);
  if (!MaskedClass) throw new Error('Masked class is not found for provided mask, appropriate module needs to be import manually before creating mask.');
  return new MaskedClass(opts);
}
IMask.createMask = createMask;

var DEFAULT_INPUT_DEFINITIONS = {
  '0': /\d/,
  'a': /[\u0041-\u005A\u0061-\u007A\u00AA\u00B5\u00BA\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02C1\u02C6-\u02D1\u02E0-\u02E4\u02EC\u02EE\u0370-\u0374\u0376\u0377\u037A-\u037D\u0386\u0388-\u038A\u038C\u038E-\u03A1\u03A3-\u03F5\u03F7-\u0481\u048A-\u0527\u0531-\u0556\u0559\u0561-\u0587\u05D0-\u05EA\u05F0-\u05F2\u0620-\u064A\u066E\u066F\u0671-\u06D3\u06D5\u06E5\u06E6\u06EE\u06EF\u06FA-\u06FC\u06FF\u0710\u0712-\u072F\u074D-\u07A5\u07B1\u07CA-\u07EA\u07F4\u07F5\u07FA\u0800-\u0815\u081A\u0824\u0828\u0840-\u0858\u08A0\u08A2-\u08AC\u0904-\u0939\u093D\u0950\u0958-\u0961\u0971-\u0977\u0979-\u097F\u0985-\u098C\u098F\u0990\u0993-\u09A8\u09AA-\u09B0\u09B2\u09B6-\u09B9\u09BD\u09CE\u09DC\u09DD\u09DF-\u09E1\u09F0\u09F1\u0A05-\u0A0A\u0A0F\u0A10\u0A13-\u0A28\u0A2A-\u0A30\u0A32\u0A33\u0A35\u0A36\u0A38\u0A39\u0A59-\u0A5C\u0A5E\u0A72-\u0A74\u0A85-\u0A8D\u0A8F-\u0A91\u0A93-\u0AA8\u0AAA-\u0AB0\u0AB2\u0AB3\u0AB5-\u0AB9\u0ABD\u0AD0\u0AE0\u0AE1\u0B05-\u0B0C\u0B0F\u0B10\u0B13-\u0B28\u0B2A-\u0B30\u0B32\u0B33\u0B35-\u0B39\u0B3D\u0B5C\u0B5D\u0B5F-\u0B61\u0B71\u0B83\u0B85-\u0B8A\u0B8E-\u0B90\u0B92-\u0B95\u0B99\u0B9A\u0B9C\u0B9E\u0B9F\u0BA3\u0BA4\u0BA8-\u0BAA\u0BAE-\u0BB9\u0BD0\u0C05-\u0C0C\u0C0E-\u0C10\u0C12-\u0C28\u0C2A-\u0C33\u0C35-\u0C39\u0C3D\u0C58\u0C59\u0C60\u0C61\u0C85-\u0C8C\u0C8E-\u0C90\u0C92-\u0CA8\u0CAA-\u0CB3\u0CB5-\u0CB9\u0CBD\u0CDE\u0CE0\u0CE1\u0CF1\u0CF2\u0D05-\u0D0C\u0D0E-\u0D10\u0D12-\u0D3A\u0D3D\u0D4E\u0D60\u0D61\u0D7A-\u0D7F\u0D85-\u0D96\u0D9A-\u0DB1\u0DB3-\u0DBB\u0DBD\u0DC0-\u0DC6\u0E01-\u0E30\u0E32\u0E33\u0E40-\u0E46\u0E81\u0E82\u0E84\u0E87\u0E88\u0E8A\u0E8D\u0E94-\u0E97\u0E99-\u0E9F\u0EA1-\u0EA3\u0EA5\u0EA7\u0EAA\u0EAB\u0EAD-\u0EB0\u0EB2\u0EB3\u0EBD\u0EC0-\u0EC4\u0EC6\u0EDC-\u0EDF\u0F00\u0F40-\u0F47\u0F49-\u0F6C\u0F88-\u0F8C\u1000-\u102A\u103F\u1050-\u1055\u105A-\u105D\u1061\u1065\u1066\u106E-\u1070\u1075-\u1081\u108E\u10A0-\u10C5\u10C7\u10CD\u10D0-\u10FA\u10FC-\u1248\u124A-\u124D\u1250-\u1256\u1258\u125A-\u125D\u1260-\u1288\u128A-\u128D\u1290-\u12B0\u12B2-\u12B5\u12B8-\u12BE\u12C0\u12C2-\u12C5\u12C8-\u12D6\u12D8-\u1310\u1312-\u1315\u1318-\u135A\u1380-\u138F\u13A0-\u13F4\u1401-\u166C\u166F-\u167F\u1681-\u169A\u16A0-\u16EA\u1700-\u170C\u170E-\u1711\u1720-\u1731\u1740-\u1751\u1760-\u176C\u176E-\u1770\u1780-\u17B3\u17D7\u17DC\u1820-\u1877\u1880-\u18A8\u18AA\u18B0-\u18F5\u1900-\u191C\u1950-\u196D\u1970-\u1974\u1980-\u19AB\u19C1-\u19C7\u1A00-\u1A16\u1A20-\u1A54\u1AA7\u1B05-\u1B33\u1B45-\u1B4B\u1B83-\u1BA0\u1BAE\u1BAF\u1BBA-\u1BE5\u1C00-\u1C23\u1C4D-\u1C4F\u1C5A-\u1C7D\u1CE9-\u1CEC\u1CEE-\u1CF1\u1CF5\u1CF6\u1D00-\u1DBF\u1E00-\u1F15\u1F18-\u1F1D\u1F20-\u1F45\u1F48-\u1F4D\u1F50-\u1F57\u1F59\u1F5B\u1F5D\u1F5F-\u1F7D\u1F80-\u1FB4\u1FB6-\u1FBC\u1FBE\u1FC2-\u1FC4\u1FC6-\u1FCC\u1FD0-\u1FD3\u1FD6-\u1FDB\u1FE0-\u1FEC\u1FF2-\u1FF4\u1FF6-\u1FFC\u2071\u207F\u2090-\u209C\u2102\u2107\u210A-\u2113\u2115\u2119-\u211D\u2124\u2126\u2128\u212A-\u212D\u212F-\u2139\u213C-\u213F\u2145-\u2149\u214E\u2183\u2184\u2C00-\u2C2E\u2C30-\u2C5E\u2C60-\u2CE4\u2CEB-\u2CEE\u2CF2\u2CF3\u2D00-\u2D25\u2D27\u2D2D\u2D30-\u2D67\u2D6F\u2D80-\u2D96\u2DA0-\u2DA6\u2DA8-\u2DAE\u2DB0-\u2DB6\u2DB8-\u2DBE\u2DC0-\u2DC6\u2DC8-\u2DCE\u2DD0-\u2DD6\u2DD8-\u2DDE\u2E2F\u3005\u3006\u3031-\u3035\u303B\u303C\u3041-\u3096\u309D-\u309F\u30A1-\u30FA\u30FC-\u30FF\u3105-\u312D\u3131-\u318E\u31A0-\u31BA\u31F0-\u31FF\u3400-\u4DB5\u4E00-\u9FCC\uA000-\uA48C\uA4D0-\uA4FD\uA500-\uA60C\uA610-\uA61F\uA62A\uA62B\uA640-\uA66E\uA67F-\uA697\uA6A0-\uA6E5\uA717-\uA71F\uA722-\uA788\uA78B-\uA78E\uA790-\uA793\uA7A0-\uA7AA\uA7F8-\uA801\uA803-\uA805\uA807-\uA80A\uA80C-\uA822\uA840-\uA873\uA882-\uA8B3\uA8F2-\uA8F7\uA8FB\uA90A-\uA925\uA930-\uA946\uA960-\uA97C\uA984-\uA9B2\uA9CF\uAA00-\uAA28\uAA40-\uAA42\uAA44-\uAA4B\uAA60-\uAA76\uAA7A\uAA80-\uAAAF\uAAB1\uAAB5\uAAB6\uAAB9-\uAABD\uAAC0\uAAC2\uAADB-\uAADD\uAAE0-\uAAEA\uAAF2-\uAAF4\uAB01-\uAB06\uAB09-\uAB0E\uAB11-\uAB16\uAB20-\uAB26\uAB28-\uAB2E\uABC0-\uABE2\uAC00-\uD7A3\uD7B0-\uD7C6\uD7CB-\uD7FB\uF900-\uFA6D\uFA70-\uFAD9\uFB00-\uFB06\uFB13-\uFB17\uFB1D\uFB1F-\uFB28\uFB2A-\uFB36\uFB38-\uFB3C\uFB3E\uFB40\uFB41\uFB43\uFB44\uFB46-\uFBB1\uFBD3-\uFD3D\uFD50-\uFD8F\uFD92-\uFDC7\uFDF0-\uFDFB\uFE70-\uFE74\uFE76-\uFEFC\uFF21-\uFF3A\uFF41-\uFF5A\uFF66-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC]/,
  // http://stackoverflow.com/a/22075070
  '*': /./
};
/** */

var PatternInputDefinition =
/*#__PURE__*/
function () {
  /** */

  /** */

  /** */

  /** */

  /** */

  /** */
  function PatternInputDefinition(opts) {
    _classCallCheck(this, PatternInputDefinition);

    var mask = opts.mask,
        blockOpts = _objectWithoutProperties(opts, ["mask"]);

    this.masked = createMask({
      mask: mask
    });
    Object.assign(this, blockOpts);
  }

  _createClass(PatternInputDefinition, [{
    key: "reset",
    value: function reset() {
      this._isFilled = false;
      this.masked.reset();
    }
  }, {
    key: "remove",
    value: function remove() {
      var fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      var toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;

      if (fromPos === 0 && toPos >= 1) {
        this._isFilled = false;
        return this.masked.remove(fromPos, toPos);
      }

      return new ChangeDetails();
    }
  }, {
    key: "_appendChar",
    value: function _appendChar(str) {
      var flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      if (this._isFilled) return new ChangeDetails();
      var state = this.masked.state; // simulate input

      var details = this.masked._appendChar(str, flags);

      if (details.inserted && this.doValidate(flags) === false) {
        details.inserted = details.rawInserted = '';
        this.masked.state = state;
      }

      if (!details.inserted && !this.isOptional && !this.lazy && !flags.input) {
        details.inserted = this.placeholderChar;
      }

      details.skip = !details.inserted && !this.isOptional;
      this._isFilled = Boolean(details.inserted);
      return details;
    }
  }, {
    key: "append",
    value: function append() {
      var _this$masked;

      return (_this$masked = this.masked).append.apply(_this$masked, arguments);
    }
  }, {
    key: "_appendPlaceholder",
    value: function _appendPlaceholder() {
      var details = new ChangeDetails();
      if (this._isFilled || this.isOptional) return details;
      this._isFilled = true;
      details.inserted = this.placeholderChar;
      return details;
    }
  }, {
    key: "extractTail",
    value: function extractTail() {
      var _this$masked2;

      return (_this$masked2 = this.masked).extractTail.apply(_this$masked2, arguments);
    }
  }, {
    key: "appendTail",
    value: function appendTail() {
      var _this$masked3;

      return (_this$masked3 = this.masked).appendTail.apply(_this$masked3, arguments);
    }
  }, {
    key: "extractInput",
    value: function extractInput() {
      var fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      var toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
      var flags = arguments.length > 2 ? arguments[2] : undefined;
      return this.masked.extractInput(fromPos, toPos, flags);
    }
  }, {
    key: "nearestInputPos",
    value: function nearestInputPos(cursorPos) {
      var direction = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DIRECTION.NONE;
      var minPos = 0;
      var maxPos = this.value.length;
      var boundPos = Math.min(Math.max(cursorPos, minPos), maxPos);

      switch (direction) {
        case DIRECTION.LEFT:
        case DIRECTION.FORCE_LEFT:
          return this.isComplete ? boundPos : minPos;

        case DIRECTION.RIGHT:
        case DIRECTION.FORCE_RIGHT:
          return this.isComplete ? boundPos : maxPos;

        case DIRECTION.NONE:
        default:
          return boundPos;
      }
    }
  }, {
    key: "doValidate",
    value: function doValidate() {
      var _this$masked4, _this$parent;

      return (_this$masked4 = this.masked).doValidate.apply(_this$masked4, arguments) && (!this.parent || (_this$parent = this.parent).doValidate.apply(_this$parent, arguments));
    }
  }, {
    key: "doCommit",
    value: function doCommit() {
      this.masked.doCommit();
    }
  }, {
    key: "value",
    get: function get() {
      return this.masked.value || (this._isFilled && !this.isOptional ? this.placeholderChar : '');
    }
  }, {
    key: "unmaskedValue",
    get: function get() {
      return this.masked.unmaskedValue;
    }
  }, {
    key: "isComplete",
    get: function get() {
      return Boolean(this.masked.value) || this.isOptional;
    }
  }, {
    key: "state",
    get: function get() {
      return {
        masked: this.masked.state,
        _isFilled: this._isFilled
      };
    },
    set: function set(state) {
      this.masked.state = state.masked;
      this._isFilled = state._isFilled;
    }
  }]);

  return PatternInputDefinition;
}();

var PatternFixedDefinition =
/*#__PURE__*/
function () {
  /** */

  /** */

  /** */

  /** */
  function PatternFixedDefinition(opts) {
    _classCallCheck(this, PatternFixedDefinition);

    Object.assign(this, opts);
    this._value = '';
  }

  _createClass(PatternFixedDefinition, [{
    key: "reset",
    value: function reset() {
      this._isRawInput = false;
      this._value = '';
    }
  }, {
    key: "remove",
    value: function remove() {
      var fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      var toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this._value.length;
      this._value = this._value.slice(0, fromPos) + this._value.slice(toPos);
      if (!this._value) this._isRawInput = false;
      return new ChangeDetails();
    }
  }, {
    key: "nearestInputPos",
    value: function nearestInputPos(cursorPos) {
      var direction = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DIRECTION.NONE;
      var minPos = 0;
      var maxPos = this._value.length;

      switch (direction) {
        case DIRECTION.LEFT:
        case DIRECTION.FORCE_LEFT:
          return minPos;

        case DIRECTION.NONE:
        case DIRECTION.RIGHT:
        case DIRECTION.FORCE_RIGHT:
        default:
          return maxPos;
      }
    }
  }, {
    key: "extractInput",
    value: function extractInput() {
      var fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      var toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this._value.length;
      var flags = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
      return flags.raw && this._isRawInput && this._value.slice(fromPos, toPos) || '';
    }
  }, {
    key: "_appendChar",
    value: function _appendChar(str) {
      var flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var details = new ChangeDetails();
      if (this._value) return details;
      var appended = this.char === str[0];
      var isResolved = appended && (this.isUnmasking || flags.input || flags.raw) && !flags.tail;
      if (isResolved) details.rawInserted = this.char;
      this._value = details.inserted = this.char;
      this._isRawInput = isResolved && (flags.raw || flags.input);
      return details;
    }
  }, {
    key: "_appendPlaceholder",
    value: function _appendPlaceholder() {
      var details = new ChangeDetails();
      if (this._value) return details;
      this._value = details.inserted = this.char;
      return details;
    }
  }, {
    key: "extractTail",
    value: function extractTail() {
      return new ContinuousTailDetails('');
    } // $FlowFixMe no ideas

  }, {
    key: "appendTail",
    value: function appendTail(tail) {
      if (isString(tail)) tail = new ContinuousTailDetails(String(tail));
      return tail.appendTo(this);
    }
  }, {
    key: "append",
    value: function append(str, flags, tail) {
      var details = this._appendChar(str, flags);

      if (tail != null) {
        details.tailShift += this.appendTail(tail).tailShift;
      }

      return details;
    }
  }, {
    key: "doCommit",
    value: function doCommit() {}
  }, {
    key: "value",
    get: function get() {
      return this._value;
    }
  }, {
    key: "unmaskedValue",
    get: function get() {
      return this.isUnmasking ? this.value : '';
    }
  }, {
    key: "isComplete",
    get: function get() {
      return true;
    }
  }, {
    key: "state",
    get: function get() {
      return {
        _value: this._value,
        _isRawInput: this._isRawInput
      };
    },
    set: function set(state) {
      Object.assign(this, state);
    }
  }]);

  return PatternFixedDefinition;
}();

var ChunksTailDetails =
/*#__PURE__*/
function () {
  /** */
  function ChunksTailDetails() {
    var chunks = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    var from = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;

    _classCallCheck(this, ChunksTailDetails);

    this.chunks = chunks;
    this.from = from;
  }

  _createClass(ChunksTailDetails, [{
    key: "toString",
    value: function toString() {
      return this.chunks.map(String).join('');
    } // $FlowFixMe no ideas

  }, {
    key: "extend",
    value: function extend(tailChunk) {
      if (!String(tailChunk)) return;
      if (isString(tailChunk)) tailChunk = new ContinuousTailDetails(String(tailChunk));
      var lastChunk = this.chunks[this.chunks.length - 1];
      var extendLast = lastChunk && ( // if stops are same or tail has no stop
      lastChunk.stop === tailChunk.stop || tailChunk.stop == null) && // if tail chunk goes just after last chunk
      tailChunk.from === lastChunk.from + lastChunk.toString().length;

      if (tailChunk instanceof ContinuousTailDetails) {
        // check the ability to extend previous chunk
        if (extendLast) {
          // extend previous chunk
          lastChunk.extend(tailChunk.toString());
        } else {
          // append new chunk
          this.chunks.push(tailChunk);
        }
      } else if (tailChunk instanceof ChunksTailDetails) {
        if (tailChunk.stop == null) {
          // unwrap floating chunks to parent, keeping `from` pos
          var firstTailChunk;

          while (tailChunk.chunks.length && tailChunk.chunks[0].stop == null) {
            firstTailChunk = tailChunk.chunks.shift();
            firstTailChunk.from += tailChunk.from;
            this.extend(firstTailChunk);
          }
        } // if tail chunk still has value


        if (tailChunk.toString()) {
          // if chunks contains stops, then popup stop to container
          tailChunk.stop = tailChunk.blockIndex;
          this.chunks.push(tailChunk);
        }
      }
    }
  }, {
    key: "appendTo",
    value: function appendTo(masked) {
      // $FlowFixMe
      if (!(masked instanceof IMask.MaskedPattern)) {
        var tail = new ContinuousTailDetails(this.toString());
        return tail.appendTo(masked);
      }

      var details = new ChangeDetails();

      for (var ci = 0; ci < this.chunks.length && !details.skip; ++ci) {
        var chunk = this.chunks[ci];

        var lastBlockIter = masked._mapPosToBlock(masked.value.length);

        var stop = chunk.stop;
        var chunkBlock = void 0;

        if (stop != null && ( // if block not found or stop is behind lastBlock
        !lastBlockIter || lastBlockIter.index <= stop)) {
          if (chunk instanceof ChunksTailDetails || // for continuous block also check if stop is exist
          masked._stops.indexOf(stop) >= 0) {
            details.aggregate(masked._appendPlaceholder(stop));
          }

          chunkBlock = chunk instanceof ChunksTailDetails && masked._blocks[stop];
        }

        if (chunkBlock) {
          var tailDetails = chunkBlock.appendTail(chunk);
          tailDetails.skip = false; // always ignore skip, it will be set on last

          details.aggregate(tailDetails);
          masked._value += tailDetails.inserted; // get not inserted chars

          var remainChars = chunk.toString().slice(tailDetails.rawInserted.length);
          if (remainChars) details.aggregate(masked.append(remainChars, {
            tail: true
          }));
        } else {
          details.aggregate(masked.append(chunk.toString(), {
            tail: true
          }));
        }
      }
      return details;
    }
  }, {
    key: "shiftBefore",
    value: function shiftBefore(pos) {
      if (this.from >= pos || !this.chunks.length) return '';
      var chunkShiftPos = pos - this.from;
      var ci = 0;

      while (ci < this.chunks.length) {
        var chunk = this.chunks[ci];
        var shiftChar = chunk.shiftBefore(chunkShiftPos);

        if (chunk.toString()) {
          // chunk still contains value
          // but not shifted - means no more available chars to shift
          if (!shiftChar) break;
          ++ci;
        } else {
          // clean if chunk has no value
          this.chunks.splice(ci, 1);
        }

        if (shiftChar) return shiftChar;
      }

      return '';
    }
  }, {
    key: "state",
    get: function get() {
      return {
        chunks: this.chunks.map(function (c) {
          return c.state;
        }),
        from: this.from,
        stop: this.stop,
        blockIndex: this.blockIndex
      };
    },
    set: function set(state) {
      var chunks = state.chunks,
          props = _objectWithoutProperties(state, ["chunks"]);

      Object.assign(this, props);
      this.chunks = chunks.map(function (cstate) {
        var chunk = "chunks" in cstate ? new ChunksTailDetails() : new ContinuousTailDetails(); // $FlowFixMe already checked above

        chunk.state = cstate;
        return chunk;
      });
    }
  }]);

  return ChunksTailDetails;
}();

/** Masking by RegExp */

var MaskedRegExp =
/*#__PURE__*/
function (_Masked) {
  _inherits(MaskedRegExp, _Masked);

  function MaskedRegExp() {
    _classCallCheck(this, MaskedRegExp);

    return _possibleConstructorReturn(this, _getPrototypeOf(MaskedRegExp).apply(this, arguments));
  }

  _createClass(MaskedRegExp, [{
    key: "_update",

    /**
      @override
      @param {Object} opts
    */
    value: function _update(opts) {
      if (opts.mask) opts.validate = function (value) {
        return value.search(opts.mask) >= 0;
      };

      _get(_getPrototypeOf(MaskedRegExp.prototype), "_update", this).call(this, opts);
    }
  }]);

  return MaskedRegExp;
}(Masked);
IMask.MaskedRegExp = MaskedRegExp;

/**
  Pattern mask
  @param {Object} opts
  @param {Object} opts.blocks
  @param {Object} opts.definitions
  @param {string} opts.placeholderChar
  @param {boolean} opts.lazy
*/
var MaskedPattern =
/*#__PURE__*/
function (_Masked) {
  _inherits(MaskedPattern, _Masked);

  /** */

  /** */

  /** Single char for empty input */

  /** Show placeholder only when needed */
  function MaskedPattern() {
    var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    _classCallCheck(this, MaskedPattern);

    // TODO type $Shape<MaskedPatternOptions>={} does not work
    opts.definitions = Object.assign({}, DEFAULT_INPUT_DEFINITIONS, opts.definitions);
    return _possibleConstructorReturn(this, _getPrototypeOf(MaskedPattern).call(this, Object.assign({}, MaskedPattern.DEFAULTS, {}, opts)));
  }
  /**
    @override
    @param {Object} opts
  */


  _createClass(MaskedPattern, [{
    key: "_update",
    value: function _update() {
      var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      opts.definitions = Object.assign({}, this.definitions, opts.definitions);

      _get(_getPrototypeOf(MaskedPattern.prototype), "_update", this).call(this, opts);

      this._rebuildMask();
    }
    /** */

  }, {
    key: "_rebuildMask",
    value: function _rebuildMask() {
      var _this = this;

      var defs = this.definitions;
      this._blocks = [];
      this._stops = [];
      this._maskedBlocks = {};
      var pattern = this.mask;
      if (!pattern || !defs) return;
      var unmaskingBlock = false;
      var optionalBlock = false;

      for (var i = 0; i < pattern.length; ++i) {
        if (this.blocks) {
          var _ret = function () {
            var p = pattern.slice(i);
            var bNames = Object.keys(_this.blocks).filter(function (bName) {
              return p.indexOf(bName) === 0;
            }); // order by key length

            bNames.sort(function (a, b) {
              return b.length - a.length;
            }); // use block name with max length

            var bName = bNames[0];

            if (bName) {
              var maskedBlock = createMask(Object.assign({
                parent: _this,
                lazy: _this.lazy,
                placeholderChar: _this.placeholderChar,
                overwrite: _this.overwrite
              }, _this.blocks[bName]));

              if (maskedBlock) {
                _this._blocks.push(maskedBlock); // store block index


                if (!_this._maskedBlocks[bName]) _this._maskedBlocks[bName] = [];

                _this._maskedBlocks[bName].push(_this._blocks.length - 1);
              }

              i += bName.length - 1;
              return "continue";
            }
          }();

          if (_ret === "continue") continue;
        }

        var char = pattern[i];

        var _isInput = char in defs;

        if (char === MaskedPattern.STOP_CHAR) {
          this._stops.push(this._blocks.length);

          continue;
        }

        if (char === '{' || char === '}') {
          unmaskingBlock = !unmaskingBlock;
          continue;
        }

        if (char === '[' || char === ']') {
          optionalBlock = !optionalBlock;
          continue;
        }

        if (char === MaskedPattern.ESCAPE_CHAR) {
          ++i;
          char = pattern[i];
          if (!char) break;
          _isInput = false;
        }

        var def = _isInput ? new PatternInputDefinition({
          parent: this,
          lazy: this.lazy,
          placeholderChar: this.placeholderChar,
          mask: defs[char],
          isOptional: optionalBlock
        }) : new PatternFixedDefinition({
          char: char,
          isUnmasking: unmaskingBlock
        });

        this._blocks.push(def);
      }
    }
    /**
      @override
    */

  }, {
    key: "reset",

    /**
      @override
    */
    value: function reset() {
      _get(_getPrototypeOf(MaskedPattern.prototype), "reset", this).call(this);

      this._blocks.forEach(function (b) {
        return b.reset();
      });
    }
    /**
      @override
    */

  }, {
    key: "doCommit",

    /**
      @override
    */
    value: function doCommit() {
      this._blocks.forEach(function (b) {
        return b.doCommit();
      });

      _get(_getPrototypeOf(MaskedPattern.prototype), "doCommit", this).call(this);
    }
    /**
      @override
    */

  }, {
    key: "appendTail",

    /**
      @override
    */
    value: function appendTail(tail) {
      return _get(_getPrototypeOf(MaskedPattern.prototype), "appendTail", this).call(this, tail).aggregate(this._appendPlaceholder());
    }
    /**
      @override
    */

  }, {
    key: "_appendCharRaw",
    value: function _appendCharRaw(ch) {
      var flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      ch = this.doPrepare(ch, flags);

      var blockIter = this._mapPosToBlock(this.value.length);

      var details = new ChangeDetails();
      if (!blockIter) return details;

      for (var bi = blockIter.index;; ++bi) {
        var _block = this._blocks[bi];
        if (!_block) break;

        var blockDetails = _block._appendChar(ch, flags);

        var skip = blockDetails.skip;
        details.aggregate(blockDetails);
        if (skip || blockDetails.rawInserted) break; // go next char
      }

      return details;
    }
    /**
      @override
    */

  }, {
    key: "extractTail",
    value: function extractTail() {
      var _this2 = this;

      var fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      var toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
      var chunkTail = new ChunksTailDetails();
      if (fromPos === toPos) return chunkTail;

      this._forEachBlocksInRange(fromPos, toPos, function (b, bi, bFromPos, bToPos) {
        var blockChunk = b.extractTail(bFromPos, bToPos);
        blockChunk.stop = _this2._findStopBefore(bi);
        blockChunk.from = _this2._blockStartPos(bi);
        if (blockChunk instanceof ChunksTailDetails) blockChunk.blockIndex = bi;
        chunkTail.extend(blockChunk);
      });

      return chunkTail;
    }
    /**
      @override
    */

  }, {
    key: "extractInput",
    value: function extractInput() {
      var fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      var toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
      var flags = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
      if (fromPos === toPos) return '';
      var input = '';

      this._forEachBlocksInRange(fromPos, toPos, function (b, _, fromPos, toPos) {
        input += b.extractInput(fromPos, toPos, flags);
      });

      return input;
    }
  }, {
    key: "_findStopBefore",
    value: function _findStopBefore(blockIndex) {
      var stopBefore;

      for (var si = 0; si < this._stops.length; ++si) {
        var stop = this._stops[si];
        if (stop <= blockIndex) stopBefore = stop;else break;
      }

      return stopBefore;
    }
    /** Appends placeholder depending on laziness */

  }, {
    key: "_appendPlaceholder",
    value: function _appendPlaceholder(toBlockIndex) {
      var _this3 = this;

      var details = new ChangeDetails();
      if (this.lazy && toBlockIndex == null) return details;

      var startBlockIter = this._mapPosToBlock(this.value.length);

      if (!startBlockIter) return details;
      var startBlockIndex = startBlockIter.index;
      var endBlockIndex = toBlockIndex != null ? toBlockIndex : this._blocks.length;

      this._blocks.slice(startBlockIndex, endBlockIndex).forEach(function (b) {
        if (!b.lazy || toBlockIndex != null) {
          // $FlowFixMe `_blocks` may not be present
          var args = b._blocks != null ? [b._blocks.length] : [];

          var bDetails = b._appendPlaceholder.apply(b, args);

          _this3._value += bDetails.inserted;
          details.aggregate(bDetails);
        }
      });

      return details;
    }
    /** Finds block in pos */

  }, {
    key: "_mapPosToBlock",
    value: function _mapPosToBlock(pos) {
      var accVal = '';

      for (var bi = 0; bi < this._blocks.length; ++bi) {
        var _block2 = this._blocks[bi];
        var blockStartPos = accVal.length;
        accVal += _block2.value;

        if (pos <= accVal.length) {
          return {
            index: bi,
            offset: pos - blockStartPos
          };
        }
      }
    }
    /** */

  }, {
    key: "_blockStartPos",
    value: function _blockStartPos(blockIndex) {
      return this._blocks.slice(0, blockIndex).reduce(function (pos, b) {
        return pos += b.value.length;
      }, 0);
    }
    /** */

  }, {
    key: "_forEachBlocksInRange",
    value: function _forEachBlocksInRange(fromPos) {
      var toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
      var fn = arguments.length > 2 ? arguments[2] : undefined;

      var fromBlockIter = this._mapPosToBlock(fromPos);

      if (fromBlockIter) {
        var toBlockIter = this._mapPosToBlock(toPos); // process first block


        var isSameBlock = toBlockIter && fromBlockIter.index === toBlockIter.index;
        var fromBlockStartPos = fromBlockIter.offset;
        var fromBlockEndPos = toBlockIter && isSameBlock ? toBlockIter.offset : this._blocks[fromBlockIter.index].value.length;
        fn(this._blocks[fromBlockIter.index], fromBlockIter.index, fromBlockStartPos, fromBlockEndPos);

        if (toBlockIter && !isSameBlock) {
          // process intermediate blocks
          for (var bi = fromBlockIter.index + 1; bi < toBlockIter.index; ++bi) {
            fn(this._blocks[bi], bi, 0, this._blocks[bi].value.length);
          } // process last block


          fn(this._blocks[toBlockIter.index], toBlockIter.index, 0, toBlockIter.offset);
        }
      }
    }
    /**
      @override
    */

  }, {
    key: "remove",
    value: function remove() {
      var fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      var toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;

      var removeDetails = _get(_getPrototypeOf(MaskedPattern.prototype), "remove", this).call(this, fromPos, toPos);

      this._forEachBlocksInRange(fromPos, toPos, function (b, _, bFromPos, bToPos) {
        removeDetails.aggregate(b.remove(bFromPos, bToPos));
      });

      return removeDetails;
    }
    /**
      @override
    */

  }, {
    key: "nearestInputPos",
    value: function nearestInputPos(cursorPos) {
      var direction = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DIRECTION.NONE;
      // TODO refactor - extract alignblock
      var beginBlockData = this._mapPosToBlock(cursorPos) || {
        index: 0,
        offset: 0
      };
      var beginBlockOffset = beginBlockData.offset,
          beginBlockIndex = beginBlockData.index;
      var beginBlock = this._blocks[beginBlockIndex];
      if (!beginBlock) return cursorPos;
      var beginBlockCursorPos = beginBlockOffset; // if position inside block - try to adjust it

      if (beginBlockCursorPos !== 0 && beginBlockCursorPos < beginBlock.value.length) {
        beginBlockCursorPos = beginBlock.nearestInputPos(beginBlockOffset, forceDirection(direction));
      }

      var cursorAtRight = beginBlockCursorPos === beginBlock.value.length;
      var cursorAtLeft = beginBlockCursorPos === 0; //  cursor is INSIDE first block (not at bounds)

      if (!cursorAtLeft && !cursorAtRight) return this._blockStartPos(beginBlockIndex) + beginBlockCursorPos;
      var searchBlockIndex = cursorAtRight ? beginBlockIndex + 1 : beginBlockIndex;

      if (direction === DIRECTION.NONE) {
        // NONE direction used to calculate start input position if no chars were removed
        // FOR NONE:
        // -
        // input|any
        // ->
        //  any|input
        // <-
        //  filled-input|any
        // check if first block at left is input
        if (searchBlockIndex > 0) {
          var blockIndexAtLeft = searchBlockIndex - 1;
          var blockAtLeft = this._blocks[blockIndexAtLeft];
          var blockInputPos = blockAtLeft.nearestInputPos(0, DIRECTION.NONE); // is input

          if (!blockAtLeft.value.length || blockInputPos !== blockAtLeft.value.length) {
            return this._blockStartPos(searchBlockIndex);
          }
        } // ->


        var firstInputAtRight = searchBlockIndex;

        for (var bi = firstInputAtRight; bi < this._blocks.length; ++bi) {
          var blockAtRight = this._blocks[bi];

          var _blockInputPos = blockAtRight.nearestInputPos(0, DIRECTION.NONE);

          if (!blockAtRight.value.length || _blockInputPos !== blockAtRight.value.length) {
            return this._blockStartPos(bi) + _blockInputPos;
          }
        } // <-
        // find first non-fixed symbol


        for (var _bi = searchBlockIndex - 1; _bi >= 0; --_bi) {
          var _block3 = this._blocks[_bi];

          var _blockInputPos2 = _block3.nearestInputPos(0, DIRECTION.NONE); // is input


          if (!_block3.value.length || _blockInputPos2 !== _block3.value.length) {
            return this._blockStartPos(_bi) + _block3.value.length;
          }
        }

        return cursorPos;
      }

      if (direction === DIRECTION.LEFT || direction === DIRECTION.FORCE_LEFT) {
        // -
        //  any|filled-input
        // <-
        //  any|first not empty is not-len-aligned
        //  not-0-aligned|any
        // ->
        //  any|not-len-aligned or end
        // check if first block at right is filled input
        var firstFilledBlockIndexAtRight;

        for (var _bi2 = searchBlockIndex; _bi2 < this._blocks.length; ++_bi2) {
          if (this._blocks[_bi2].value) {
            firstFilledBlockIndexAtRight = _bi2;
            break;
          }
        }

        if (firstFilledBlockIndexAtRight != null) {
          var filledBlock = this._blocks[firstFilledBlockIndexAtRight];

          var _blockInputPos3 = filledBlock.nearestInputPos(0, DIRECTION.RIGHT);

          if (_blockInputPos3 === 0 && filledBlock.unmaskedValue.length) {
            // filled block is input
            return this._blockStartPos(firstFilledBlockIndexAtRight) + _blockInputPos3;
          }
        } // <-
        // find this vars


        var firstFilledInputBlockIndex = -1;
        var firstEmptyInputBlockIndex; // TODO consider nested empty inputs

        for (var _bi3 = searchBlockIndex - 1; _bi3 >= 0; --_bi3) {
          var _block4 = this._blocks[_bi3];

          var _blockInputPos4 = _block4.nearestInputPos(_block4.value.length, DIRECTION.FORCE_LEFT);

          if (!_block4.value || _blockInputPos4 !== 0) firstEmptyInputBlockIndex = _bi3;

          if (_blockInputPos4 !== 0) {
            if (_blockInputPos4 !== _block4.value.length) {
              // aligned inside block - return immediately
              return this._blockStartPos(_bi3) + _blockInputPos4;
            } else {
              // found filled
              firstFilledInputBlockIndex = _bi3;
              break;
            }
          }
        }

        if (direction === DIRECTION.LEFT) {
          // try find first empty input before start searching position only when not forced
          for (var _bi4 = firstFilledInputBlockIndex + 1; _bi4 <= Math.min(searchBlockIndex, this._blocks.length - 1); ++_bi4) {
            var _block5 = this._blocks[_bi4];

            var _blockInputPos5 = _block5.nearestInputPos(0, DIRECTION.NONE);

            var blockAlignedPos = this._blockStartPos(_bi4) + _blockInputPos5;

            if (blockAlignedPos > cursorPos) break; // if block is not lazy input

            if (_blockInputPos5 !== _block5.value.length) return blockAlignedPos;
          }
        } // process overflow


        if (firstFilledInputBlockIndex >= 0) {
          return this._blockStartPos(firstFilledInputBlockIndex) + this._blocks[firstFilledInputBlockIndex].value.length;
        } // for lazy if has aligned left inside fixed and has came to the start - use start position


        if (direction === DIRECTION.FORCE_LEFT || this.lazy && !this.extractInput() && !isInput(this._blocks[searchBlockIndex])) {
          return 0;
        }

        if (firstEmptyInputBlockIndex != null) {
          return this._blockStartPos(firstEmptyInputBlockIndex);
        } // find first input


        for (var _bi5 = searchBlockIndex; _bi5 < this._blocks.length; ++_bi5) {
          var _block6 = this._blocks[_bi5];

          var _blockInputPos6 = _block6.nearestInputPos(0, DIRECTION.NONE); // is input


          if (!_block6.value.length || _blockInputPos6 !== _block6.value.length) {
            return this._blockStartPos(_bi5) + _blockInputPos6;
          }
        }

        return 0;
      }

      if (direction === DIRECTION.RIGHT || direction === DIRECTION.FORCE_RIGHT) {
        // ->
        //  any|not-len-aligned and filled
        //  any|not-len-aligned
        // <-
        //  not-0-aligned or start|any
        var firstInputBlockAlignedIndex;
        var firstInputBlockAlignedPos;

        for (var _bi6 = searchBlockIndex; _bi6 < this._blocks.length; ++_bi6) {
          var _block7 = this._blocks[_bi6];

          var _blockInputPos7 = _block7.nearestInputPos(0, DIRECTION.NONE);

          if (_blockInputPos7 !== _block7.value.length) {
            firstInputBlockAlignedPos = this._blockStartPos(_bi6) + _blockInputPos7;
            firstInputBlockAlignedIndex = _bi6;
            break;
          }
        }

        if (firstInputBlockAlignedIndex != null && firstInputBlockAlignedPos != null) {
          for (var _bi7 = firstInputBlockAlignedIndex; _bi7 < this._blocks.length; ++_bi7) {
            var _block8 = this._blocks[_bi7];

            var _blockInputPos8 = _block8.nearestInputPos(0, DIRECTION.FORCE_RIGHT);

            if (_blockInputPos8 !== _block8.value.length) {
              return this._blockStartPos(_bi7) + _blockInputPos8;
            }
          }

          return direction === DIRECTION.FORCE_RIGHT ? this.value.length : firstInputBlockAlignedPos;
        }

        for (var _bi8 = Math.min(searchBlockIndex, this._blocks.length - 1); _bi8 >= 0; --_bi8) {
          var _block9 = this._blocks[_bi8];

          var _blockInputPos9 = _block9.nearestInputPos(_block9.value.length, DIRECTION.LEFT);

          if (_blockInputPos9 !== 0) {
            var alignedPos = this._blockStartPos(_bi8) + _blockInputPos9;

            if (alignedPos >= cursorPos) return alignedPos;
            break;
          }
        }
      }

      return cursorPos;
    }
    /** Get block by name */

  }, {
    key: "maskedBlock",
    value: function maskedBlock(name) {
      return this.maskedBlocks(name)[0];
    }
    /** Get all blocks by name */

  }, {
    key: "maskedBlocks",
    value: function maskedBlocks(name) {
      var _this4 = this;

      var indices = this._maskedBlocks[name];
      if (!indices) return [];
      return indices.map(function (gi) {
        return _this4._blocks[gi];
      });
    }
  }, {
    key: "state",
    get: function get() {
      return Object.assign({}, _get(_getPrototypeOf(MaskedPattern.prototype), "state", this), {
        _blocks: this._blocks.map(function (b) {
          return b.state;
        })
      });
    },
    set: function set(state) {
      var _blocks = state._blocks,
          maskedState = _objectWithoutProperties(state, ["_blocks"]);

      this._blocks.forEach(function (b, bi) {
        return b.state = _blocks[bi];
      });

      _set(_getPrototypeOf(MaskedPattern.prototype), "state", maskedState, this, true);
    }
  }, {
    key: "isComplete",
    get: function get() {
      return this._blocks.every(function (b) {
        return b.isComplete;
      });
    }
  }, {
    key: "unmaskedValue",
    get: function get() {
      return this._blocks.reduce(function (str, b) {
        return str += b.unmaskedValue;
      }, '');
    },
    set: function set(unmaskedValue) {
      _set(_getPrototypeOf(MaskedPattern.prototype), "unmaskedValue", unmaskedValue, this, true);
    }
    /**
      @override
    */

  }, {
    key: "value",
    get: function get() {
      // TODO return _value when not in change?
      return this._blocks.reduce(function (str, b) {
        return str += b.value;
      }, '');
    },
    set: function set(value) {
      _set(_getPrototypeOf(MaskedPattern.prototype), "value", value, this, true);
    }
  }]);

  return MaskedPattern;
}(Masked);
MaskedPattern.DEFAULTS = {
  lazy: true,
  placeholderChar: '_'
};
MaskedPattern.STOP_CHAR = '`';
MaskedPattern.ESCAPE_CHAR = '\\';
MaskedPattern.InputDefinition = PatternInputDefinition;
MaskedPattern.FixedDefinition = PatternFixedDefinition;

function isInput(block) {
  if (!block) return false;
  var value = block.value;
  return !value || block.nearestInputPos(0, DIRECTION.NONE) !== value.length;
}

IMask.MaskedPattern = MaskedPattern;

/** Pattern which accepts ranges */

var MaskedRange =
/*#__PURE__*/
function (_MaskedPattern) {
  _inherits(MaskedRange, _MaskedPattern);

  function MaskedRange() {
    _classCallCheck(this, MaskedRange);

    return _possibleConstructorReturn(this, _getPrototypeOf(MaskedRange).apply(this, arguments));
  }

  _createClass(MaskedRange, [{
    key: "_update",

    /**
      @override
    */
    value: function _update(opts) {
      // TODO type
      opts = Object.assign({
        to: this.to || 0,
        from: this.from || 0
      }, opts);
      var maxLength = String(opts.to).length;
      if (opts.maxLength != null) maxLength = Math.max(maxLength, opts.maxLength);
      opts.maxLength = maxLength;
      var fromStr = String(opts.from).padStart(maxLength, '0');
      var toStr = String(opts.to).padStart(maxLength, '0');
      var sameCharsCount = 0;

      while (sameCharsCount < toStr.length && toStr[sameCharsCount] === fromStr[sameCharsCount]) {
        ++sameCharsCount;
      }

      opts.mask = toStr.slice(0, sameCharsCount).replace(/0/g, '\\0') + '0'.repeat(maxLength - sameCharsCount);

      _get(_getPrototypeOf(MaskedRange.prototype), "_update", this).call(this, opts);
    }
    /**
      @override
    */

  }, {
    key: "boundaries",
    value: function boundaries(str) {
      var minstr = '';
      var maxstr = '';

      var _ref = str.match(/^(\D*)(\d*)(\D*)/) || [],
          _ref2 = _slicedToArray(_ref, 3),
          placeholder = _ref2[1],
          num = _ref2[2];

      if (num) {
        minstr = '0'.repeat(placeholder.length) + num;
        maxstr = '9'.repeat(placeholder.length) + num;
      }

      minstr = minstr.padEnd(this.maxLength, '0');
      maxstr = maxstr.padEnd(this.maxLength, '9');
      return [minstr, maxstr];
    }
    /**
      @override
    */

  }, {
    key: "doPrepare",
    value: function doPrepare(str) {
      var flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      str = _get(_getPrototypeOf(MaskedRange.prototype), "doPrepare", this).call(this, str, flags).replace(/\D/g, '');
      if (!this.autofix) return str;
      var fromStr = String(this.from).padStart(this.maxLength, '0');
      var toStr = String(this.to).padStart(this.maxLength, '0');
      var val = this.value;
      var prepStr = '';

      for (var ci = 0; ci < str.length; ++ci) {
        var nextVal = val + prepStr + str[ci];

        var _this$boundaries = this.boundaries(nextVal),
            _this$boundaries2 = _slicedToArray(_this$boundaries, 2),
            minstr = _this$boundaries2[0],
            maxstr = _this$boundaries2[1];

        if (Number(maxstr) < this.from) prepStr += fromStr[nextVal.length - 1];else if (Number(minstr) > this.to) prepStr += toStr[nextVal.length - 1];else prepStr += str[ci];
      }

      return prepStr;
    }
    /**
      @override
    */

  }, {
    key: "doValidate",
    value: function doValidate() {
      var _get2;

      var str = this.value;
      var firstNonZero = str.search(/[^0]/);
      if (firstNonZero === -1 && str.length <= this._matchFrom) return true;

      var _this$boundaries3 = this.boundaries(str),
          _this$boundaries4 = _slicedToArray(_this$boundaries3, 2),
          minstr = _this$boundaries4[0],
          maxstr = _this$boundaries4[1];

      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      return this.from <= Number(maxstr) && Number(minstr) <= this.to && (_get2 = _get(_getPrototypeOf(MaskedRange.prototype), "doValidate", this)).call.apply(_get2, [this].concat(args));
    }
  }, {
    key: "_matchFrom",

    /**
      Optionally sets max length of pattern.
      Used when pattern length is longer then `to` param length. Pads zeros at start in this case.
    */

    /** Min bound */

    /** Max bound */

    /** */
    get: function get() {
      return this.maxLength - String(this.from).length;
    }
  }, {
    key: "isComplete",
    get: function get() {
      return _get(_getPrototypeOf(MaskedRange.prototype), "isComplete", this) && Boolean(this.value);
    }
  }]);

  return MaskedRange;
}(MaskedPattern);
IMask.MaskedRange = MaskedRange;

/** Date mask */

var MaskedDate =
/*#__PURE__*/
function (_MaskedPattern) {
  _inherits(MaskedDate, _MaskedPattern);

  /** Pattern mask for date according to {@link MaskedDate#format} */

  /** Start date */

  /** End date */

  /** */

  /**
    @param {Object} opts
  */
  function MaskedDate(opts) {
    _classCallCheck(this, MaskedDate);

    return _possibleConstructorReturn(this, _getPrototypeOf(MaskedDate).call(this, Object.assign({}, MaskedDate.DEFAULTS, {}, opts)));
  }
  /**
    @override
  */


  _createClass(MaskedDate, [{
    key: "_update",
    value: function _update(opts) {
      if (opts.mask === Date) delete opts.mask;
      if (opts.pattern) opts.mask = opts.pattern;
      var blocks = opts.blocks;
      opts.blocks = Object.assign({}, MaskedDate.GET_DEFAULT_BLOCKS()); // adjust year block

      if (opts.min) opts.blocks.Y.from = opts.min.getFullYear();
      if (opts.max) opts.blocks.Y.to = opts.max.getFullYear();

      if (opts.min && opts.max && opts.blocks.Y.from === opts.blocks.Y.to) {
        opts.blocks.m.from = opts.min.getMonth() + 1;
        opts.blocks.m.to = opts.max.getMonth() + 1;

        if (opts.blocks.m.from === opts.blocks.m.to) {
          opts.blocks.d.from = opts.min.getDate();
          opts.blocks.d.to = opts.max.getDate();
        }
      }

      Object.assign(opts.blocks, blocks); // add autofix

      Object.keys(opts.blocks).forEach(function (bk) {
        var b = opts.blocks[bk];
        if (!('autofix' in b)) b.autofix = opts.autofix;
      });

      _get(_getPrototypeOf(MaskedDate.prototype), "_update", this).call(this, opts);
    }
    /**
      @override
    */

  }, {
    key: "doValidate",
    value: function doValidate() {
      var _get2;

      var date = this.date;

      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      return (_get2 = _get(_getPrototypeOf(MaskedDate.prototype), "doValidate", this)).call.apply(_get2, [this].concat(args)) && (!this.isComplete || this.isDateExist(this.value) && date != null && (this.min == null || this.min <= date) && (this.max == null || date <= this.max));
    }
    /** Checks if date is exists */

  }, {
    key: "isDateExist",
    value: function isDateExist(str) {
      return this.format(this.parse(str, this), this).indexOf(str) >= 0;
    }
    /** Parsed Date */

  }, {
    key: "date",
    get: function get() {
      return this.typedValue;
    },
    set: function set(date) {
      this.typedValue = date;
    }
    /**
      @override
    */

  }, {
    key: "typedValue",
    get: function get() {
      return this.isComplete ? _get(_getPrototypeOf(MaskedDate.prototype), "typedValue", this) : null;
    },
    set: function set(value) {
      _set(_getPrototypeOf(MaskedDate.prototype), "typedValue", value, this, true);
    }
  }]);

  return MaskedDate;
}(MaskedPattern);
MaskedDate.DEFAULTS = {
  pattern: 'd{.}`m{.}`Y',
  format: function format(date) {
    var day = String(date.getDate()).padStart(2, '0');
    var month = String(date.getMonth() + 1).padStart(2, '0');
    var year = date.getFullYear();
    return [day, month, year].join('.');
  },
  parse: function parse(str) {
    var _str$split = str.split('.'),
        _str$split2 = _slicedToArray(_str$split, 3),
        day = _str$split2[0],
        month = _str$split2[1],
        year = _str$split2[2];

    return new Date(year, month - 1, day);
  }
};

MaskedDate.GET_DEFAULT_BLOCKS = function () {
  return {
    d: {
      mask: MaskedRange,
      from: 1,
      to: 31,
      maxLength: 2
    },
    m: {
      mask: MaskedRange,
      from: 1,
      to: 12,
      maxLength: 2
    },
    Y: {
      mask: MaskedRange,
      from: 1900,
      to: 9999
    }
  };
};

IMask.MaskedDate = MaskedDate;

/**
  Generic element API to use with mask
  @interface
*/
var MaskElement =
/*#__PURE__*/
function () {
  function MaskElement() {
    _classCallCheck(this, MaskElement);
  }

  _createClass(MaskElement, [{
    key: "select",

    /** Safely sets element selection */
    value: function select(start, end) {
      if (start == null || end == null || start === this.selectionStart && end === this.selectionEnd) return;

      try {
        this._unsafeSelect(start, end);
      } catch (e) {}
    }
    /** Should be overriden in subclasses */

  }, {
    key: "_unsafeSelect",
    value: function _unsafeSelect(start, end) {}
    /** Should be overriden in subclasses */

  }, {
    key: "bindEvents",

    /** Should be overriden in subclasses */
    value: function bindEvents(handlers) {}
    /** Should be overriden in subclasses */

  }, {
    key: "unbindEvents",
    value: function unbindEvents() {}
  }, {
    key: "selectionStart",

    /** */

    /** */

    /** */

    /** Safely returns selection start */
    get: function get() {
      var start;

      try {
        start = this._unsafeSelectionStart;
      } catch (e) {}

      return start != null ? start : this.value.length;
    }
    /** Safely returns selection end */

  }, {
    key: "selectionEnd",
    get: function get() {
      var end;

      try {
        end = this._unsafeSelectionEnd;
      } catch (e) {}

      return end != null ? end : this.value.length;
    }
  }, {
    key: "isActive",
    get: function get() {
      return false;
    }
  }]);

  return MaskElement;
}();
IMask.MaskElement = MaskElement;

/** Bridge between HTMLElement and {@link Masked} */

var HTMLMaskElement =
/*#__PURE__*/
function (_MaskElement) {
  _inherits(HTMLMaskElement, _MaskElement);

  /** Mapping between HTMLElement events and mask internal events */

  /** HTMLElement to use mask on */

  /**
    @param {HTMLInputElement|HTMLTextAreaElement} input
  */
  function HTMLMaskElement(input) {
    var _this;

    _classCallCheck(this, HTMLMaskElement);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(HTMLMaskElement).call(this));
    _this.input = input;
    _this._handlers = {};
    return _this;
  }
  /** */
  // $FlowFixMe https://github.com/facebook/flow/issues/2839


  _createClass(HTMLMaskElement, [{
    key: "_unsafeSelect",

    /**
      Sets HTMLElement selection
      @override
    */
    value: function _unsafeSelect(start, end) {
      this.input.setSelectionRange(start, end);
    }
    /**
      HTMLElement value
      @override
    */

  }, {
    key: "bindEvents",

    /**
      Binds HTMLElement events to mask internal events
      @override
    */
    value: function bindEvents(handlers) {
      var _this2 = this;

      Object.keys(handlers).forEach(function (event) {
        return _this2._toggleEventHandler(HTMLMaskElement.EVENTS_MAP[event], handlers[event]);
      });
    }
    /**
      Unbinds HTMLElement events to mask internal events
      @override
    */

  }, {
    key: "unbindEvents",
    value: function unbindEvents() {
      var _this3 = this;

      Object.keys(this._handlers).forEach(function (event) {
        return _this3._toggleEventHandler(event);
      });
    }
    /** */

  }, {
    key: "_toggleEventHandler",
    value: function _toggleEventHandler(event, handler) {
      if (this._handlers[event]) {
        this.input.removeEventListener(event, this._handlers[event]);
        delete this._handlers[event];
      }

      if (handler) {
        this.input.addEventListener(event, handler);
        this._handlers[event] = handler;
      }
    }
  }, {
    key: "rootElement",
    get: function get() {
      return this.input.getRootNode ? this.input.getRootNode() : document;
    }
    /**
      Is element in focus
      @readonly
    */

  }, {
    key: "isActive",
    get: function get() {
      //$FlowFixMe
      return this.input === this.rootElement.activeElement;
    }
    /**
      Returns HTMLElement selection start
      @override
    */

  }, {
    key: "_unsafeSelectionStart",
    get: function get() {
      return this.input.selectionStart;
    }
    /**
      Returns HTMLElement selection end
      @override
    */

  }, {
    key: "_unsafeSelectionEnd",
    get: function get() {
      return this.input.selectionEnd;
    }
  }, {
    key: "value",
    get: function get() {
      return this.input.value;
    },
    set: function set(value) {
      this.input.value = value;
    }
  }]);

  return HTMLMaskElement;
}(MaskElement);
HTMLMaskElement.EVENTS_MAP = {
  selectionChange: 'keydown',
  input: 'input',
  drop: 'drop',
  click: 'click',
  focus: 'focus',
  commit: 'blur'
};
IMask.HTMLMaskElement = HTMLMaskElement;

var HTMLContenteditableMaskElement =
/*#__PURE__*/
function (_HTMLMaskElement) {
  _inherits(HTMLContenteditableMaskElement, _HTMLMaskElement);

  function HTMLContenteditableMaskElement() {
    _classCallCheck(this, HTMLContenteditableMaskElement);

    return _possibleConstructorReturn(this, _getPrototypeOf(HTMLContenteditableMaskElement).apply(this, arguments));
  }

  _createClass(HTMLContenteditableMaskElement, [{
    key: "_unsafeSelect",

    /**
      Sets HTMLElement selection
      @override
    */
    value: function _unsafeSelect(start, end) {
      if (!this.rootElement.createRange) return;
      var range = this.rootElement.createRange();
      range.setStart(this.input.firstChild || this.input, start);
      range.setEnd(this.input.lastChild || this.input, end);
      var root = this.rootElement;
      var selection = root.getSelection && root.getSelection();

      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
    /**
      HTMLElement value
      @override
    */

  }, {
    key: "_unsafeSelectionStart",

    /**
      Returns HTMLElement selection start
      @override
    */
    get: function get() {
      var root = this.rootElement;
      var selection = root.getSelection && root.getSelection();
      return selection && selection.anchorOffset;
    }
    /**
      Returns HTMLElement selection end
      @override
    */

  }, {
    key: "_unsafeSelectionEnd",
    get: function get() {
      var root = this.rootElement;
      var selection = root.getSelection && root.getSelection();
      return selection && this._unsafeSelectionStart + String(selection).length;
    }
  }, {
    key: "value",
    get: function get() {
      // $FlowFixMe
      return this.input.textContent;
    },
    set: function set(value) {
      this.input.textContent = value;
    }
  }]);

  return HTMLContenteditableMaskElement;
}(HTMLMaskElement);
IMask.HTMLContenteditableMaskElement = HTMLContenteditableMaskElement;

/** Listens to element events and controls changes between element and {@link Masked} */

var InputMask =
/*#__PURE__*/
function () {
  /**
    View element
    @readonly
  */

  /**
    Internal {@link Masked} model
    @readonly
  */

  /**
    @param {MaskElement|HTMLInputElement|HTMLTextAreaElement} el
    @param {Object} opts
  */
  function InputMask(el, opts) {
    _classCallCheck(this, InputMask);

    this.el = el instanceof MaskElement ? el : el.isContentEditable && el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA' ? new HTMLContenteditableMaskElement(el) : new HTMLMaskElement(el);
    this.masked = createMask(opts);
    this._listeners = {};
    this._value = '';
    this._unmaskedValue = '';
    this._saveSelection = this._saveSelection.bind(this);
    this._onInput = this._onInput.bind(this);
    this._onChange = this._onChange.bind(this);
    this._onDrop = this._onDrop.bind(this);
    this._onFocus = this._onFocus.bind(this);
    this._onClick = this._onClick.bind(this);
    this.alignCursor = this.alignCursor.bind(this);
    this.alignCursorFriendly = this.alignCursorFriendly.bind(this);

    this._bindEvents(); // refresh


    this.updateValue();

    this._onChange();
  }
  /** Read or update mask */


  _createClass(InputMask, [{
    key: "maskEquals",
    value: function maskEquals(mask) {
      return mask == null || mask === this.masked.mask || mask === Date && this.masked instanceof MaskedDate;
    }
  }, {
    key: "_bindEvents",

    /**
      Starts listening to element events
      @protected
    */
    value: function _bindEvents() {
      this.el.bindEvents({
        selectionChange: this._saveSelection,
        input: this._onInput,
        drop: this._onDrop,
        click: this._onClick,
        focus: this._onFocus,
        commit: this._onChange
      });
    }
    /**
      Stops listening to element events
      @protected
     */

  }, {
    key: "_unbindEvents",
    value: function _unbindEvents() {
      if (this.el) this.el.unbindEvents();
    }
    /**
      Fires custom event
      @protected
     */

  }, {
    key: "_fireEvent",
    value: function _fireEvent(ev) {
      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      var listeners = this._listeners[ev];
      if (!listeners) return;
      listeners.forEach(function (l) {
        return l.apply(void 0, args);
      });
    }
    /**
      Current selection start
      @readonly
    */

  }, {
    key: "_saveSelection",

    /**
      Stores current selection
      @protected
    */
    value: function _saveSelection()
    /* ev */
    {
      if (this.value !== this.el.value) {
        console.warn('Element value was changed outside of mask. Syncronize mask using `mask.updateValue()` to work properly.'); // eslint-disable-line no-console
      }

      this._selection = {
        start: this.selectionStart,
        end: this.cursorPos
      };
    }
    /** Syncronizes model value from view */

  }, {
    key: "updateValue",
    value: function updateValue() {
      this.masked.value = this.el.value;
      this._value = this.masked.value;
    }
    /** Syncronizes view from model value, fires change events */

  }, {
    key: "updateControl",
    value: function updateControl() {
      var newUnmaskedValue = this.masked.unmaskedValue;
      var newValue = this.masked.value;
      var isChanged = this.unmaskedValue !== newUnmaskedValue || this.value !== newValue;
      this._unmaskedValue = newUnmaskedValue;
      this._value = newValue;
      if (this.el.value !== newValue) this.el.value = newValue;
      if (isChanged) this._fireChangeEvents();
    }
    /** Updates options with deep equal check, recreates @{link Masked} model if mask type changes */

  }, {
    key: "updateOptions",
    value: function updateOptions(opts) {
      var mask = opts.mask,
          restOpts = _objectWithoutProperties(opts, ["mask"]);

      var updateMask = !this.maskEquals(mask);
      var updateOpts = !objectIncludes(this.masked, restOpts);
      if (updateMask) this.mask = mask;
      if (updateOpts) this.masked.updateOptions(restOpts);
      if (updateMask || updateOpts) this.updateControl();
    }
    /** Updates cursor */

  }, {
    key: "updateCursor",
    value: function updateCursor(cursorPos) {
      if (cursorPos == null) return;
      this.cursorPos = cursorPos; // also queue change cursor for mobile browsers

      this._delayUpdateCursor(cursorPos);
    }
    /**
      Delays cursor update to support mobile browsers
      @private
    */

  }, {
    key: "_delayUpdateCursor",
    value: function _delayUpdateCursor(cursorPos) {
      var _this = this;

      this._abortUpdateCursor();

      this._changingCursorPos = cursorPos;
      this._cursorChanging = setTimeout(function () {
        if (!_this.el) return; // if was destroyed

        _this.cursorPos = _this._changingCursorPos;

        _this._abortUpdateCursor();
      }, 10);
    }
    /**
      Fires custom events
      @protected
    */

  }, {
    key: "_fireChangeEvents",
    value: function _fireChangeEvents() {
      this._fireEvent('accept', this._inputEvent);

      if (this.masked.isComplete) this._fireEvent('complete', this._inputEvent);
    }
    /**
      Aborts delayed cursor update
      @private
    */

  }, {
    key: "_abortUpdateCursor",
    value: function _abortUpdateCursor() {
      if (this._cursorChanging) {
        clearTimeout(this._cursorChanging);
        delete this._cursorChanging;
      }
    }
    /** Aligns cursor to nearest available position */

  }, {
    key: "alignCursor",
    value: function alignCursor() {
      this.cursorPos = this.masked.nearestInputPos(this.cursorPos, DIRECTION.LEFT);
    }
    /** Aligns cursor only if selection is empty */

  }, {
    key: "alignCursorFriendly",
    value: function alignCursorFriendly() {
      if (this.selectionStart !== this.cursorPos) return; // skip if range is selected

      this.alignCursor();
    }
    /** Adds listener on custom event */

  }, {
    key: "on",
    value: function on(ev, handler) {
      if (!this._listeners[ev]) this._listeners[ev] = [];

      this._listeners[ev].push(handler);

      return this;
    }
    /** Removes custom event listener */

  }, {
    key: "off",
    value: function off(ev, handler) {
      if (!this._listeners[ev]) return this;

      if (!handler) {
        delete this._listeners[ev];
        return this;
      }

      var hIndex = this._listeners[ev].indexOf(handler);

      if (hIndex >= 0) this._listeners[ev].splice(hIndex, 1);
      return this;
    }
    /** Handles view input event */

  }, {
    key: "_onInput",
    value: function _onInput(e) {
      this._inputEvent = e;

      this._abortUpdateCursor(); // fix strange IE behavior


      if (!this._selection) return this.updateValue();
      var details = new ActionDetails( // new state
      this.el.value, this.cursorPos, // old state
      this.value, this._selection);
      var oldRawValue = this.masked.rawInputValue;
      var offset = this.masked.splice(details.startChangePos, details.removed.length, details.inserted, details.removeDirection).offset; // force align in remove direction only if no input chars were removed
      // otherwise we still need to align with NONE (to get out from fixed symbols for instance)

      var removeDirection = oldRawValue === this.masked.rawInputValue ? details.removeDirection : DIRECTION.NONE;
      var cursorPos = this.masked.nearestInputPos(details.startChangePos + offset, removeDirection);
      this.updateControl();
      this.updateCursor(cursorPos);
      delete this._inputEvent;
    }
    /** Handles view change event and commits model value */

  }, {
    key: "_onChange",
    value: function _onChange() {
      if (this.value !== this.el.value) {
        this.updateValue();
      }

      this.masked.doCommit();
      this.updateControl();

      this._saveSelection();
    }
    /** Handles view drop event, prevents by default */

  }, {
    key: "_onDrop",
    value: function _onDrop(ev) {
      ev.preventDefault();
      ev.stopPropagation();
    }
    /** Restore last selection on focus */

  }, {
    key: "_onFocus",
    value: function _onFocus(ev) {
      this.alignCursorFriendly();
    }
    /** Restore last selection on focus */

  }, {
    key: "_onClick",
    value: function _onClick(ev) {
      this.alignCursorFriendly();
    }
    /** Unbind view events and removes element reference */

  }, {
    key: "destroy",
    value: function destroy() {
      this._unbindEvents(); // $FlowFixMe why not do so?


      this._listeners.length = 0; // $FlowFixMe

      delete this.el;
    }
  }, {
    key: "mask",
    get: function get() {
      return this.masked.mask;
    },
    set: function set(mask) {
      if (this.maskEquals(mask)) return;

      if (!(mask instanceof IMask.Masked) && this.masked.constructor === maskedClass(mask)) {
        this.masked.updateOptions({
          mask: mask
        });
        return;
      }

      var masked = createMask({
        mask: mask
      });
      masked.unmaskedValue = this.masked.unmaskedValue;
      this.masked = masked;
    }
    /** Raw value */

  }, {
    key: "value",
    get: function get() {
      return this._value;
    },
    set: function set(str) {
      this.masked.value = str;
      this.updateControl();
      this.alignCursor();
    }
    /** Unmasked value */

  }, {
    key: "unmaskedValue",
    get: function get() {
      return this._unmaskedValue;
    },
    set: function set(str) {
      this.masked.unmaskedValue = str;
      this.updateControl();
      this.alignCursor();
    }
    /** Typed unmasked value */

  }, {
    key: "typedValue",
    get: function get() {
      return this.masked.typedValue;
    },
    set: function set(val) {
      this.masked.typedValue = val;
      this.updateControl();
      this.alignCursor();
    }
  }, {
    key: "selectionStart",
    get: function get() {
      return this._cursorChanging ? this._changingCursorPos : this.el.selectionStart;
    }
    /** Current cursor position */

  }, {
    key: "cursorPos",
    get: function get() {
      return this._cursorChanging ? this._changingCursorPos : this.el.selectionEnd;
    },
    set: function set(pos) {
      if (!this.el || !this.el.isActive) return;
      this.el.select(pos, pos);

      this._saveSelection();
    }
  }]);

  return InputMask;
}();
IMask.InputMask = InputMask;

/** Pattern which validates enum values */

var MaskedEnum =
/*#__PURE__*/
function (_MaskedPattern) {
  _inherits(MaskedEnum, _MaskedPattern);

  function MaskedEnum() {
    _classCallCheck(this, MaskedEnum);

    return _possibleConstructorReturn(this, _getPrototypeOf(MaskedEnum).apply(this, arguments));
  }

  _createClass(MaskedEnum, [{
    key: "_update",

    /**
      @override
      @param {Object} opts
    */
    value: function _update(opts) {
      // TODO type
      if (opts.enum) opts.mask = '*'.repeat(opts.enum[0].length);

      _get(_getPrototypeOf(MaskedEnum.prototype), "_update", this).call(this, opts);
    }
    /**
      @override
    */

  }, {
    key: "doValidate",
    value: function doValidate() {
      var _this = this,
          _get2;

      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      return this.enum.some(function (e) {
        return e.indexOf(_this.unmaskedValue) >= 0;
      }) && (_get2 = _get(_getPrototypeOf(MaskedEnum.prototype), "doValidate", this)).call.apply(_get2, [this].concat(args));
    }
  }]);

  return MaskedEnum;
}(MaskedPattern);
IMask.MaskedEnum = MaskedEnum;

/**
  Number mask
  @param {Object} opts
  @param {string} opts.radix - Single char
  @param {string} opts.thousandsSeparator - Single char
  @param {Array<string>} opts.mapToRadix - Array of single chars
  @param {number} opts.min
  @param {number} opts.max
  @param {number} opts.scale - Digits after point
  @param {boolean} opts.signed - Allow negative
  @param {boolean} opts.normalizeZeros - Flag to remove leading and trailing zeros in the end of editing
  @param {boolean} opts.padFractionalZeros - Flag to pad trailing zeros after point in the end of editing
*/
var MaskedNumber =
/*#__PURE__*/
function (_Masked) {
  _inherits(MaskedNumber, _Masked);

  /** Single char */

  /** Single char */

  /** Array of single chars */

  /** */

  /** */

  /** Digits after point */

  /** */

  /** Flag to remove leading and trailing zeros in the end of editing */

  /** Flag to pad trailing zeros after point in the end of editing */
  function MaskedNumber(opts) {
    _classCallCheck(this, MaskedNumber);

    return _possibleConstructorReturn(this, _getPrototypeOf(MaskedNumber).call(this, Object.assign({}, MaskedNumber.DEFAULTS, {}, opts)));
  }
  /**
    @override
  */


  _createClass(MaskedNumber, [{
    key: "_update",
    value: function _update(opts) {
      _get(_getPrototypeOf(MaskedNumber.prototype), "_update", this).call(this, opts);

      this._updateRegExps();
    }
    /** */

  }, {
    key: "_updateRegExps",
    value: function _updateRegExps() {
      // use different regexp to process user input (more strict, input suffix) and tail shifting
      var start = '^' + (this.allowNegative ? '[+|\\-]?' : '');
      var midInput = '(0|([1-9]+\\d*))?';
      var mid = '\\d*';
      var end = (this.scale ? '(' + escapeRegExp(this.radix) + '\\d{0,' + this.scale + '})?' : '') + '$';
      this._numberRegExpInput = new RegExp(start + midInput + end);
      this._numberRegExp = new RegExp(start + mid + end);
      this._mapToRadixRegExp = new RegExp('[' + this.mapToRadix.map(escapeRegExp).join('') + ']', 'g');
      this._thousandsSeparatorRegExp = new RegExp(escapeRegExp(this.thousandsSeparator), 'g');
    }
    /** */

  }, {
    key: "_removeThousandsSeparators",
    value: function _removeThousandsSeparators(value) {
      return value.replace(this._thousandsSeparatorRegExp, '');
    }
    /** */

  }, {
    key: "_insertThousandsSeparators",
    value: function _insertThousandsSeparators(value) {
      // https://stackoverflow.com/questions/2901102/how-to-print-a-number-with-commas-as-thousands-separators-in-javascript
      var parts = value.split(this.radix);
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, this.thousandsSeparator);
      return parts.join(this.radix);
    }
    /**
      @override
    */

  }, {
    key: "doPrepare",
    value: function doPrepare(str) {
      var _get2;

      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      return (_get2 = _get(_getPrototypeOf(MaskedNumber.prototype), "doPrepare", this)).call.apply(_get2, [this, this._removeThousandsSeparators(str.replace(this._mapToRadixRegExp, this.radix))].concat(args));
    }
    /** */

  }, {
    key: "_separatorsCount",
    value: function _separatorsCount(to) {
      var extendOnSeparators = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      var count = 0;

      for (var pos = 0; pos < to; ++pos) {
        if (this._value.indexOf(this.thousandsSeparator, pos) === pos) {
          ++count;
          if (extendOnSeparators) to += this.thousandsSeparator.length;
        }
      }

      return count;
    }
    /** */

  }, {
    key: "_separatorsCountFromSlice",
    value: function _separatorsCountFromSlice() {
      var slice = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this._value;
      return this._separatorsCount(this._removeThousandsSeparators(slice).length, true);
    }
    /**
      @override
    */

  }, {
    key: "extractInput",
    value: function extractInput() {
      var fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      var toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;
      var flags = arguments.length > 2 ? arguments[2] : undefined;

      var _this$_adjustRangeWit = this._adjustRangeWithSeparators(fromPos, toPos);

      var _this$_adjustRangeWit2 = _slicedToArray(_this$_adjustRangeWit, 2);

      fromPos = _this$_adjustRangeWit2[0];
      toPos = _this$_adjustRangeWit2[1];
      return this._removeThousandsSeparators(_get(_getPrototypeOf(MaskedNumber.prototype), "extractInput", this).call(this, fromPos, toPos, flags));
    }
    /**
      @override
    */

  }, {
    key: "_appendCharRaw",
    value: function _appendCharRaw(ch) {
      var flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      if (!this.thousandsSeparator) return _get(_getPrototypeOf(MaskedNumber.prototype), "_appendCharRaw", this).call(this, ch, flags);
      var prevBeforeTailValue = flags.tail && flags._beforeTailState ? flags._beforeTailState._value : this._value;

      var prevBeforeTailSeparatorsCount = this._separatorsCountFromSlice(prevBeforeTailValue);

      this._value = this._removeThousandsSeparators(this.value);

      var appendDetails = _get(_getPrototypeOf(MaskedNumber.prototype), "_appendCharRaw", this).call(this, ch, flags);

      this._value = this._insertThousandsSeparators(this._value);
      var beforeTailValue = flags.tail && flags._beforeTailState ? flags._beforeTailState._value : this._value;

      var beforeTailSeparatorsCount = this._separatorsCountFromSlice(beforeTailValue);

      appendDetails.tailShift += (beforeTailSeparatorsCount - prevBeforeTailSeparatorsCount) * this.thousandsSeparator.length;
      appendDetails.skip = !appendDetails.rawInserted && ch === this.thousandsSeparator;
      return appendDetails;
    }
    /** */

  }, {
    key: "_findSeparatorAround",
    value: function _findSeparatorAround(pos) {
      if (this.thousandsSeparator) {
        var searchFrom = pos - this.thousandsSeparator.length + 1;
        var separatorPos = this.value.indexOf(this.thousandsSeparator, searchFrom);
        if (separatorPos <= pos) return separatorPos;
      }

      return -1;
    }
  }, {
    key: "_adjustRangeWithSeparators",
    value: function _adjustRangeWithSeparators(from, to) {
      var separatorAroundFromPos = this._findSeparatorAround(from);

      if (separatorAroundFromPos >= 0) from = separatorAroundFromPos;

      var separatorAroundToPos = this._findSeparatorAround(to);

      if (separatorAroundToPos >= 0) to = separatorAroundToPos + this.thousandsSeparator.length;
      return [from, to];
    }
    /**
      @override
    */

  }, {
    key: "remove",
    value: function remove() {
      var fromPos = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      var toPos = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.value.length;

      var _this$_adjustRangeWit3 = this._adjustRangeWithSeparators(fromPos, toPos);

      var _this$_adjustRangeWit4 = _slicedToArray(_this$_adjustRangeWit3, 2);

      fromPos = _this$_adjustRangeWit4[0];
      toPos = _this$_adjustRangeWit4[1];
      var valueBeforePos = this.value.slice(0, fromPos);
      var valueAfterPos = this.value.slice(toPos);

      var prevBeforeTailSeparatorsCount = this._separatorsCount(valueBeforePos.length);

      this._value = this._insertThousandsSeparators(this._removeThousandsSeparators(valueBeforePos + valueAfterPos));

      var beforeTailSeparatorsCount = this._separatorsCountFromSlice(valueBeforePos);

      return new ChangeDetails({
        tailShift: (beforeTailSeparatorsCount - prevBeforeTailSeparatorsCount) * this.thousandsSeparator.length
      });
    }
    /**
      @override
    */

  }, {
    key: "nearestInputPos",
    value: function nearestInputPos(cursorPos, direction) {
      if (!this.thousandsSeparator) return cursorPos;

      switch (direction) {
        case DIRECTION.NONE:
        case DIRECTION.LEFT:
        case DIRECTION.FORCE_LEFT:
          {
            var separatorAtLeftPos = this._findSeparatorAround(cursorPos - 1);

            if (separatorAtLeftPos >= 0) {
              var separatorAtLeftEndPos = separatorAtLeftPos + this.thousandsSeparator.length;

              if (cursorPos < separatorAtLeftEndPos || this.value.length <= separatorAtLeftEndPos || direction === DIRECTION.FORCE_LEFT) {
                return separatorAtLeftPos;
              }
            }

            break;
          }

        case DIRECTION.RIGHT:
        case DIRECTION.FORCE_RIGHT:
          {
            var separatorAtRightPos = this._findSeparatorAround(cursorPos);

            if (separatorAtRightPos >= 0) {
              return separatorAtRightPos + this.thousandsSeparator.length;
            }
          }
      }

      return cursorPos;
    }
    /**
      @override
    */

  }, {
    key: "doValidate",
    value: function doValidate(flags) {
      var regexp = flags.input ? this._numberRegExpInput : this._numberRegExp; // validate as string

      var valid = regexp.test(this._removeThousandsSeparators(this.value));

      if (valid) {
        // validate as number
        var number = this.number;
        valid = valid && !isNaN(number) && ( // check min bound for negative values
        this.min == null || this.min >= 0 || this.min <= this.number) && ( // check max bound for positive values
        this.max == null || this.max <= 0 || this.number <= this.max);
      }

      return valid && _get(_getPrototypeOf(MaskedNumber.prototype), "doValidate", this).call(this, flags);
    }
    /**
      @override
    */

  }, {
    key: "doCommit",
    value: function doCommit() {
      if (this.value) {
        var number = this.number;
        var validnum = number; // check bounds

        if (this.min != null) validnum = Math.max(validnum, this.min);
        if (this.max != null) validnum = Math.min(validnum, this.max);
        if (validnum !== number) this.unmaskedValue = String(validnum);
        var formatted = this.value;
        if (this.normalizeZeros) formatted = this._normalizeZeros(formatted);
        if (this.padFractionalZeros) formatted = this._padFractionalZeros(formatted);
        this._value = formatted;
      }

      _get(_getPrototypeOf(MaskedNumber.prototype), "doCommit", this).call(this);
    }
    /** */

  }, {
    key: "_normalizeZeros",
    value: function _normalizeZeros(value) {
      var parts = this._removeThousandsSeparators(value).split(this.radix); // remove leading zeros


      parts[0] = parts[0].replace(/^(\D*)(0*)(\d*)/, function (match, sign, zeros, num) {
        return sign + num;
      }); // add leading zero

      if (value.length && !/\d$/.test(parts[0])) parts[0] = parts[0] + '0';

      if (parts.length > 1) {
        parts[1] = parts[1].replace(/0*$/, ''); // remove trailing zeros

        if (!parts[1].length) parts.length = 1; // remove fractional
      }

      return this._insertThousandsSeparators(parts.join(this.radix));
    }
    /** */

  }, {
    key: "_padFractionalZeros",
    value: function _padFractionalZeros(value) {
      if (!value) return value;
      var parts = value.split(this.radix);
      if (parts.length < 2) parts.push('');
      parts[1] = parts[1].padEnd(this.scale, '0');
      return parts.join(this.radix);
    }
    /**
      @override
    */

  }, {
    key: "unmaskedValue",
    get: function get() {
      return this._removeThousandsSeparators(this._normalizeZeros(this.value)).replace(this.radix, '.');
    },
    set: function set(unmaskedValue) {
      _set(_getPrototypeOf(MaskedNumber.prototype), "unmaskedValue", unmaskedValue.replace('.', this.radix), this, true);
    }
    /**
      @override
    */

  }, {
    key: "typedValue",
    get: function get() {
      return Number(this.unmaskedValue);
    },
    set: function set(n) {
      _set(_getPrototypeOf(MaskedNumber.prototype), "unmaskedValue", String(n), this, true);
    }
    /** Parsed Number */

  }, {
    key: "number",
    get: function get() {
      return this.typedValue;
    },
    set: function set(number) {
      this.typedValue = number;
    }
    /**
      Is negative allowed
      @readonly
    */

  }, {
    key: "allowNegative",
    get: function get() {
      return this.signed || this.min != null && this.min < 0 || this.max != null && this.max < 0;
    }
  }]);

  return MaskedNumber;
}(Masked);
MaskedNumber.DEFAULTS = {
  radix: ',',
  thousandsSeparator: '',
  mapToRadix: ['.'],
  scale: 2,
  signed: false,
  normalizeZeros: true,
  padFractionalZeros: false
};
IMask.MaskedNumber = MaskedNumber;

/** Masking by custom Function */

var MaskedFunction =
/*#__PURE__*/
function (_Masked) {
  _inherits(MaskedFunction, _Masked);

  function MaskedFunction() {
    _classCallCheck(this, MaskedFunction);

    return _possibleConstructorReturn(this, _getPrototypeOf(MaskedFunction).apply(this, arguments));
  }

  _createClass(MaskedFunction, [{
    key: "_update",

    /**
      @override
      @param {Object} opts
    */
    value: function _update(opts) {
      if (opts.mask) opts.validate = opts.mask;

      _get(_getPrototypeOf(MaskedFunction.prototype), "_update", this).call(this, opts);
    }
  }]);

  return MaskedFunction;
}(Masked);
IMask.MaskedFunction = MaskedFunction;

/** Dynamic mask for choosing apropriate mask in run-time */
var MaskedDynamic =
/*#__PURE__*/
function (_Masked) {
  _inherits(MaskedDynamic, _Masked);

  /** Currently chosen mask */

  /** Compliled {@link Masked} options */

  /** Chooses {@link Masked} depending on input value */

  /**
    @param {Object} opts
  */
  function MaskedDynamic(opts) {
    var _this;

    _classCallCheck(this, MaskedDynamic);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(MaskedDynamic).call(this, Object.assign({}, MaskedDynamic.DEFAULTS, {}, opts)));
    _this.currentMask = null;
    return _this;
  }
  /**
    @override
  */


  _createClass(MaskedDynamic, [{
    key: "_update",
    value: function _update(opts) {
      _get(_getPrototypeOf(MaskedDynamic.prototype), "_update", this).call(this, opts);

      if ('mask' in opts) {
        // mask could be totally dynamic with only `dispatch` option
        this.compiledMasks = Array.isArray(opts.mask) ? opts.mask.map(function (m) {
          return createMask(m);
        }) : [];
      }
    }
    /**
      @override
    */

  }, {
    key: "_appendCharRaw",
    value: function _appendCharRaw() {
      var details = this._applyDispatch.apply(this, arguments);

      if (this.currentMask) {
        var _this$currentMask;

        details.aggregate((_this$currentMask = this.currentMask)._appendChar.apply(_this$currentMask, arguments));
      }

      return details;
    }
  }, {
    key: "_applyDispatch",
    value: function _applyDispatch() {
      var appended = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
      var flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var prevValueBeforeTail = flags.tail && flags._beforeTailState != null ? flags._beforeTailState._value : this.value;
      var inputValue = this.rawInputValue;
      var insertValue = flags.tail && flags._beforeTailState != null ? // $FlowFixMe - tired to fight with type system
      flags._beforeTailState._rawInputValue : inputValue;
      var tailValue = inputValue.slice(insertValue.length);
      var prevMask = this.currentMask;
      var details = new ChangeDetails();
      var prevMaskState = prevMask && prevMask.state; // clone flags to prevent overwriting `_beforeTailState`

      this.currentMask = this.doDispatch(appended, Object.assign({}, flags)); // restore state after dispatch

      if (this.currentMask) {
        if (this.currentMask !== prevMask) {
          // if mask changed reapply input
          this.currentMask.reset(); // $FlowFixMe - it's ok, we don't change current mask above

          var d = this.currentMask.append(insertValue, {
            raw: true
          });
          details.tailShift = d.inserted.length - prevValueBeforeTail.length;

          if (tailValue) {
            // $FlowFixMe - it's ok, we don't change current mask above
            details.tailShift += this.currentMask.append(tailValue, {
              raw: true,
              tail: true
            }).tailShift;
          }
        } else {
          // Dispatch can do something bad with state, so
          // restore prev mask state
          this.currentMask.state = prevMaskState;
        }
      }

      return details;
    }
  }, {
    key: "_appendPlaceholder",
    value: function _appendPlaceholder() {
      var details = this._applyDispatch.apply(this, arguments);

      if (this.currentMask) {
        details.aggregate(this.currentMask._appendPlaceholder());
      }

      return details;
    }
    /**
      @override
    */

  }, {
    key: "doDispatch",
    value: function doDispatch(appended) {
      var flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      return this.dispatch(appended, this, flags);
    }
    /**
      @override
    */

  }, {
    key: "doValidate",
    value: function doValidate() {
      var _get2, _this$currentMask2;

      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      return (_get2 = _get(_getPrototypeOf(MaskedDynamic.prototype), "doValidate", this)).call.apply(_get2, [this].concat(args)) && (!this.currentMask || (_this$currentMask2 = this.currentMask).doValidate.apply(_this$currentMask2, args));
    }
    /**
      @override
    */

  }, {
    key: "reset",
    value: function reset() {
      if (this.currentMask) this.currentMask.reset();
      this.compiledMasks.forEach(function (m) {
        return m.reset();
      });
    }
    /**
      @override
    */

  }, {
    key: "remove",

    /**
      @override
    */
    value: function remove() {
      var details = new ChangeDetails();

      if (this.currentMask) {
        var _this$currentMask3;

        details.aggregate((_this$currentMask3 = this.currentMask).remove.apply(_this$currentMask3, arguments)) // update with dispatch
        .aggregate(this._applyDispatch());
      }

      return details;
    }
    /**
      @override
    */

  }, {
    key: "extractInput",

    /**
      @override
    */
    value: function extractInput() {
      var _this$currentMask4;

      return this.currentMask ? (_this$currentMask4 = this.currentMask).extractInput.apply(_this$currentMask4, arguments) : '';
    }
    /**
      @override
    */

  }, {
    key: "extractTail",
    value: function extractTail() {
      var _this$currentMask5, _get3;

      for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        args[_key2] = arguments[_key2];
      }

      return this.currentMask ? (_this$currentMask5 = this.currentMask).extractTail.apply(_this$currentMask5, args) : (_get3 = _get(_getPrototypeOf(MaskedDynamic.prototype), "extractTail", this)).call.apply(_get3, [this].concat(args));
    }
    /**
      @override
    */

  }, {
    key: "doCommit",
    value: function doCommit() {
      if (this.currentMask) this.currentMask.doCommit();

      _get(_getPrototypeOf(MaskedDynamic.prototype), "doCommit", this).call(this);
    }
    /**
      @override
    */

  }, {
    key: "nearestInputPos",
    value: function nearestInputPos() {
      var _this$currentMask6, _get4;

      for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
        args[_key3] = arguments[_key3];
      }

      return this.currentMask ? (_this$currentMask6 = this.currentMask).nearestInputPos.apply(_this$currentMask6, args) : (_get4 = _get(_getPrototypeOf(MaskedDynamic.prototype), "nearestInputPos", this)).call.apply(_get4, [this].concat(args));
    }
  }, {
    key: "value",
    get: function get() {
      return this.currentMask ? this.currentMask.value : '';
    },
    set: function set(value) {
      _set(_getPrototypeOf(MaskedDynamic.prototype), "value", value, this, true);
    }
    /**
      @override
    */

  }, {
    key: "unmaskedValue",
    get: function get() {
      return this.currentMask ? this.currentMask.unmaskedValue : '';
    },
    set: function set(unmaskedValue) {
      _set(_getPrototypeOf(MaskedDynamic.prototype), "unmaskedValue", unmaskedValue, this, true);
    }
    /**
      @override
    */

  }, {
    key: "typedValue",
    get: function get() {
      return this.currentMask ? this.currentMask.typedValue : '';
    } // probably typedValue should not be used with dynamic
    ,
    set: function set(value) {
      var unmaskedValue = String(value); // double check it

      if (this.currentMask) {
        this.currentMask.typedValue = value;
        unmaskedValue = this.currentMask.unmaskedValue;
      }

      this.unmaskedValue = unmaskedValue;
    }
    /**
      @override
    */

  }, {
    key: "isComplete",
    get: function get() {
      return !!this.currentMask && this.currentMask.isComplete;
    }
  }, {
    key: "state",
    get: function get() {
      return Object.assign({}, _get(_getPrototypeOf(MaskedDynamic.prototype), "state", this), {
        _rawInputValue: this.rawInputValue,
        compiledMasks: this.compiledMasks.map(function (m) {
          return m.state;
        }),
        currentMaskRef: this.currentMask,
        currentMask: this.currentMask && this.currentMask.state
      });
    },
    set: function set(state) {
      var compiledMasks = state.compiledMasks,
          currentMaskRef = state.currentMaskRef,
          currentMask = state.currentMask,
          maskedState = _objectWithoutProperties(state, ["compiledMasks", "currentMaskRef", "currentMask"]);

      this.compiledMasks.forEach(function (m, mi) {
        return m.state = compiledMasks[mi];
      });

      if (currentMaskRef != null) {
        this.currentMask = currentMaskRef;
        this.currentMask.state = currentMask;
      }

      _set(_getPrototypeOf(MaskedDynamic.prototype), "state", maskedState, this, true);
    }
  }, {
    key: "overwrite",
    get: function get() {
      return this.currentMask ? this.currentMask.overwrite : _get(_getPrototypeOf(MaskedDynamic.prototype), "overwrite", this);
    },
    set: function set(overwrite) {
      console.warn('"overwrite" option is not available in dynamic mask, use this option in siblings');
    }
  }]);

  return MaskedDynamic;
}(Masked);
MaskedDynamic.DEFAULTS = {
  dispatch: function dispatch(appended, masked, flags) {
    if (!masked.compiledMasks.length) return;
    var inputValue = masked.rawInputValue; // simulate input

    var inputs = masked.compiledMasks.map(function (m, index) {
      m.reset();
      m.append(inputValue, {
        raw: true
      });
      m.append(appended, flags);
      var weight = m.rawInputValue.length;
      return {
        weight: weight,
        index: index
      };
    }); // pop masks with longer values first

    inputs.sort(function (i1, i2) {
      return i2.weight - i1.weight;
    });
    return masked.compiledMasks[inputs[0].index];
  }
};
IMask.MaskedDynamic = MaskedDynamic;

/** Mask pipe source and destination types */

var PIPE_TYPE = {
  MASKED: 'value',
  UNMASKED: 'unmaskedValue',
  TYPED: 'typedValue'
};
/** Creates new pipe function depending on mask type, source and destination options */

function createPipe(mask) {
  var from = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : PIPE_TYPE.MASKED;
  var to = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : PIPE_TYPE.MASKED;
  var masked = createMask(mask);
  return function (value) {
    return masked.runIsolated(function (m) {
      m[from] = value;
      return m[to];
    });
  };
}
/** Pipes value through mask depending on mask type, source and destination options */

function pipe(value) {
  for (var _len = arguments.length, pipeArgs = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    pipeArgs[_key - 1] = arguments[_key];
  }

  return createPipe.apply(void 0, pipeArgs)(value);
}
IMask.PIPE_TYPE = PIPE_TYPE;
IMask.createPipe = createPipe;
IMask.pipe = pipe;

try {
  globalThis.IMask = IMask;
} catch (e) {}

const inputCss = ".fi-input.sc-fi-input{display:block;text-align:left;font-family:inherit;position:relative;padding-top:19px}.fi-input.sc-fi-input .input-group.sc-fi-input{width:100%;display:flex;flex-wrap:nowrap;align-items:stretch}.fi-input.sc-fi-input .input-group.sc-fi-input .input-container.sc-fi-input{width:100%;display:flex;flex-direction:column;position:unset}.fi-input.sc-fi-input .input-group.sc-fi-input .input-container.sc-fi-input::after{content:\"\";display:block;width:100%;height:1px;background-color:var(--fi-secondary-600, #d1d6df);transition:background-color 0.3s}.fi-input.sc-fi-input .input-group.sc-fi-input .input-container.sc-fi-input label.sc-fi-input{font-size:12px;font-weight:normal;font-stretch:normal;font-style:normal;letter-spacing:normal;color:var(--fi-secondary-400, #8b99ae);transition:0.2s ease all;pointer-events:none;position:absolute;left:0}.fi-input.sc-fi-input .input-group.sc-fi-input .input-container.sc-fi-input label.label.sc-fi-input{top:0}.fi-input.sc-fi-input .input-group.sc-fi-input .input-container.sc-fi-input label.placeholder.sc-fi-input{font-size:18px;opacity:1;top:unset;left:0;bottom:8px;line-height:18px}.fi-input.sc-fi-input .input-group.sc-fi-input .input-container.sc-fi-input label.placeholder.sc-fi-input+.input-control.sc-fi-input input.sc-fi-input::placeholder{opacity:0}.fi-input.sc-fi-input .input-group.sc-fi-input .input-container.sc-fi-input .input-control.sc-fi-input{display:flex;align-items:center;width:100%;padding:1px 0 8px 0}.fi-input.sc-fi-input .input-group.sc-fi-input .input-container.sc-fi-input .input-control.sc-fi-input input.sc-fi-input{font-size:18px;font-weight:normal;box-shadow:none;box-sizing:border-box;line-height:normal;margin:0;max-width:100%;vertical-align:middle;width:100%;border-radius:0;outline:none;border:none;background-color:transparent;font-stretch:normal;font-style:normal;letter-spacing:normal;color:var(--fi-secondary, #2b2e42);caret-color:#888d96;font-family:inherit;padding:0}.fi-input.sc-fi-input .input-group.sc-fi-input .input-container.sc-fi-input .input-control.sc-fi-input input.sc-fi-input:disabled{border-bottom:none !important;color:var(--fi-secondary, #2b2e42);-webkit-text-fill-color:var(--fi-secondary, #2b2e42);opacity:1;text-overflow:ellipsis}.fi-input.sc-fi-input .input-group.sc-fi-input .input-container.sc-fi-input .input-control.sc-fi-input input.sc-fi-input:focus{outline:none}.fi-input.sc-fi-input .input-group.sc-fi-input .input-container.sc-fi-input .input-control.sc-fi-input input.sc-fi-input::placeholder{font-size:18px;font-weight:normal;font-stretch:normal;font-style:normal;letter-spacing:normal;color:var(--fi-secondary-400, #8b99ae);opacity:1;bottom:8px;left:0;line-height:18px}.fi-input.sc-fi-input .input-group.sc-fi-input .input-container.sc-fi-input .input-control.sc-fi-input .status-icon.sc-fi-input{display:flex;margin:0 4px}.fi-input.sc-fi-input .input-group.sc-fi-input .input-container.sc-fi-input:has(label.placeholder){position:relative}.fi-input.sc-fi-input .hint.sc-fi-input{font-size:12px;padding-top:6px;text-align:left;color:var(--fi-secondary-400, #8b99ae)}.fi-input.sc-fi-input .hint.warning.sc-fi-input{color:var(--fi-warning, #ff5d00)}.fi-input.sc-fi-input .hint.error.sc-fi-input{color:var(--fi-error, #ff0226)}.fi-input.sc-fi-input .progress-bar.sc-fi-input{height:10px;border-radius:7px;background-color:#edeff3;flex:1}.fi-input.sc-fi-input .progress-bar-wrapper.sc-fi-input{display:flex;margin-top:10px}.fi-input.sc-fi-input .progress-bar-fill.sc-fi-input{display:block;height:10px;border-radius:7px;transition:width 500ms ease-in-out}.fi-input.sc-fi-input .progress-bar-label.sc-fi-input{font-size:10px;font-weight:normal;font-stretch:normal;font-style:normal;line-height:1;letter-spacing:normal;text-align:right;color:#2b2e42;margin-left:8px}.fi-input.sc-fi-input .password-toggle.sc-fi-input{display:flex}.fi-input.valid.sc-fi-input .input-group.sc-fi-input .input-container.sc-fi-input::after{background-color:#00c316 !important}.fi-input.error.sc-fi-input .input-group.sc-fi-input .input-container.sc-fi-input::after{background-color:var(--fi-error, #ff0226) !important}.fi-input.warning.sc-fi-input .input-group.sc-fi-input .input-container.sc-fi-input::after{background-color:var(--fi-warning, #ffa672) !important}.fi-input.sc-fi-input:has(input:focus) .input-group.sc-fi-input .input-container.sc-fi-input{position:unset}.fi-input.sc-fi-input:has(input:focus) .input-group.sc-fi-input .input-container.sc-fi-input label.placeholder.sc-fi-input{font-size:12px;line-height:unset;top:0;bottom:unset}.fi-input.sc-fi-input:has(input:focus) .input-group.sc-fi-input .input-container.sc-fi-input .input-control.sc-fi-input input.sc-fi-input::placeholder{opacity:1 !important}.fi-input.sc-fi-input:has(input:focus) .input-group.sc-fi-input .input-container.sc-fi-input::after{background-color:#1a84c5}@keyframes scale{0%{transform:scale(1)}50%{transform:scale(2)}100%{transform:scale(1)}}";

let Input = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.fiInput = createEvent(this, "fiInput", 7);
    this.fiFocus = createEvent(this, "fiFocus", 7);
    this.fiBlur = createEvent(this, "fiBlur", 7);
    /**
     * Input label
     */
    this.label = '';
    /**
     * Input label
     */
    this.placeholder = '';
    /**
     * The value of the input.
     */
    this.value = '';
    /**
     * Input type
     */
    this.type = 'text';
    /**
     * Input helper text.
     * Can be stringyfied HTML
     */
    this.description = '';
    /**
     * Input is disabled
     */
    this.disabled = false;
    /**
     * Input pattern for imask
     */
    this.pattern = /[\s\S]*/;
    /**
     * Class name that is injected based on field validation
     */
    this.validationClass = '';
    /**
     * Strength indicator values
     */
    this.strengthIndicator = [];
    /**
     * Strength indicator values
     */
    this.validationCheckmarkEnabled = true;
    this.inputType = 'text';
    this.onMaskedInput = () => {
      this.fiInput.emit({ unmasked: this.imask.unmaskedValue, masked: this.imask.value });
    };
    this.togglePasswordType = () => {
      if (this.disabled)
        return;
      this.inputType = this.inputType === 'password' ? 'text' : 'password';
    };
    this.renderPasswordToggle = () => {
      return (h("div", { class: "password-toggle", onClick: this.togglePasswordType }, this.inputType == 'password' ?
        h("svg", { xmlns: "http://www.w3.org/2000/svg", width: "23", height: "17", viewBox: "0 0 20 15" }, h("g", { fill: "#8B99AE", "fill-rule": "evenodd" }, h("path", { d: "M10.328 1.5c-2.204 0-4.135 1.065-5.776 2.815-.564.602-1.054 1.244-1.468 1.887-.25.388-.424.694-.516.878-.092.183-.092.4 0 .583.092.183.265.49.516.878.414.642.904 1.284 1.468 1.886 1.641 1.751 3.572 2.816 5.776 2.816 2.205 0 4.136-1.065 5.777-2.816.564-.602 1.053-1.244 1.47-1.886.25-.389.421-.695.513-.878.092-.184.092-.4 0-.583-.092-.184-.264-.49-.514-.878-.416-.643-.905-1.285-1.469-1.887-1.64-1.75-3.572-2.815-5.777-2.815m0 1c1.794 0 3.492.841 5.048 2.5.493.525.95 1.112 1.357 1.744.181.28.304.492.378.627-.074.136-.197.348-.378.627-.408.632-.865 1.22-1.357 1.745-1.556 1.658-3.253 2.5-5.048 2.5-1.793 0-3.49-.842-5.047-2.5-.492-.526-.949-1.113-1.357-1.745-.18-.28-.304-.49-.378-.627.074-.135.197-.347.378-.627.408-.632.865-1.219 1.357-1.744 1.556-1.659 3.254-2.5 5.047-2.5" }), h("path", { d: "M13.402 7.371c0 1.698-1.376 3.074-3.073 3.074-1.698 0-3.074-1.376-3.074-3.074 0-1.697 1.376-3.074 3.074-3.074 1.697 0 3.073 1.377 3.073 3.074" })))
        :
          h("svg", { xmlns: "http://www.w3.org/2000/svg", width: "23", height: "17", viewBox: "0 0 20 15" }, h("g", { fill: "none", "fill-rule": "evenodd" }, h("path", { stroke: "#2B2D42", "stroke-linecap": "round", d: "M1.918 2.633C1.39 3.195.934 3.796.545 4.397c-.234.363-.396.649-.481.82-.085.172-.085.374 0 .546.085.171.247.457.48.821.39.601.847 1.201 1.374 1.764 1.535 1.637 3.34 2.633 5.402 2.633 1.022 0 1.98-.245 2.874-.691m2.249-1.656c.094-.093.187-.189.279-.286.527-.563.985-1.163 1.373-1.764.234-.364.395-.65.48-.821.087-.172.087-.374 0-.546-.085-.171-.246-.457-.48-.82-.388-.601-.846-1.202-1.373-1.764C11.187.996 9.382 0 7.32 0 6.218 0 5.189.284 4.234.8", transform: "translate(3 2)" }), h("path", { fill: "#2B2D42", d: "M8.69 8.5c-.357.145-.747.225-1.155.225-1.699 0-3.074-1.376-3.074-3.074 0-.378.068-.739.192-1.074L8.69 8.5zM5.993 2.991c.453-.263.979-.414 1.542-.414 1.697 0 3.073 1.376 3.073 3.074 0 .507-.123.986-.341 1.408L5.993 2.991z", transform: "translate(3 2)" }), h("path", { stroke: "#2B2D42", "stroke-linecap": "round", d: "M1.523 0.236L13.134 11.14", transform: "translate(3 2)" })))));
    };
    this.renderPasswordStrengthIndicator = () => {
      if (this.strengthIndicator.length === 0) {
        return;
      }
      return (h("div", { class: "progress-bar-wrapper" }, h("div", { class: "progress-bar" }, h("span", { class: "progress-bar-fill", style: {
          width: this.getPasswordStrengthIndicatorLength + '%',
          backgroundColor: this.getPasswordStrength.color,
        } })), h("div", { class: "progress-bar-label" }, this.getPasswordStrength.label)));
    };
    this.renderCheckmark = () => {
      return (h("div", { class: `status-icon checkmark ${this.validationClass}` }, h("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 16 16" }, h("g", { fill: "none", "fill-rule": "evenodd" }, h("g", { fill: "#00C316" }, h("g", null, h("path", { d: "M3.175 10.407c-.233-.225-.233-.589 0-.814l8.547-8.349c.338-.325.887-.325 1.225 0 .337.326.337.855 0 1.18L5.2 10l3.746 3.575c.337.326.337.855 0 1.18-.338.326-.887.326-1.225 0l-4.547-4.348z", transform: "translate(-314 -310) translate(20 283) matrix(0 -1 -1 0 310.1 43.1)" })))))));
    };
    this.renderWarning = () => {
      return (h("div", { class: `status-icon warning` }, h("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 16 16" }, h("g", { fill: "none", "fill-rule": "evenodd" }, h("g", { fill: "#ff5d00" }, h("g", null, h("path", { d: "m14.933 13.375 -6.5 -11.25c-0.097 -0.167 -0.264 -0.25 -0.433 -0.25s-0.338 0.083 -0.433 0.25l-6.5 11.25C0.875 13.709 1.116 14.125 1.5 14.125h13c0.384 0 0.625 -0.416 0.433 -0.75M7.5 6.5c0 -0.069 0.056 -0.125 0.125 -0.125h0.75c0.069 0 0.125 0.056 0.125 0.125v2.875c0 0.069 -0.056 0.125 -0.125 0.125h-0.75c-0.069 0 -0.125 -0.056 -0.125 -0.125zm0.5 5.5a0.75 0.75 0 0 1 0 -1.5 0.75 0.75 0 0 1 0 1.5" })))))));
    };
    this.focus = (e) => {
      this.fiFocus.emit(e);
    };
    this.blur = (e) => {
      this.fiBlur.emit(e);
    };
  }
  /**
   * Update the native input element when the value changes
   */
  valueChanged(newValue, oldValue) {
    if (this.imask === undefined) {
      return;
    }
    if (newValue.toString() !== oldValue.toString()) {
      if (this.type === 'email') {
        this.imask.unmaskedValue = this.value.replace(/ /g, '');
      }
      else {
        this.imask.unmaskedValue = this.value;
      }
      this.imask.updateValue();
    }
  }
  getLabelClass() {
    if (this.value.length > 0)
      return 'label';
    else
      return 'placeholder';
  }
  setupTextMask(inputElement, mask) {
    if (typeof mask === 'string' || mask instanceof RegExp) {
      this.imask = IMask(inputElement, { mask });
    }
    else {
      this.imask = IMask(inputElement, mask);
    }
    this.imask.on('accept', this.onMaskedInput);
    this.imask.unmaskedValue = this.value;
  }
  componentWillLoad() {
    if (this.type === 'numeric') {
      this.inputType = 'tel';
    }
    else {
      this.inputType = this.type;
    }
  }
  componentDidLoad() {
    const element = this.element.querySelector('input');
    if (element !== null)
      this.setupTextMask(element, this.pattern);
  }
  disconnectedCallback() {
    this.imask.destroy();
  }
  get getPasswordStrength() {
    let strength;
    if (this.strengthIndicator.length > 0) {
      for (const item of this.strengthIndicator) {
        if (new RegExp(item.regex).test(this.value)) {
          if (!strength) {
            strength = item;
          }
          if (strength && strength.index < item.index) {
            strength = item;
          }
        }
      }
    }
    return strength;
  }
  get getPasswordStrengthIndicatorLength() {
    if (this.getPasswordStrength.index === 1 && this.value.length === 0) {
      return '0';
    }
    if (this.strengthIndicator.length > 0) {
      return ((this.getPasswordStrength.index / this.strengthIndicator.length) * 100);
    }
  }
  render() {
    return (h("div", { class: `fi-input ${this.type} ${this.value.length > 0 ? 'visited' : ''} ${this.validationClass}` }, h("div", { class: "input-group" }, h("slot", { name: "prefix" }), h("div", { class: "input-container" }, this.label && h("label", { htmlFor: this.name, class: `${this.getLabelClass()}` }, this.label), h("div", { class: "input-control" }, h("input", { name: this.name, type: this.inputType, value: "", disabled: this.disabled, autoComplete: this.name, placeholder: this.placeholder, onFocus: this.focus, onBlur: this.blur }), (this.validationClass.includes('valid') && this.validationCheckmarkEnabled) && this.renderCheckmark(), (this.validationClass.includes('warning') && this.validationCheckmarkEnabled) && this.renderWarning(), this.type === 'password' && this.renderPasswordToggle()), h("span", { class: "underline" })), h("slot", { name: "suffix" })), this.type === 'password' && this.renderPasswordStrengthIndicator(), this.description && h("div", { class: "hint", innerHTML: this.description }), (this.validationClass.includes('error') && this.errorMessage) && h("div", { class: "hint error" }, this.errorMessage), this.hint && h("div", { class: `hint ${this.validationClass}` }, this.hint)));
  }
  get element() { return getElement(this); }
  static get watchers() { return {
    "value": ["valueChanged"]
  }; }
};
Input.style = inputCss;

const loaderCss = ".sc-fi-loader-h{display:block}.loader.sc-fi-loader{color:inherit;font-size:inherit;text-indent:-9999em;overflow:hidden;width:1em;height:1em;border-radius:50%;margin:0 auto;position:relative;display:inline-block;vertical-align:bottom;-webkit-transform:translateZ(0);-ms-transform:translateZ(0);transform:translateZ(0);-webkit-animation:load6 1s infinite ease, round 1s infinite ease;animation:load6 1s infinite ease, round 1s infinite ease}@-webkit-keyframes load6{0%{box-shadow:0 -0.83em 0 -0.4em, 0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em, 0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em}5%,95%{box-shadow:0 -0.83em 0 -0.4em, 0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em, 0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em}10%,59%{box-shadow:0 -0.83em 0 -0.4em, -0.087em -0.825em 0 -0.42em, -0.173em -0.812em 0 -0.44em, -0.256em -0.789em 0 -0.46em, -0.297em -0.775em 0 -0.477em}20%{box-shadow:0 -0.83em 0 -0.4em, -0.338em -0.758em 0 -0.42em, -0.555em -0.617em 0 -0.44em, -0.671em -0.488em 0 -0.46em, -0.749em -0.34em 0 -0.477em}38%{box-shadow:0 -0.83em 0 -0.4em, -0.377em -0.74em 0 -0.42em, -0.645em -0.522em 0 -0.44em, -0.775em -0.297em 0 -0.46em, -0.82em -0.09em 0 -0.477em}100%{box-shadow:0 -0.83em 0 -0.4em, 0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em, 0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em}}@keyframes load6{0%{box-shadow:0 -0.83em 0 -0.4em, 0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em, 0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em}5%,95%{box-shadow:0 -0.83em 0 -0.4em, 0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em, 0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em}10%,59%{box-shadow:0 -0.83em 0 -0.4em, -0.087em -0.825em 0 -0.42em, -0.173em -0.812em 0 -0.44em, -0.256em -0.789em 0 -0.46em, -0.297em -0.775em 0 -0.477em}20%{box-shadow:0 -0.83em 0 -0.4em, -0.338em -0.758em 0 -0.42em, -0.555em -0.617em 0 -0.44em, -0.671em -0.488em 0 -0.46em, -0.749em -0.34em 0 -0.477em}38%{box-shadow:0 -0.83em 0 -0.4em, -0.377em -0.74em 0 -0.42em, -0.645em -0.522em 0 -0.44em, -0.775em -0.297em 0 -0.46em, -0.82em -0.09em 0 -0.477em}100%{box-shadow:0 -0.83em 0 -0.4em, 0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em, 0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em}}@-webkit-keyframes round{0%{-webkit-transform:rotate(0deg);transform:rotate(0deg)}100%{-webkit-transform:rotate(360deg);transform:rotate(360deg)}}@keyframes round{0%{-webkit-transform:rotate(0deg);transform:rotate(0deg)}100%{-webkit-transform:rotate(360deg);transform:rotate(360deg)}}";

let Loader = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
  }
  render() {
    return (h("div", { class: "loader", style: { color: this.color, fontSize: `${this.size}px` } }));
  }
};
Loader.style = loaderCss;

const progressStepsCss$1 = ".progress-steps .progress-step{display:flex;flex-direction:row;position:relative}.progress-steps .progress-step .step-number{align-items:center;display:flex;flex-direction:column}.progress-steps .progress-step .step-number .step-identifier{color:#6d798c;border:1px solid #6d798c;align-items:center;border-radius:50%;display:inline-flex;font-size:0.75rem;justify-content:center;height:24px;min-width:24px;width:24px}.progress-steps .progress-step .step-number .step-spacer{flex:1;border-left:1px solid #6d798c}.progress-steps .progress-step .step-label{margin-top:4px;margin-left:10px;margin-bottom:20px;text-align:initial}.progress-steps .progress-step:last-child .step-spacer{display:none}.progress-steps .progress-step.step-current .step-identifier{border-color:#02d529}.progress-steps .progress-step.step-done .step-identifier{color:#02d529;border-color:#02d529;background-color:#ECFFF0}.progress-steps .progress-step.step-done .step-spacer{border-color:#02d529}";

let ProgressStep = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    /**
     * Current step
     */
    this.current = false;
    /**
     * Step done
     */
    this.done = false;
  }
  render() {
    return (h(Host, { class: `progress-step ${this.current ? 'step-current' : ''} ${this.done ? 'step-done' : ''}` }, h("div", { class: `step-number` }, h("span", { class: `step-identifier` }, this.done ?
      h("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 16 16" }, h("g", { fill: "none", "fill-rule": "evenodd" }, h("g", { fill: "#00C316" }, h("g", null, h("path", { d: "M3.175 10.407c-.233-.225-.233-.589 0-.814l8.547-8.349c.338-.325.887-.325 1.225 0 .337.326.337.855 0 1.18L5.2 10l3.746 3.575c.337.326.337.855 0 1.18-.338.326-.887.326-1.225 0l-4.547-4.348z", transform: "translate(-314 -310) translate(20 283) matrix(0 -1 -1 0 310.1 43.1)" })))))
      :
        h("slot", { name: "number" })), h("div", { class: `step-spacer` })), h("div", { class: `step-label` }, h("slot", { name: "label" }))));
  }
};
ProgressStep.style = progressStepsCss$1;

const progressStepsCss = ".progress-steps .progress-step{display:flex;flex-direction:row;position:relative}.progress-steps .progress-step .step-number{align-items:center;display:flex;flex-direction:column}.progress-steps .progress-step .step-number .step-identifier{color:#6d798c;border:1px solid #6d798c;align-items:center;border-radius:50%;display:inline-flex;font-size:0.75rem;justify-content:center;height:24px;min-width:24px;width:24px}.progress-steps .progress-step .step-number .step-spacer{flex:1;border-left:1px solid #6d798c}.progress-steps .progress-step .step-label{margin-top:4px;margin-left:10px;margin-bottom:20px;text-align:initial}.progress-steps .progress-step:last-child .step-spacer{display:none}.progress-steps .progress-step.step-current .step-identifier{border-color:#02d529}.progress-steps .progress-step.step-done .step-identifier{color:#02d529;border-color:#02d529;background-color:#ECFFF0}.progress-steps .progress-step.step-done .step-spacer{border-color:#02d529}";

let ProgressSteps = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
  }
  render() {
    return (h(Host, null, h("div", { class: `progress-steps` }, h("slot", null))));
  }
};
ProgressSteps.style = progressStepsCss;

const progressbarCss = ".progress-bar{height:1px;background-color:var(--fi-secondary-600, #d1d6df);flex:1}.progress-bar-wrapper{display:flex}.progress-bar-fill{margin-top:-1px;display:block;height:3px;border-top-right-radius:5px;border-bottom-right-radius:5px;transition:width 500ms ease-in-out;background-color:var(--fi-primary, #ffc809)}";

let Progressbar = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
  }
  computeProgressStyles() {
    return (this.currentStep / this.steps) * 100 + '%';
  }
  render() {
    return (h(Host, null, h("div", { class: "progress-bar-wrapper" }, h("div", { part: "bar", class: "progress-bar" }, h("span", { part: "fill", class: "progress-bar-fill", style: {
        width: this.computeProgressStyles()
      } })))));
  }
};
Progressbar.style = progressbarCss;

const radioButtonCss = ".sc-fi-radio-button-h{display:block}.radio-buttons-container.sc-fi-radio-button .label.sc-fi-radio-button{font-size:18px;color:#8b99ae}.radio-buttons-container.sc-fi-radio-button .label.label-small.sc-fi-radio-button{font-size:12px;height:18px}.radio-buttons-container.sc-fi-radio-button .radio-buttons.sc-fi-radio-button{display:flex;flex-wrap:wrap;text-align:center;font-size:14px;line-height:1.43;cursor:pointer}.radio-buttons-container.sc-fi-radio-button .radio-buttons.sc-fi-radio-button .radio-button.sc-fi-radio-button{border:solid 1px var(--fi-secondary-600, #d1d6df);padding:10px 40px;border-radius:var(--fi-button-border-radius, 6px);margin-right:0.2rem}.radio-buttons-container.sc-fi-radio-button .radio-buttons.sc-fi-radio-button .radio-button.sc-fi-radio-button:last-child{margin-right:0}.radio-buttons-container.sc-fi-radio-button .radio-buttons.sc-fi-radio-button .radio-button.radio-button-small.sc-fi-radio-button{padding:10px 22px}.radio-buttons-container.sc-fi-radio-button .radio-buttons.sc-fi-radio-button .selected.sc-fi-radio-button{background:var(--fi-light-green, #ecfff0);border:1px solid var(--fi-green, #00c316)}.radio-buttons-container.error.sc-fi-radio-button .radio-buttons.sc-fi-radio-button .radio-button.sc-fi-radio-button{border-color:var(--fi-error, #ff0226)}.disabled.sc-fi-radio-button{opacity:0.5;cursor:default}";

let RadioButton = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.fiInput = createEvent(this, "fiInput", 7);
    /**
     * Label for dropdown
     */
    this.label = "";
    /**
     * Label style
     */
    this.labelStyle = "";
    /**
     * Value that is selected
     */
    this.checkedValue = "";
    /**
     *
     */
    this.options = [];
    /**
     * Options style
     */
    this.optionsStyle = "";
    /**
     * Disabled status
     */
    this.disabled = false;
    /**
     * Class name that is injected based on field validation
     */
    this.validationClass = "";
    this.value = '';
    this.onChange = (value) => {
      if (this.disabled) {
        return;
      }
      this.value = value;
      this.fiInput.emit(this.value);
    };
  }
  /**
   * Update the native input element when the checkedValue changes
   */
  valueChanged() {
    this.value = this.checkedValue;
  }
  componentWillLoad() {
    this.value = this.checkedValue;
  }
  render() {
    return (h(Host, null, h("div", { class: `radio-buttons-container ${this.validationClass}` }, this.label && h("div", { class: `label label-${this.labelStyle || 'default'}` }, this.label), h("div", { class: "radio-buttons" }, this.options.map((option) => (h("div", { onClick: () => this.onChange(option.value), class: `radio-button radio-button-${this.optionsStyle || 'default'} ${this.value === option.value && "selected"} ${this.disabled && 'disabled'}` }, option.properties && option.properties.label ? option.properties.label : option.label)))))));
  }
  static get watchers() { return {
    "checkedValue": ["valueChanged"]
  }; }
};
RadioButton.style = radioButtonCss;

const rangeCss = ".sc-fi-range-h{display:block;position:relative}.input-range-wrapper.sc-fi-range{position:relative;line-height:normal}.input-number.sc-fi-range{box-sizing:border-box;width:100%;border-radius:6px;border:1px solid #d1d6df;background-color:#ffffff;padding:8px}.error.sc-fi-range .input-number.sc-fi-range,.error.sc-fi-range .input-number.sc-fi-range:focus-within{border:1px solid var(--fi-error, #ff0226)}.range-wrapper.sc-fi-range{width:100%;padding:0 6px;box-sizing:border-box;position:absolute;bottom:-7px;left:0}.input-prefix.sc-fi-range,.input-suffix.sc-fi-range{font-size:var(--fi-slider-input-font-size, 18px);font-weight:var(--fi-slider-input-font-weight, normal);padding:0 2px;vertical-align:middle}#input-wrapper.sc-fi-range input[type=tel].sc-fi-range{border:none;outline:none;appearance:none;background:none;padding:0;font-size:var(--fi-slider-input-font-size, 18px);font-weight:var(--fi-slider-input-font-weight, normal);font-family:inherit;text-align:center;display:inline-flex;color:var(--fi-secondary, #2b2e42);vertical-align:middle;caret-color:var(--fi-primary, #ffc809)}.min-max.sc-fi-range{display:flex;justify-content:space-between;color:var(--fi-secondary-100, #535567);margin-top:14px;font-size:12px}.error-message.sc-fi-range{font-size:12px;font-weight:normal;font-stretch:normal;font-style:normal;line-height:normal;letter-spacing:normal;color:var(--fi-error, #ff0226);margin-top:5px}label.sc-fi-range{visibility:visible;font-size:12px;font-weight:normal;font-stretch:normal;font-style:normal;line-height:normal;letter-spacing:normal;color:var(--fi-secondary-100, #535567);display:block;padding-left:10px;padding-bottom:5px}.input-number.sc-fi-range:focus-within{border:1px solid var(--fi-primary, #ffc809)}.error.sc-fi-range .input-number.sc-fi-range:focus-within{border:1px solid var(--fi-error, #ff0226)}input[type=range].sc-fi-range{height:3px;-webkit-appearance:none;width:100%;margin:0;border-radius:5px}input[type=range].sc-fi-range:focus{outline:none}input[type=range].sc-fi-range::-webkit-slider-runnable-track{width:100%;height:3px;cursor:pointer;box-shadow:0 0 0 #000000;border-radius:5px;border:0 solid #000000}input[type=range].sc-fi-range::-moz-range-track{width:100%;height:3px;cursor:pointer;box-shadow:0 0 0 #000000;border-radius:5px;border:0 solid #000000}input[type=range].sc-fi-range::-moz-focus-outer{border:0}input[type=range].sc-fi-range::-webkit-slider-thumb{border:5px solid #ffffff;box-shadow:0 0 0 1px #e3e8e9;height:21px;width:21px;border-radius:25px;background:var(--fi-primary, #ffc809);cursor:pointer;-webkit-appearance:none;margin-top:-9px}input[type=range].sc-fi-range::-moz-range-thumb{border:5px solid #ffffff;box-shadow:0 0 0 1px #e3e8e9;height:21px;width:21px;border-radius:25px;background:var(--fi-primary, #ffc809);cursor:pointer;-webkit-appearance:none;margin-top:-9px}.slider-move.sc-fi-range input[type=range].sc-fi-range::-webkit-slider-thumb{box-shadow:0 0 0 8px #ffc8097d;border:none}.slider-move.sc-fi-range input[type=range].sc-fi-range::-moz-range-thumb{box-shadow:0 0 0 8px #ffc8097d;border:none}";

let Range = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.fiInput = createEvent(this, "fiInput", 7);
    /**
     * Input label
     */
    this.label = "";
    /**
     * Input value
     */
    this.value = 0;
    /**
     * Input max value
     */
    this.max = 1;
    /**
     * Input min value
     */
    this.min = 0;
    /**
     * Min label
     */
    this.labelMin = '';
    /**
     * Max label
     */
    this.labelMax = '';
    /**
     * Input steps
     */
    this.steps = [{ "min": "500", "max": "5000", "increment": "250", "rounding": "50" }];
    /**
     * Input prefix
     */
    this.inputPrefix = "";
    /**
     * Input prefix
     */
    this.inputSuffix = "";
    /**
     * Class name that is injected based on field validation
     */
    this.validationClass = "";
    /**
     * locale to display number separators
     */
    this.locale = "en-US";
    this.moveStyle = '';
    this.sliderValue = 0;
    this.rangeValues = [];
    this.getClosestBigger = () => {
      return this.rangeValues.reduce(this.reduceBigger, this.max);
    };
    this.reduceBigger = (currentClosest, currentValue) => {
      return currentClosest > currentValue && currentValue > this.value ? currentValue : currentClosest;
    };
    this.handleRangeChange = (event) => {
      const value = event.target.value.replace(/[^0-9]/g, '');
      let intValue = parseInt(value, 10);
      if (this.value === intValue)
        return;
      this.value = this.rangeValues[intValue] ? this.rangeValues[intValue] : this.max;
      this.sliderValue = intValue;
      if (isNaN(intValue)) {
        this.fiInput.emit(this.min);
      }
      else {
        this.fiInput.emit(this.value);
      }
    };
    this.handleInputChange = (event) => {
      const value = event.target.value.replace(/[^0-9]/g, '');
      const intValue = parseInt(value, 10);
      if (this.value === intValue)
        return;
      if (isNaN(intValue)) {
        this.fiInput.emit(this.min);
        this.value = this.min;
      }
      else {
        this.fiInput.emit(intValue);
        this.value = intValue;
      }
      this.sliderValue = this.rangeValues.includes(this.value) ? this.rangeValues.indexOf(this.value) : this.rangeValues.findIndex(value => value === this.getClosestBigger());
    };
    this.between = (x, min, max) => {
      return x >= min && x <= max;
    };
    this.roundToPrecision = (x, precision) => {
      const y = +x + (precision === undefined ? 0.5 : precision);
      return y - (y % (precision === undefined ? 1 : +precision));
    };
    this.onInputBlur = (event) => {
      const value = event.target.value.replace(/[^0-9]/g, '');
      let intValue = parseInt(value, 10);
      if (this.rangeValues.includes(intValue))
        return;
      if (isNaN(intValue) || intValue < this.min) {
        this.fiInput.emit(this.min);
        this.value = this.min;
        this.sliderValue = 0;
        return;
      }
      else if (intValue > this.max) {
        this.fiInput.emit(this.max);
        this.value = this.max;
        this.sliderValue = this.rangeValues.length ? this.rangeValues.length - 1 : 0;
        return;
      }
      this.steps.forEach(step => {
        if (this.between(intValue, parseInt(step.min, 10), parseInt(step.max, 10)) && intValue % parseInt(step.rounding, 10) !== 0) {
          this.fiInput.emit(this.roundToPrecision(intValue, parseInt(step.rounding, 10)));
          this.value = this.roundToPrecision(intValue, parseInt(step.rounding, 10));
        }
      });
    };
    this.startMove = () => {
      this.moveStyle = 'slider-move';
    };
    this.endMove = () => {
      this.moveStyle = '';
    };
    this.focusInput = () => {
      this.element.querySelector('#input').select();
    };
    this.sequenceGenerator = (minVal, maxVal, step) => {
      let currVal = minVal;
      let calculatedStep = step;
      const values = [];
      while (currVal < maxVal) {
        if (currVal + step > maxVal)
          calculatedStep = currVal + step - maxVal;
        values.push(currVal = currVal + calculatedStep);
      }
      return values;
    };
    this.generateRangeListValues = () => {
      const tempData = [];
      tempData.push(parseInt(this.steps[0].min, 10));
      for (let i = 0; i < this.steps.length; i++) {
        const min = i === 0 ? parseInt(this.steps[i].min, 10) : parseInt(this.steps[i].min, 10) - 1;
        // eslint-disable-next-line @stencil/strict-boolean-conditions
        const max = this.steps[i + 1] ? parseInt(this.steps[i + 1].min, 10) - 1 : this.max;
        const step = parseInt(this.steps[i].increment, 10);
        tempData.push(...this.sequenceGenerator(min, max, step));
      }
      return tempData;
    };
  }
  backgroundSize() {
    return (this.sliderValue / (this.rangeValues.length - 1)) * 100;
  }
  static toLocaleStringSupportsLocales() {
    try {
      new Date().toLocaleString('i');
    }
    catch (e) {
      return e instanceof RangeError;
    }
    return false;
  }
  get getInputValue() {
    if (Range.toLocaleStringSupportsLocales())
      return this.value.toLocaleString(this.locale);
    else
      return this.value;
  }
  componentWillLoad() {
    this.rangeValues = this.generateRangeListValues();
    this.sliderValue = this.rangeValues.findIndex(value => value === this.value);
  }
  get getInputSize() {
    const value = this.getInputValue.toString();
    let inputSizeOffset = getComputedStyle(this.element).getPropertyValue('--fi-slider-input-size-offset');
    if (!inputSizeOffset) {
      inputSizeOffset = "10";
    }
    return value.length * 9 + parseInt(inputSizeOffset, 10);
  }
  render() {
    return (h(Host, null, h("div", { class: `${this.validationClass} ${this.moveStyle} ` }, h("label", null, this.label), h("div", { class: "input-range-wrapper" }, h("div", { class: "input-number", onClick: this.focusInput, id: "input-wrapper" }, h("span", { class: "input-prefix" }, this.inputPrefix), h("input", { id: "input", class: "input", type: "tel", value: this.getInputValue, style: {
        width: this.getInputSize + 'px'
      }, onInput: this.handleInputChange, onBlur: this.onInputBlur }), h("span", { class: "input-suffix" }, this.inputSuffix)), h("div", { class: "range-wrapper" }, h("input", { id: "range", type: "range", min: "0", max: this.rangeValues.length - 1, onInput: this.handleRangeChange, onTouchStart: this.startMove, onTouchEnd: this.endMove, onMouseDown: this.startMove, onMouseUp: this.endMove, style: {
        background: 'linear-gradient(to right, #ffc809 0%, #ffc809 ' +
          this.backgroundSize() +
          '%, #e3e8e9 ' +
          this.backgroundSize() +
          '%, #e3e8e9 100%)'
      }, step: "1", value: this.sliderValue })))), h("div", { class: "min-max" }, h("div", { class: "min" }, this.labelMin), h("div", { class: "max" }, this.labelMax)), (this.validationClass.includes('error') && this.errorMessage) && h("div", { class: "error-message" }, this.errorMessage)));
  }
  get element() { return getElement(this); }
};
Range.style = rangeCss;

const fiRangeSliderCss = ":host{display:block;position:relative}input[type=range]{height:var(--fi-slider-track-height, 3px);-webkit-appearance:none;width:100%;margin:0 11px;border-radius:var(--fi-slider-track-border-radius, 5px)}input[type=range]:focus{outline:none}input[type=range]::-webkit-slider-runnable-track{width:100%;height:var(--fi-slider-track-height, 3px);cursor:pointer;box-shadow:0 0 0 #000000;border-radius:var(--fi-slider-track-border-radius, 5px);border:0 solid #000000}input[type=range]::-moz-range-track{width:100%;height:var(--fi-slider-track-height, 3px);cursor:pointer;box-shadow:0 0 0 #000000;border-radius:var(--fi-slider-track-border-radius, 5px);border:0 solid #000000}input[type=range]::-moz-focus-outer{border:0}input[type=range]::-webkit-slider-thumb{border:var(--fi-slider-thumb-border-size, 5px) solid var(--fi-slider-thumb-border-color, #ffffff);box-shadow:0 0 0 1px var(--fi-slider-thumb-box-shadow-color, #e3e8e9);height:var(--fi-slider-thumb-size, 21px);width:var(--fi-slider-thumb-size, 21px);border-radius:var(--fi-slider-thumb-border-radius, 25px);background:var(--fi-slider-thumb-color, var(--fi-primary, #ffc809));cursor:pointer;-webkit-appearance:none;margin:var(--fi-slider-thumb-margin, -9px 0 0 0)}input[type=range]::-moz-range-thumb{border:var(--fi-slider-thumb-border-size, 5px) solid var(--fi-slider-thumb-border-color, #ffffff);box-shadow:0 0 0 1px var(--fi-slider-thumb-box-shadow-color, #e3e8e9);height:var(--fi-slider-thumb-size, 21px);width:var(--fi-slider-thumb-size, 21px);border-radius:var(--fi-slider-thumb-border-radius, 25px);background:var(--fi-slider-thumb-color, var(--fi-primary, #ffc809));cursor:pointer;-webkit-appearance:none;margin:var(--fi-slider-thumb-margin, -9px 0 0 0)}.control{position:relative;display:inline-block;vertical-align:middle;min-width:var(--fi-slider-control-size, 24px);min-height:var(--fi-slider-control-size, 24px);border-radius:var(--fi-slider-control-border-radius, 50%);text-align:center;color:var(--fi-slider-control-color, #fff);font-size:29px;font-weight:bold;background-color:var(--fi-slider-control-bg-color, var(--fi-secondary-100, #535567));cursor:pointer}.control:before,.control:after{content:\"\";position:absolute;top:0;left:0;right:0;bottom:0}.control.plus:before{width:var(--fi-slider-control-thickness, 2px);margin:var(--fi-slider-control-margin, 6px) auto}.control.plus:after{margin:auto var(--fi-slider-control-margin, 6px);height:var(--fi-slider-control-thickness, 2px);box-shadow:none}.control.plus:before,.control.plus:after{background:var(--fi-slider-control-color, #fff)}.control.minus:before{background:var(--fi-slider-control-color, #fff);margin:auto var(--fi-slider-control-margin, 6px);height:var(--fi-slider-control-thickness, 2px)}.input-container .input-wrapper{box-sizing:border-box;width:100%;border-radius:6px;border:1px solid #d1d6df;background-color:#ffffff;padding:8px}.input-container .input-wrapper .input-prefix,.input-container .input-wrapper .input-suffix{font-size:var(--fi-slider-input-font-size, 18px);font-weight:var(--fi-slider-input-font-weight, normal);padding:0 2px;vertical-align:middle}.input-container .input-wrapper input{border:none;outline:none;appearance:none;background:none;padding:0;font-size:var(--fi-slider-input-font-size, 18px);font-weight:var(--fi-slider-input-font-weight, normal);font-family:inherit;text-align:center;display:inline-flex;color:var(--fi-secondary, #2b2e42);vertical-align:middle;caret-color:var(--fi-primary, #ffc809)}.value{display:inline-flex;justify-content:center;align-items:center;flex:1;font-weight:bold;font-size:var(--fi-slider-value-font-size, 20px)}.value-container{display:flex;flex-direction:column}.value-container .label{text-align:center}.value-control,.range-wrapper{display:flex;align-items:center}.min,.max{display:inline-flex;flex-direction:column;align-items:center;color:var(--fi-slider-affix-color, var(--fi-secondary-100, #535567));font-size:var(--fi-slider-affix-font-size, 12px);font-weight:var(--fi-slider-affix-font-weight, bold);white-space:nowrap}.max{text-align:right}.range-wrapper{margin:var(--fi-slider-range-margin, 7px 0 0 0)}.label-wrapper{display:flex;flex-direction:row;justify-content:space-between}.light .min,.light .max,.light .value{color:var(--fi-secondary-900, #FFFFFF)}";

let FiRangeSlider = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.fiInput = createEvent(this, "fiInput", 7);
    /**
     * Input value
     */
    this.value = 0;
    /**
     * Input max value
     */
    this.max = 1;
    /**
     * Input min value
     */
    this.min = 0;
    /**
     * Input label
     */
    this.inputLabel = "";
    /**
     * Input prefix
     */
    this.inputPrefix = "";
    /**
     * Input prefix
     */
    this.inputSuffix = "";
    /**
     * Min/Max label position: aside or bottom of the slider
     */
    this.labelPosition = 'aside';
    /**
     * Min label
     */
    this.labelMin = '';
    /**
     * Max label
     */
    this.labelMax = '';
    /**
     * Label for current value
     */
    this.valueLabel = '';
    /**
     * Suffix for current value
     */
    this.valueSuffix = '';
    /**
     * Prefix for current value
     */
    this.valuePrefix = '';
    /**
     * Input steps
     */
    this.steps = [{ "min": "500", "max": "5000", "increment": "250", "rounding": "50" }];
    /**
     * locale to display number separators
     */
    this.locale = "en-US";
    /**
     * Defines slider text color:
     * dark uses secondary-100 color with main webpage font color and light uses secondary-900
     */
    this.theme = "dark";
    this.sliderValue = 0;
    this.rangeValues = [];
    this.sequenceGenerator = (minVal, maxVal, step) => {
      let currVal = minVal;
      let calculatedStep = step;
      const values = [];
      while (currVal < maxVal) {
        if (currVal + step > maxVal)
          calculatedStep = currVal + step - maxVal;
        values.push(currVal = currVal + calculatedStep);
      }
      return values;
    };
    this.generateRangeListValues = () => {
      const tempData = [];
      tempData.push(parseInt(this.steps[0].min, 10));
      for (let i = 0; i < this.steps.length; i++) {
        const min = i === 0 ? parseInt(this.steps[i].min, 10) : parseInt(this.steps[i].min, 10) - 1;
        // eslint-disable-next-line @stencil/strict-boolean-conditions
        const max = this.steps[i + 1] ? parseInt(this.steps[i + 1].min, 10) - 1 : this.max;
        const step = parseInt(this.steps[i].increment, 10);
        tempData.push(...this.sequenceGenerator(min, max, step));
      }
      return tempData;
    };
    this.handleRangeChange = (event) => {
      const value = event.target.value.replace(/[^0-9]/g, '');
      let intValue = parseInt(value, 10);
      if (this.value === intValue)
        return;
      this.value = this.rangeValues[intValue] ? this.rangeValues[intValue] : this.max;
      this.sliderValue = intValue;
      if (isNaN(intValue)) {
        this.fiInput.emit(this.min);
      }
      else {
        this.fiInput.emit(this.value);
      }
    };
    this.onPlusClick = () => {
      if (this.sliderValue === this.rangeValues.length - 1)
        return;
      this.sliderValue += 1;
      this.value = this.rangeValues[this.sliderValue];
      this.fiInput.emit(this.value);
    };
    this.onMinusClick = () => {
      if (this.sliderValue === 0)
        return;
      this.sliderValue -= 1;
      this.value = this.rangeValues[this.sliderValue];
      this.fiInput.emit(this.value);
    };
    this.handleInputChange = (event) => {
      const value = event.target.value.replace(/[^0-9]/g, '');
      const intValue = parseInt(value, 10);
      if (this.value === intValue)
        return;
      if (isNaN(intValue)) {
        this.fiInput.emit(this.min);
        this.value = this.min;
      }
      else {
        this.fiInput.emit(intValue);
        this.value = intValue;
      }
      this.sliderValue = this.rangeValues.includes(this.value) ? this.rangeValues.indexOf(this.value) : this.rangeValues.findIndex(value => value === this.getClosestBigger());
    };
    this.onInputBlur = (event) => {
      const value = event.target.value.replace(/[^0-9]/g, '');
      let intValue = parseInt(value, 10);
      if (this.rangeValues.includes(intValue))
        return;
      if (isNaN(intValue) || intValue < this.min) {
        this.fiInput.emit(this.min);
        this.value = this.min;
        this.sliderValue = 0;
        return;
      }
      else if (intValue > this.max) {
        this.fiInput.emit(this.max);
        this.value = this.max;
        this.sliderValue = this.rangeValues.length ? this.rangeValues.length - 1 : 0;
        return;
      }
      this.steps.forEach(step => {
        if (this.between(intValue, parseInt(step.min, 10), parseInt(step.max, 10)) && intValue % parseInt(step.rounding, 10) !== 0) {
          this.fiInput.emit(this.roundToPrecision(intValue, parseInt(step.rounding, 10)));
          this.value = this.roundToPrecision(intValue, parseInt(step.rounding, 10));
        }
      });
    };
    this.between = (x, min, max) => {
      return x >= min && x <= max;
    };
    this.roundToPrecision = (x, precision) => {
      const y = +x + (precision === undefined ? 0.5 : precision);
      return y - (y % (precision === undefined ? 1 : +precision));
    };
    this.getClosestBigger = () => {
      return this.rangeValues.reduce(this.reduceBigger, this.max);
    };
    this.reduceBigger = (currentClosest, currentValue) => {
      return currentClosest > currentValue && currentValue > this.value ? currentValue : currentClosest;
    };
  }
  backgroundSize() {
    return (this.sliderValue / (this.rangeValues.length - 1)) * 100;
  }
  componentWillLoad() {
    this.rangeValues = this.generateRangeListValues();
    this.sliderValue = this.rangeValues.findIndex(value => value === this.value);
  }
  get getInputValue() {
    if (FiRangeSlider.toLocaleStringSupportsLocales())
      return this.value.toLocaleString(this.locale);
    else
      return this.value;
  }
  get getInputSize() {
    const value = this.getInputValue.toString();
    let inputSizeOffset = getComputedStyle(this.element).getPropertyValue('--fi-slider-input-size-offset');
    if (!inputSizeOffset) {
      inputSizeOffset = "10";
    }
    return value.length * 9 + parseInt(inputSizeOffset, 10);
  }
  static toLocaleStringSupportsLocales() {
    try {
      new Date().toLocaleString('i');
    }
    catch (e) {
      return e instanceof RangeError;
    }
    return false;
  }
  render() {
    return (h(Host, null, h("div", { class: this.theme }, this.inputLabel != '' ?
      h("div", { part: "input", class: "input-container" }, h("div", { part: "input-label", class: "label" }, this.inputLabel), h("div", { part: "input-field", class: "input-wrapper" }, h("span", { class: "input-prefix" }, this.inputPrefix), h("input", { id: "input", type: "tel", value: this.getInputValue, style: {
          width: this.getInputSize + 'px'
        }, onInput: this.handleInputChange, onBlur: this.onInputBlur }), h("span", { class: "input-suffix" }, this.inputSuffix)))
      : h("div", { part: "value", class: "value-container" }, h("div", { class: "label" }, this.valueLabel), h("div", { class: "value-control" }, h("div", { class: "control minus", onClick: this.onMinusClick }), h("div", { class: "value" }, this.valuePrefix, this.getInputValue, this.valueSuffix), h("div", { class: "control plus", onClick: this.onPlusClick }))), h("div", { part: "range", class: "range-wrapper" }, this.labelPosition == 'aside' ?
      h("div", { class: "min" }, this.inputLabel != '' ? h("div", { class: "control minus", onClick: this.onMinusClick }) : this.labelMin)
      : '', h("input", { id: "range", type: "range", min: "0", max: this.rangeValues.length - 1, onInput: this.handleRangeChange, style: {
        background: 'linear-gradient(to right, var(--fi-slider-track-bg-color, var(--fi-primary, #ffc809)) 0%, var(--fi-slider-track-bg-color, var(--fi-primary, #ffc809)) ' +
          this.backgroundSize() +
          '%, #e3e8e9 ' +
          this.backgroundSize() +
          '%, #e3e8e9 100%)'
      }, step: "1", value: this.sliderValue, part: "slider" }), this.labelPosition == 'aside' ?
      h("div", { class: "max" }, this.inputLabel != '' ? h("div", { class: "control plus", onClick: this.onPlusClick }) : this.labelMax)
      : ''), this.labelPosition == 'bottom' ?
      h("div", { class: "label-wrapper" }, h("div", { class: "min" }, this.labelMin), h("div", { class: "max" }, this.labelMax))
      : '')));
  }
  get element() { return getElement(this); }
};
FiRangeSlider.style = fiRangeSliderCss;

const rippleCss = ":host{position:relative}.ripple{position:relative;overflow:hidden;border:none;border-radius:var(--fi-button-border-radius, 6px);height:100%;width:100%}.ripple .rippleContainer{position:absolute;top:0;right:0;bottom:0;left:0}.ripple .rippleContainer span{transform:scale(0);border-radius:100%;position:absolute;opacity:0.26;background-color:var(--fi-secondary-color, #2b2e42);animation:ripple 850ms}@keyframes ripple{to{opacity:0;transform:scale(2)}}";

let bounce;
let Ripple = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.spanStyles = {};
    this.count = 0;
    this.showRipple = (e) => {
      const rippleContainer = e.currentTarget;
      const size = rippleContainer.offsetWidth;
      const pos = rippleContainer.getBoundingClientRect();
      const x = e.pageX - (pos.left + window.scrollX) - (size / 2);
      const y = e.pageY - (pos.top + window.scrollY) - (size / 2);
      const spanStyles = { top: y + 'px', left: x + 'px', height: size + 'px', width: size + 'px' };
      const count = this.count + 1;
      this.spanStyles = Object.assign(Object.assign({}, this.spanStyles), { [count]: spanStyles });
      this.count = count;
    };
    this.cleanUp = () => {
      if (Object.keys(this.spanStyles).length === 0)
        return;
      this.spanStyles = {};
      this.count = 0;
    };
    this.setUpBounce = () => {
      bounce = setTimeout(() => { this.cleanUp(); }, 2000);
    };
    this.cleanTimeouts = () => {
      clearTimeout(bounce);
      this.setUpBounce();
    };
    this.renderRippleSpan = () => {
      const { spanStyles = {} } = this;
      const spanArray = Object.keys(spanStyles);
      if (spanArray && spanArray.length > 0) {
        this.cleanTimeouts();
        return (spanArray.map((key, index) => {
          return h("span", { key: 'spanCount_' + index, class: "", style: Object.assign({}, spanStyles[key]) });
        }));
      }
      else {
        return null;
      }
    };
  }
  render() {
    return (h(Host, null, h("div", { class: "ripple" }, h("slot", null), h("div", { class: "rippleContainer", onClick: this.showRipple }, this.renderRippleSpan()))));
  }
};
Ripple.style = rippleCss;

const selectCss = ".sc-fi-select-h{position:relative;color:var(--fi-secondary-color, #2b2e42);text-align:left;box-sizing:border-box}.sc-fi-select-h .dropdown-active.sc-fi-select{border-top-left-radius:4px;border-top-right-radius:4px;border:1px solid var(--fi-select-border, #888d96);box-sizing:content-box;margin-left:-11px !important;margin-top:-1px;padding:10px 10px 0 10px !important}.sc-fi-select-h .dropdown-active.sc-fi-select .underline.sc-fi-select,.sc-fi-select-h .dropdown-active.sc-fi-select button.sc-fi-select{width:calc(100% - 20px) !important}.sc-fi-select-h .dropdown-active.sc-fi-select .underline.sc-fi-select{left:10px !important}.sc-fi-select-h .dropdown-active.sc-fi-select .valid.sc-fi-select .underline.sc-fi-select{width:calc(100% - 10px) !important}.sc-fi-select-h .dropdown-active.sc-fi-select .icon-valid.sc-fi-select{right:14px !important}.sc-fi-select-h .dropdown-active.sc-fi-select .icon-check.sc-fi-select{right:8px !important}.sc-fi-select-h .dropdown.sc-fi-select{vertical-align:top;position:relative;display:inline-block;width:100%;padding-top:10px}.sc-fi-select-h .dropdown.sc-fi-select .label.sc-fi-select{display:none}.sc-fi-select-h .dropdown.sc-fi-select button.sc-fi-select{font-size:18px;line-height:normal;text-align:left;border:none;background:none;width:100%;flex:1;border-bottom:1px solid var(--fi-secondary-600, #d1d6df);padding:0 5px 10px 0;cursor:pointer;font-family:inherit;margin:0}.sc-fi-select-h .dropdown.sc-fi-select button.sc-fi-select:focus{box-shadow:none;outline:none}.sc-fi-select-h .dropdown.sc-fi-select button.sc-fi-select:focus+.underline.sc-fi-select{transform:scale(1)}.sc-fi-select-h .dropdown.sc-fi-select button.sc-fi-select .button-text.sc-fi-select{width:100%;font-size:18px;font-weight:normal;font-stretch:normal;font-style:normal;line-height:normal;letter-spacing:normal;color:var(--fi-secondary, #2b2e42);display:flex;align-items:center;padding-top:5px;padding-right:18px}.sc-fi-select-h .dropdown.sc-fi-select button.sc-fi-select .button-text.sc-fi-select img.sc-fi-select{margin-right:10px}.sc-fi-select-h .dropdown.sc-fi-select button.sc-fi-select .empty.sc-fi-select{font-size:18px;font-weight:normal;font-stretch:normal;font-style:normal;line-height:normal;letter-spacing:normal;color:var(--fi-secondary-400, #8b99ae)}.sc-fi-select-h .dropdown.sc-fi-select button.sc-fi-select .icon.sc-fi-select{margin-left:var(--fi-button-padding, 13px);position:absolute}.sc-fi-select-h .dropdown.sc-fi-select button.sc-fi-select .icon.sc-fi-select img.sc-fi-select{display:block}.sc-fi-select-h .dropdown.sc-fi-select button.sc-fi-select .icon-check.sc-fi-select{transform:rotate(90deg);transition:all 0.15s linear;bottom:0;right:-2px}.sc-fi-select-h .dropdown.sc-fi-select button.sc-fi-select .icon-valid.sc-fi-select{animation:scale 0.5s;bottom:10px;right:4px}@keyframes scale{0%{transform:scale(1)}50%{transform:scale(2)}100%{transform:scale(1)}}.sc-fi-select-h .dropdown.valid.sc-fi-select .underline.sc-fi-select{transform:scale(1);background-color:#00c316;width:100%}.sc-fi-select-h .dropdown.valid.sc-fi-select .label.sc-fi-select{display:block;font-size:12px;color:#8b99ae;height:15px}.sc-fi-select-h .dropdown.error.sc-fi-select .underline.sc-fi-select{transform:scale(1);background-color:var(--fi-error, #ff0226);backface-visibility:hidden;perspective:1000px}.sc-fi-select-h .dropdown.sc-fi-select:focus{box-shadow:none;outline:none}.sc-fi-select-h .dropdown.sc-fi-select .dropdown-menu.sc-fi-select{left:0;position:absolute;top:100%;z-index:20;width:100%;overflow-y:auto;background-color:var(--fi-process-background, #ffffff);margin-left:-1px;max-height:var(--dropdownMaxHeight, 300px);display:none}.sc-fi-select-h .dropdown.sc-fi-select .dropdown-menu.sc-fi-select::-webkit-scrollbar{width:8px}.sc-fi-select-h .dropdown.sc-fi-select .dropdown-menu.sc-fi-select::-webkit-scrollbar-thumb{background:#d7d7d7;border-radius:6px}.sc-fi-select-h .dropdown.sc-fi-select .dropdown-menu.sc-fi-select .dropdown-item.sc-fi-select{display:flex;align-items:center;cursor:pointer;padding:var(--fi-button-padding, 10px);font-size:18px}.sc-fi-select-h .dropdown.sc-fi-select .dropdown-menu.sc-fi-select .dropdown-item.sc-fi-select:hover{background:#ecfff0}.sc-fi-select-h .dropdown.sc-fi-select .dropdown-menu.sc-fi-select .dropdown-item.sc-fi-select img.sc-fi-select{margin-right:10px}.sc-fi-select-h .dropdown.is-active.sc-fi-select .icon-check.sc-fi-select{transform:rotate(-90deg) !important}.sc-fi-select-h .dropdown.is-active.sc-fi-select .dropdown-menu.sc-fi-select{max-height:var(--dropdownMaxHeight, 300px);border-bottom-left-radius:4px;border-bottom-right-radius:4px;border-right:1px solid;border-bottom:1px solid;border-left:1px solid;border-color:var(--fi-select-border, #888d96);display:block}.sc-fi-select-h .dropdown.is-active.sc-fi-select .dropdown-menu.sc-fi-select .is-active.sc-fi-select{font-weight:bold}.sc-fi-select-h .dropdown.is-active.sc-fi-select .shadow.sc-fi-select{box-shadow:inset 0 -10px 7px -10px #888d96}.sc-fi-select-h .dropdown.is-up.sc-fi-select .dropdown-menu.sc-fi-select{bottom:100%;margin-bottom:4px;padding-top:initial;top:auto}.sc-fi-select-h .underline.sc-fi-select{background-color:var(--fi-secondary-600, #888d96);display:inline-block;height:1px;left:0;position:absolute;bottom:0;-webkit-transform:scale(0, 1);transform:scale(0, 1)}.sc-fi-select-h .select.sc-fi-select{position:relative;display:inline-block;max-width:100%;width:100%;vertical-align:top}.sc-fi-select-h .select.sc-fi-select:after{position:absolute;content:\" \";display:block;right:6px;top:22px;width:5px;height:5px;border:solid var(--fi-secondary-400, #8b99ae);border-width:0 1px 1px 0;-webkit-transform:rotate(45deg);-ms-transform:rotate(45deg);transform:rotate(45deg)}.sc-fi-select-h .select.sc-fi-select select.sc-fi-select{cursor:pointer;display:block;width:100%;max-width:100%;outline:0;background-color:transparent;border-radius:0;border:none;box-shadow:none;box-sizing:border-box;border-bottom:1px solid var(--fi-secondary-600, #d1d6df);padding:var(--fi-button-padding, 13px) 20px var(--fi-button-padding, 13px) 0;justify-content:flex-start;-moz-appearance:none;-webkit-appearance:none;appearance:none;margin:0;font-size:18px;font-weight:normal;font-stretch:normal;font-style:normal;line-height:normal;letter-spacing:normal;font-family:inherit;color:var(--fi-secondary, #2b2e42)}.sc-fi-select-h .select.sc-fi-select select.sc-fi-select:focus+.underline.sc-fi-select{transform:scale(1)}.sc-fi-select-h .select.sc-fi-select .placeholder.sc-fi-select{font-size:18px;font-weight:normal;font-stretch:normal;font-style:normal;line-height:normal;letter-spacing:normal;color:var(--fi-secondary-400, #8b99ae)}.sc-fi-select-h .select.valid.sc-fi-select .underline.sc-fi-select{transform:scale(1)}.sc-fi-select-h .select.error.sc-fi-select .underline.sc-fi-select{transform:scale(1);background-color:var(--fi-error, #ff0226);backface-visibility:hidden;perspective:1000px}.sc-fi-select-h .on-select.sc-fi-select{animation:background 0.15s;font-weight:normal !important}@keyframes background{0%{background-color:transparent}100%{background-color:#addba9}}";

let Select = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.fiInput = createEvent(this, "fiInput", 7);
    this.fiFocus = createEvent(this, "fiFocus", 7);
    this.isExpanded = false;
    this.isUp = "";
    /**
     * Use native <select>
     */
    this.type = "auto";
    /**
     * Empty value
     */
    this.emptyValue = "";
    /**
     * Label for dropdown
     */
    this.label = "";
    /**
     * Placeholder for dropdown
     */
    this.placeholder = "";
    /**
     * Initial value
     */
    this.value = "";
    /**
     * Maximum height for dropdown
     */
    this.maxHeight = 300;
    /**
     * Disabled status
     */
    this.disabled = false;
    /**
    * Select options
    */
    this.options = [];
    /**
    * Class name that is injected based on field validation
    */
    this.validationClass = "";
    /**
     * If select has filtering/searching option enabled
     */
    this.searchable = false;
    this.selectOption = (event, option) => {
      this.value = option.value;
      this.fiInput.emit(this.value);
      const element = event.target;
      if (element !== null) {
        element.classList.add('on-select');
        setTimeout(() => {
          this.isExpanded = false;
          element.classList.remove('on-select');
          this.scrollToTarget();
        }, 130);
      }
    };
    this.nativeSelectOnChange = (event) => {
      this.value = event.target.value;
      this.fiInput.emit(this.value);
    };
    this.scrollToTarget = () => {
      const element = this.el.querySelector('.item-active');
      const container = this.el.querySelector('.dropdown-menu');
      if (element !== null) {
        // @ts-ignore
        container.scrollTop = element.offsetTop - 43;
      }
    };
    this.openSelect = () => {
      this.fiFocus.emit();
      this.isExpanded = !this.isExpanded;
      this.scrollToTarget();
    };
  }
  onBodyClicked(event) {
    if (!this.isExpanded) {
      return;
    }
    if (event.composedPath().includes(this.el)) {
      return;
    }
    this.scrollToTarget();
    return this.isExpanded = !this.isExpanded;
  }
  get getOperatingSystem() {
    const userAgent = navigator.userAgent || navigator.vendor;
    if (/android/i.test(userAgent)) {
      return "Android";
    }
    // eslint-disable-next-line @stencil/strict-boolean-conditions
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return "iOS";
    }
    return "Desktop";
  }
  get getType() {
    if (this.type === "native") {
      return "native";
    }
    if ((this.getOperatingSystem === "Android" || this.getOperatingSystem === "iOS") && this.type === "auto") {
      return "native";
    }
    return "markup";
  }
  get getOpenDirection() {
    const spaceAbove = this.el.getBoundingClientRect().top;
    const spaceBelow = window.innerHeight - this.el.getBoundingClientRect().bottom;
    const hasEnoughSpaceBelow = spaceBelow > this.maxHeight;
    if (hasEnoughSpaceBelow || spaceBelow > spaceAbove) {
      return "";
    }
    else {
      return "is-up";
    }
  }
  get getCurrentValue() {
    if (this.value === "" || this.value === null) {
      if (this.placeholder !== '') {
        return { label: this.placeholder, image: null };
      }
      return { label: this.label, image: null };
    }
    if (this.value !== "") {
      const option = this.options.find(option => { var _a, _b; return ((_a = option.value) === null || _a === void 0 ? void 0 : _a.toString()) === ((_b = this.value) === null || _b === void 0 ? void 0 : _b.toString()); });
      // eslint-disable-next-line @stencil/strict-boolean-conditions
      return option;
    }
    return { label: "", image: null };
  }
  createOption(option) {
    return (
    // eslint-disable-next-line react/jsx-no-bind
    h("div", { key: option.value, class: `dropdown-item ${this.value.toString() === option.value.toString() ? "is-active item-active" : ""}`, onClick: (event) => this.selectOption(event, option) }, option.image && h("img", { src: option.image, width: 23, height: 23, alt: option.label }), option.label));
  }
  createNativeOption(option) {
    return (h("option", { key: option.value, class: `select-option ${this.value === option.value ? "is-active" : ""}`, value: option.value, selected: this.value === option.value, onClick: (event) => this.selectOption(event, option) }, option.label));
  }
  createNativeSelect(selectOptions) {
    return (h("div", { class: `select ${this.isExpanded ? "is-active" : ""} ${this.validationClass}` }, h("select", { disabled: this.disabled ? true : null, class: this.value === 'NaN' && 'placeholder', onChange: this.nativeSelectOnChange }, this.value === 'NaN' && h("option", { disabled: true, selected: true, hidden: true }, this.getCurrentValue.label), selectOptions), h("span", { class: "underline" })));
  }
  createMarkupSelect(selectOptions) {
    return (h("div", { class: `dropdown ${this.isExpanded ? "dropdown-active is-active" : ""} ${this.isUp} ${this.validationClass}` }, h("div", { class: "label" }, this.label), h("button", { type: "button", class: "button dropdown-button", disabled: this.disabled ? true : null, "aria-haspopup": "true", "aria-controls": "dropdown-menu", onClick: this.openSelect }, h("span", { class: `button-text ${this.getCurrentValue.label === this.label && 'empty'}` }, this.getCurrentValue.image && h("img", { src: this.getCurrentValue.image, width: 23, height: 23, alt: this.getCurrentValue.label }), this.getCurrentValue.label), h("div", { class: `icon ${this.validationClass === 'valid' ? 'icon-valid' : 'icon-check'}` }, this.validationClass === 'valid' ?
      h("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 16 16" }, h("g", { fill: "none", "fill-rule": "evenodd" }, h("g", { fill: "#00C316" }, h("g", null, h("path", { d: "M3.175 10.407c-.233-.225-.233-.589 0-.814l8.547-8.349c.338-.325.887-.325 1.225 0 .337.326.337.855 0 1.18L5.2 10l3.746 3.575c.337.326.337.855 0 1.18-.338.326-.887.326-1.225 0l-4.547-4.348z", transform: "translate(-314 -310) translate(20 283) matrix(0 -1 -1 0 310.1 43.1)" })))))
      :
        h("svg", { xmlns: "http://www.w3.org/2000/svg", width: "30", height: "30", viewBox: "0 0 30 30" }, h("g", { fill: "none", "fill-rule": "evenodd" }, h("g", { fill: "#535567" }, h("g", null, h("path", { d: "M17.563 20.756l-5.393-5.349c-.227-.225-.227-.589 0-.814l5.393-5.349c.328-.325.862-.325 1.19 0 .329.326.329.855 0 1.18L14.14 15l4.614 4.575c.328.326.328.855 0 1.18-.329.326-.863.326-1.19 0", transform: "translate(-290 -274) translate(30 239) matrix(-1 0 0 1 291 35)" }))))))), h("span", { class: "underline" }), h("div", { class: `dropdown-menu ${selectOptions.length > 5 ? 'shadow' : ''}`, style: { '--dropdownMaxHeight': `${selectOptions.length > 5 ? 198 : 300}px` } }, selectOptions)));
  }
  render() {
    const selectOptions = this.options.map(option => {
      return this.getType === "markup" || this.searchable ? this.createOption(option) : this.createNativeOption(option);
    });
    const select = this.getType === "markup" ? this.createMarkupSelect(selectOptions) : this.createNativeSelect(selectOptions);
    return (h(Host, { "aria-disabled": this.disabled ? "true" : null, "aria-expanded": `${this.isExpanded}` }, select));
  }
  get el() { return getElement(this); }
};
Select.style = selectCss;

const toggleButtonCss = ".sc-fi-toggle-button-h{display:block}.toggle-buttons-container.sc-fi-toggle-button .label.sc-fi-toggle-button{font-size:18px;color:#8b99ae}.toggle-buttons-container.sc-fi-toggle-button .label.label-small.sc-fi-toggle-button{font-size:12px;height:18px}.toggle-buttons-container.sc-fi-toggle-button .toggle-buttons.sc-fi-toggle-button{display:flex;text-align:center;font-size:14px;line-height:1.43}.toggle-buttons-container.sc-fi-toggle-button .toggle-buttons.sc-fi-toggle-button .toggle-button.sc-fi-toggle-button{border:solid 1px var(--fi-secondary-600, #d1d6df);min-width:54px;padding:10px 40px;cursor:pointer}.toggle-buttons-container.sc-fi-toggle-button .toggle-buttons.sc-fi-toggle-button .toggle-button.sc-fi-toggle-button:first-child{border-top-left-radius:var(--fi-button-border-radius, 6px);border-bottom-left-radius:var(--fi-button-border-radius, 6px)}.toggle-buttons-container.sc-fi-toggle-button .toggle-buttons.sc-fi-toggle-button .toggle-button.sc-fi-toggle-button:first-child:nth-last-child(2){border-right:solid 1px var(--fi-secondary-600, #d1d6df)}.toggle-buttons-container.sc-fi-toggle-button .toggle-buttons.sc-fi-toggle-button .toggle-button.sc-fi-toggle-button:first-child:not(:nth-last-child(2)){border-right:none}.toggle-buttons-container.sc-fi-toggle-button .toggle-buttons.sc-fi-toggle-button .toggle-button.sc-fi-toggle-button:last-child{border-left:none;border-top-right-radius:var(--fi-button-border-radius, 6px);border-bottom-right-radius:var(--fi-button-border-radius, 6px)}.toggle-buttons-container.sc-fi-toggle-button .toggle-buttons.sc-fi-toggle-button .toggle-button.toggle-button-small.sc-fi-toggle-button{padding:10px 22px}.toggle-buttons-container.sc-fi-toggle-button .toggle-buttons.sc-fi-toggle-button .toggle-button.selected.sc-fi-toggle-button{background:var(--fi-light-green, #ecfff0);border:1px solid var(--fi-green, #00c316) !important}.toggle-buttons-container.sc-fi-toggle-button .toggle-buttons.sc-fi-toggle-button .toggle-button.selected.sc-fi-toggle-button:not(:first-child){margin-left:-1px}.toggle-buttons-container.error.sc-fi-toggle-button .toggle-buttons.sc-fi-toggle-button .toggle-button.sc-fi-toggle-button{border-color:var(--fi-error, #ff0226)}.disabled.sc-fi-toggle-button{opacity:0.5;cursor:default}";

let ToggleButton = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.fiInput = createEvent(this, "fiInput", 7);
    /**
     * Label for dropdown
     */
    this.label = "";
    /**
     * Label style
     */
    this.labelStyle = "";
    /**
     * Value that is selected
     */
    this.checkedValue = "";
    /**
     * Values to show
     */
    this.options = [];
    /**
     * Options style
     */
    this.optionsStyle = "";
    /**
     * Disabled status
     */
    this.disabled = false;
    /**
     * Class name that is injected based on field validation
     */
    this.validationClass = "";
    this.value = '';
    this.onChange = (value) => {
      if (this.disabled) {
        return;
      }
      this.value = value;
      this.fiInput.emit(this.value);
    };
  }
  /**
   * Update the native input element when the checkedValue changes
   */
  valueChanged() {
    this.value = this.checkedValue;
  }
  componentWillLoad() {
    this.value = this.checkedValue;
  }
  render() {
    return (h(Host, null, h("div", { class: `toggle-buttons-container ${this.validationClass}` }, this.label && h("div", { class: `label label-${this.labelStyle || 'default'}` }, this.label), h("div", { class: "toggle-buttons" }, this.options.map((option) => (h("div", { onClick: () => this.onChange(option.value), class: `toggle-button toggle-button-${this.optionsStyle || 'default'} ${this.value === option.value && "selected"} ${this.disabled && 'disabled'}` }, option.properties && option.properties.label ? option.properties.label : option.label)))))));
  }
  static get watchers() { return {
    "checkedValue": ["valueChanged"]
  }; }
};
ToggleButton.style = toggleButtonCss;

export { Autocomplete as fi_autocomplete, Button as fi_button, ButtonCollection as fi_button_collection, ButtonSelect as fi_button_select, Checkbox as fi_checkbox, DatePicker as fi_date, Input as fi_input, Loader as fi_loader, ProgressStep as fi_progress_step, ProgressSteps as fi_progress_steps, Progressbar as fi_progressbar, RadioButton as fi_radio_button, Range as fi_range, FiRangeSlider as fi_range_slider, Ripple as fi_ripple, Select as fi_select, ToggleButton as fi_toggle_button };
